import React, { useState, useEffect, useMemo } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import "./App.css";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./components/ui/alert-dialog";
import { Book, Music, Film, Search, Plus, Edit, Trash2, Library, Upload, Download, FileText, AlertCircle, ExternalLink, Grid, List, ChevronUp, ChevronDown, ChevronRight, Users, Calendar, CheckCircle, Clock, XCircle, LogOut, User, Shield, LogIn, UserPlus, Building2, Mail, Phone, Globe, MapPin, RotateCcw, Ban, CreditCard, HelpCircle, Info, DollarSign, BarChart3, UserCheck, X } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import { UsersTabContent, LoansTabContent } from "./UserLoanTabs";
import { ResendRenewedCardsButton } from "./ResendRenewedCardsButton";
import { CopyEventLinkButton } from "./CopyEventLinkButton";
import { toLocalDateTimeInput } from "./dateInput";
import { HelpContent } from "./HelpContent";
import { LoginForm, RegisterForm, UserMenu, ForgotPasswordForm, ResetPasswordForm, ChangePasswordForm, DeleteAccountForm } from "./AuthComponents";
import EventsTabContent, { EventDetailView } from "./EventsTabContent";
import RegistrationResult from "./RegistrationResult";
import MembershipRenewalResult from "./MembershipRenewalResult";
import { BookingDialog, BookingHistory, EventBookingsTable, SophisticatedBookingDialog } from "./BookingComponents";
import EventCapacityChart from "./EventCapacityChart";
import EventWaitlistDialog from "./EventWaitlistDialog";
import PaymentHistoryDialog from "./PaymentHistoryDialog";
import AdminManualBookingDialog from "./AdminManualBookingDialog";
import DocumentRepositoryDialog from "./DocumentRepositoryDialog";
import SeasonPassManagement from "./SeasonPassManagement";
import SeasonPassBrowse from "./SeasonPassBrowse";
import SeasonPassSuccess from "./SeasonPassSuccess";
import SeasonPassCancel from "./SeasonPassCancel";
import CustomRowsBookingDialog from "./CustomRowsBookingDialog";
import AdminSettingsDialog from "./AdminSettingsDialog";
import Dashboard from "./Dashboard";
import AdminLoanManagement from "./AdminLoanManagement";
import BookCategoryManagement from "./BookCategoryManagement";
import EventFinancialManagement from "./EventFinancialManagement";
import LoginReportDialog from "./LoginReportDialog";

const API = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001') + '/api';
const PUBLIC_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Permission helper — checks if user has a specific permission
const isAdmin = (user) => user?.role === 'Administrator';
const hasPermission = (user, perm) => {
  if (!user || user.role !== 'Administrator') return false;
  const perms = user.permissions || [];
  return perms.includes(perm);
};

function App() {
  // Authentication state - moved to top to fix initialization order
  const [currentUser, setCurrentUser] = useState(null);
  const [authToken, setAuthToken] = useState(() => {
    // Initialize with token from localStorage immediately
    const token = localStorage.getItem('authToken');
    if (token) {
      // Set axios defaults immediately on initialization
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      console.log('🚀 Initial auth token loaded and axios defaults set');
    }
    return token;
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Admin role members may switch their dashboard view to behave like a regular
  // member (so they can browse + book events as members of the association).
  // Persisted to localStorage so the choice survives reloads.
  const [viewAsMember, setViewAsMember] = useState(() => {
    return localStorage.getItem('viewAsMember') === '1';
  });
  useEffect(() => {
    localStorage.setItem('viewAsMember', viewAsMember ? '1' : '0');
  }, [viewAsMember]);

  // Effective user reflects the toggle. Admin actions/UI use this so a toggled-on
  // admin sees the member-style dashboard. Actual API/role guards keep using
  // `currentUser` (the real account).
  const effectiveUser = useMemo(() => {
    if (!currentUser) return null;
    if (viewAsMember && currentUser.role === 'Administrator') {
      return { ...currentUser, role: 'Member', admin_roles: [], permissions: [] };
    }
    return currentUser;
  }, [currentUser, viewAsMember]);

  const [books, setBooks] = useState([]);
  const [cds, setCds] = useState([]);
  const [dvds, setDvds] = useState([]);
  const [users, setUsers] = useState([]);
  const [loans, setLoans] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditFormReady, setIsEditFormReady] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [currentTab, setCurrentTab] = useState("events");
  
  // Cultural Events state - moved up to fix initialization order
  const [events, setEvents] = useState([]);
  const [bookedEventIds, setBookedEventIds] = useState([]);
  const [eventAvailability, setEventAvailability] = useState({});
  const [eventBookingParams, setEventBookingParams] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [helpContent, setHelpContent] = useState('');
  
  // Books pagination state
  const [booksCurrentPage, setBooksCurrentPage] = useState(1);
  const [booksPerPage, setBooksPerPage] = useState(50);
  const [showAllBooks, setShowAllBooks] = useState(false);
  
  // DVDs pagination state
  const [dvdsCurrentPage, setDvdsCurrentPage] = useState(1);
  const [dvdsPerPage, setDvdsPerPage] = useState(50);
  const [showAllDvds, setShowAllDvds] = useState(false);
  
  // CDs pagination state
  const [cdsCurrentPage, setCdsCurrentPage] = useState(1);
  const [cdsPerPage, setCdsPerPage] = useState(50);
  const [showAllCds, setShowAllCds] = useState(false);
  
  // Database backup state
  const [showBackupDialog, setShowBackupDialog] = useState(false);
  const [backupData, setBackupData] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [collectionsInfo, setCollectionsInfo] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(false);
  
  // URL-based navigation support  
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    const eventId = urlParams.get('event');
    const action = urlParams.get('action');
    const sessionId = urlParams.get('session_id');
    const validTabs = ['books', 'cds', 'dvds', 'events', 'users', 'loans', 'financials'];
    
    // Handle payment success redirect.
    // NOTE: The actual confirm-payment / success dialog is handled by the dedicated
    // payment-success useEffect which waits for `isAuthenticated`. Here we only
    // make sure URL params get processed and avoid double-handling.
    if (window.location.pathname.includes('/bookings/payment-success') && sessionId) {
      console.log('🎉 Payment success path detected, deferring to payment-success effect…');
      return; // Exit early to avoid other URL processing
    }
    
    // Store URL parameters for processing after login (if user is not logged in)
    if (!currentUser && (tab || (eventId && action))) {
      console.log('🔗 URL parameters detected, storing for post-login processing:', { tab, eventId, action });
      sessionStorage.setItem('pendingUrlParams', JSON.stringify({ tab, eventId, action }));
      return;
    }
    
    if (tab && validTabs.includes(tab)) {
      setCurrentTab(tab);
    }
    
    // Handle direct event links (booking and viewing)
    if (eventId && (action === 'book' || action === 'view')) {
      setCurrentTab('events'); // Ensure we're on events tab
      setEventBookingParams({eventId, action}); // Store for later processing
    }
  }, [currentUser]); // Add currentUser dependency to re-run when user logs in
  
  // Handle event actions after events are loaded
  useEffect(() => {
    if (eventBookingParams && events.length > 0) {
      const {eventId, action} = eventBookingParams;
      
      // Find the specific event
      const targetEvent = events.find(event => event.id === eventId);
      if (targetEvent) {
        if (action === 'book' && targetEvent.pricing_type === 'custom_rows') {
          // Ticket-type (custom rows) events use their own booking dialog
          setSelectedEventForBooking(targetEvent);
          setIsCustomRowsBookingOpen(true);
        } else if (action === 'book') {
          // Open sophisticated booking dialog for this event (use same initialization as handleSophisticatedBookEvent)
          setSelectedEventForBooking(targetEvent);
          setBookingForm({
            event_id: targetEvent.id,
            tickets: {
              current_user_tickets: 0,
              current_user_price: 0.0,
              current_user_seat_type: null,
              minor_tickets: [],  // Array to store minor ages
              minor_total_price: 0.0,
              minor_seat_type: null,
              family_members: [],  // Array to store family member details
              family_seat_type: null,     // Legacy seat type for backward compatibility
              family_total_price: 0.0
            },
            guided_visit_required: false,
            guided_visit_language: "",
            total_amount: 0,
            comment: ""
          });
          setIsSophisticatedBookingOpen(true);
        } else if (action === 'view') {
          // Open event details/management for this event
          handleViewEvent(targetEvent);
        }
        
        // Clear the URL parameters after handling
        const newUrl = window.location.pathname + window.location.search.replace(/[?&](event|action)=[^&]*/g, '').replace(/^&/, '?').replace(/\?$/, '');
        window.history.replaceState({}, document.title, newUrl);
        
        // Clear booking params
        setEventBookingParams(null);
      }
    }
  }, [events, eventBookingParams]);
  
  // Handle password reset token from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const resetPath = window.location.pathname.includes('reset-password');
    
    if (token && resetPath) {
      setResetToken(token);
      setIsResetPasswordOpen(true);
      
      // Clean up URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);
  
  // Update URL when tab changes
  const handleTabChange = async (newTab) => {
    setCurrentTab(newTab);
    
    // Set appropriate view mode based on tab
    if (newTab === "events") {
      setViewMode(window.innerWidth < 768 ? "cards" : "table"); // Events: cards on phones, table on larger screens
    } else {
      setViewMode("cards"); // Books, CDs, DVDs default to cards view
    }
    
    // Update URL without reloading the page
    const newUrl = new URL(window.location);
    newUrl.searchParams.set('tab', newTab);
    
    // Clear event-specific parameters when changing tabs
    newUrl.searchParams.delete('event');
    newUrl.searchParams.delete('action');
    
    window.history.pushState({}, '', newUrl);
    
    // Fetch data for the selected tab if authenticated
    if (isAuthenticated) {
      setIsDataLoading(true);
      try {
        switch (newTab) {
          case 'books':
            await fetchBooks(searchTerm, selectedCategory, selectedLanguage);
            break;
          case 'cds':
            await fetchCds(searchTerm, selectedCategory);
            break;
          case 'dvds':
            await fetchDvds(searchTerm, selectedDvdCategory);
            break;
          case 'events':
            await fetchEvents();
            await fetchOrganisers();
            await fetchVenues();
            break;
          case 'users':
            if (isAdmin(effectiveUser)) {
              await fetchUsers();
            }
            break;
          case 'loans':
            if (isAdmin(effectiveUser)) {
              await fetchLoans();
            }
            break;
          default:
            break;
        }
      } catch (error) {
        console.error(`Failed to fetch data for ${newTab} tab:`, error);
        // Error is already handled by individual fetch functions
      } finally {
        setIsDataLoading(false);
      }
    }
  };
  
  // Helper function to generate event booking URL
  const getEventBookingUrl = (eventId) => {
    const baseUrl = window.location.origin + window.location.pathname;
    return `${baseUrl}?tab=events&event=${eventId}&action=book`;
  };
  
  // Helper function to generate event view URL
  const getEventViewUrl = (eventId) => {
    const baseUrl = window.location.origin + window.location.pathname;
    return `${baseUrl}?tab=events&event=${eventId}&action=view`;
  };

  // Helper function to get upcoming events (all, sorted by date)
  const getUpcomingEvents = () => {
    const now = new Date();
    return events
      .filter(event => new Date(event.event_date) >= now)
      .filter(event => isAdmin(effectiveUser) || event.status !== 'draft')
      .sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
  };

  // Helper function to get past/archived events of the year
  const getArchivedEvents = () => {
    const now = new Date();
    const yearStart = new Date(now.getFullYear(), 0, 1);
    return events
      .filter(event => {
        const eventDate = new Date(event.event_date);
        return eventDate < now && eventDate >= yearStart;
      })
      .sort((a, b) => new Date(b.event_date) - new Date(a.event_date));
  };

  // Helper function to format event date
  const formatEventDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Helper function to strip HTML tags from text
  const stripHtml = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  // Helper function to get pricing information for display
  const getEventPricing = (event) => {
    if (!event) return null;

    // New custom_rows pricing takes priority — 1..5 admin-defined labelled prices.
    if (event.pricing_type === 'custom_rows' && Array.isArray(event.custom_price_rows) && event.custom_price_rows.length > 0) {
      const priced = event.custom_price_rows.filter(r => Number(r?.amount) > 0);
      if (priced.length === 0) return 'Free Event';
      return priced.map(r => `€${Number(r.amount).toFixed(2)} (${r.label})`).join(' / ');
    }

    // Check for matrix pricing (sophisticated pricing)
    if (event.matrix_pricing) {
      const { member_pricing, minor_pricing, senior_pricing, young_adult_pricing } = event.matrix_pricing;
      
      // For matrix pricing, show section-based prices (Galleria/Platea)
      const sectionPrices = [];
      
      // Check if we have both Galleria and Platea prices (typical for enabled categories)
      let hasGalleria = false;
      let hasPlatea = false;
      let galleriaPrice = null;
      let plateaPrice = null;
      
      // Get prices from the first enabled category to determine section pricing
      if (event.matrix_pricing.member_pricing_enabled && member_pricing) {
        if (member_pricing.galleria_price) {
          hasGalleria = true;
          galleriaPrice = member_pricing.galleria_price;
        }
        if (member_pricing.platea_price) {
          hasPlatea = true;
          plateaPrice = member_pricing.platea_price;
        }
      } else if (event.matrix_pricing.minor_pricing_enabled && minor_pricing) {
        if (minor_pricing.galleria_price) {
          hasGalleria = true;
          galleriaPrice = minor_pricing.galleria_price;
        }
        if (minor_pricing.platea_price) {
          hasPlatea = true;
          plateaPrice = minor_pricing.platea_price;
        }
      } else if (event.matrix_pricing.senior_pricing_enabled && senior_pricing) {
        if (senior_pricing.galleria_price) {
          hasGalleria = true;
          galleriaPrice = senior_pricing.galleria_price;
        }
        if (senior_pricing.platea_price) {
          hasPlatea = true;
          plateaPrice = senior_pricing.platea_price;
        }
      } else if (event.matrix_pricing.young_adult_pricing_enabled && young_adult_pricing) {
        if (young_adult_pricing.galleria_price) {
          hasGalleria = true;
          galleriaPrice = young_adult_pricing.galleria_price;
        }
        if (young_adult_pricing.platea_price) {
          hasPlatea = true;
          plateaPrice = young_adult_pricing.platea_price;
        }
      }
      
      // Build section pricing display
      if (hasGalleria && hasPlatea) {
        // Both sections available - show both with proper formatting
        return `€${galleriaPrice.toFixed(2)} Galleria, €${plateaPrice.toFixed(2)} Platea`;
      } else if (hasGalleria) {
        // Only Galleria available
        return `€${galleriaPrice.toFixed(2)} Galleria`;
      } else if (hasPlatea) {
        // Only Platea available
        return `€${plateaPrice.toFixed(2)} Platea`;
      }
      
      return 'Free Event';
    }
    
    // Check for legacy pricing
    if (event.price_categories) {
      const pricesWithLabels = [];
      const { member_price, minor_price, senior_price, young_adult_price } = event.price_categories;
      
      // Build array of prices with their category labels
      if (event.price_categories.member_price_enabled && member_price) {
        pricesWithLabels.push(`€${member_price.toFixed(2)} (Member)`);
      }
      if (event.price_categories.minor_price_enabled && minor_price) {
        pricesWithLabels.push(`€${minor_price.toFixed(2)} (Under 18)`);
      }
      if (event.price_categories.senior_price_enabled && senior_price) {
        pricesWithLabels.push(`€${senior_price.toFixed(2)} (Senior(over 65))`);
      }
      if (event.price_categories.young_adult_price_enabled && young_adult_price) {
        pricesWithLabels.push(`€${young_adult_price.toFixed(2)} (Under 26)`);
      }
      
      if (pricesWithLabels.length === 0) return 'Free Event';
      if (pricesWithLabels.length === 1) return pricesWithLabels[0];
      
      // For multiple prices, show all with their labels separated by space
      return pricesWithLabels.join(' ');
    }
    
    return 'Free Event';
  };

  // Helper function to show help content
  const showHelpGuide = (type) => {
    let content = '';
    
    if (type === 'system') {
      content = `# ACE System Documentation

## System Overview
The Associazione Culturale Europea - APS Member's Portal is a comprehensive full-stack application designed to manage:
- **Cultural Events**: Exhibitions, concerts, workshops, and other cultural activities
- **User Management**: Member registration, authentication, and role-based access  
- **Media Library**: Books, CDs, DVDs lending system
- **Event Bookings**: Ticket reservations with sophisticated pricing structures
- **Membership Management**: Yearly membership system with automatic renewal
- **Payment Processing**: Stripe integration for secure payments

## Technical Architecture

### Backend (FastAPI + Python)
- **Framework**: FastAPI with async support
- **Database**: MongoDB with motor async driver
- **Authentication**: JWT-based authentication system
- **Payments**: Stripe integration with webhook processing
- **File Storage**: AWS S3 for media and documents
- **PDF Generation**: ReportLab for membership cards
- **Email**: SMTP integration for notifications

### Frontend (React)
- **Framework**: React with Hooks and functional components
- **Routing**: React Router for navigation
- **HTTP Client**: Axios for API communication
- **UI Components**: shadcn/ui component library
- **Icons**: Lucide React icons
- **Styling**: Tailwind CSS with responsive design

### Infrastructure
- **Deployment**: Kubernetes container environment
- **Process Management**: Supervisor for service management
- **Hot Reload**: Enabled for development
- **Background Tasks**: AsyncIO-based scheduled tasks

## API Endpoints

### Authentication
POST /api/auth/login
POST /api/auth/register
GET  /api/auth/me
POST /api/auth/logout
GET  /api/auth/registration-status/{session_id}

### Events Management
GET    /api/events
POST   /api/events
PUT    /api/events/{event_id}
DELETE /api/events/{event_id}
POST   /api/events/{event_id}/close-bookings
GET    /api/events/{event_id}/bookings/excel

### Bookings
POST /api/events/{event_id}/book
GET  /api/bookings/user/{user_id}
POST /api/bookings/payment/create-checkout

### Membership
POST /api/membership/payment/create-checkout
POST /api/membership/renewal/create-checkout
GET  /api/membership/card/{user_id}

### Admin Tools
POST /api/process-pending-sessions
GET  /api/background-task-status
POST /api/webhook/stripe

## Database Collections

### Core Collections
- **users**: User accounts and membership information
- **events**: Event definitions and configuration
- **venues**: Event venue information
- **organisers**: Event organiser details
- **categories**: Event category classifications

### Media Collections
- **books**: Book inventory and metadata
- **cds**: CD inventory and metadata
- **dvds**: DVD inventory and metadata
- **loans**: Media lending records

### Transaction Collections
- **bookings**: Event booking records
- **membership_payment_transactions**: Membership payment tracking
- **booking_payment_transactions**: Event booking payment tracking
- **membership_renewal_transactions**: Membership renewal tracking

## Background Tasks

### Automatic Session Processing
- **Purpose**: Backup system for failed webhook processing
- **Frequency**: Every 10 seconds
- **Session Age Threshold**: 15 seconds
- **Maximum Wait Time**: 25 seconds

### Process Flow
1. Find pending sessions older than 15 seconds
2. Verify payment status with Stripe
3. Process only confirmed paid sessions
4. Complete user registration/booking
5. Generate membership cards and send emails

## System Health & Monitoring

### Key Metrics to Monitor
- Background task execution frequency and success rate
- Payment session processing times
- Database connection health
- Stripe webhook delivery success
- User registration completion rates
- Event booking success rates

### Troubleshooting Common Issues
1. **Stuck Registration**: Use admin manual processing endpoint
2. **Payment Issues**: Check Stripe dashboard and webhook logs
3. **Capacity Problems**: Verify event max_capacity settings
4. **Background Task Issues**: Monitor /api/background-task-status
5. **Email Delivery**: Check SMTP configuration and logs

For complete detailed documentation, see: /app/SYSTEM_DOCUMENTATION.md`;

    } else if (type === 'admin') {
      content = `# Administrator Help Guide

## Getting Started
- **Admin tools**: The green "User Management" tab and admin-only buttons appear only for administrators.
- **View as member**: Shows the site as members see it (drafts hidden). It is a preview only — you keep your admin rights, so test real permissions with a member account.

## Events
- **Creating**: "Cultural Events Dashboard" → "Add New Event". Set status to **Published** to make it visible.
- **Drafts**: Draft events are never shown to members and cannot be booked by them.
- **Closing bookings**: Use the red X button to stop new bookings.

## Ticket Categories & Limits
- Under **Custom Ticket Types (1–5)** give each category a label, a price and optionally **Tickets available** (empty = no limit).
- Each category counts its own tickets. Card payments count only once paid.
- When a category is full, members see **Sold out, choose another category**.
- Manual/admin bookings also count; going over a limit shows a warning but is still allowed.

## Minors-only Prices
- Tick **Minors only** on a ticket row to show it only in the Minors section of the booking form, not under "For yourself" or partner.
- For existing events, edit the event and tick the box on the relevant row.

## Event Links & QR Codes
- **Copy view link** / **Copy booking link** give a direct link to the event page or straight to its booking form.
- **QR code for posters** opens a printable QR code pointing at the event; members log in and land on it.
- The site-wide QR code (ace-members.eu) is available as ace-members-qr.png / .jpg for general flyers.

## Guided Visits: Languages & Guide Lists
- Members choose one guide language (Italian, English or both) when booking.
- In the event's financial view you see the count per language.
- Use **Guide list (Excel)** to download separate attendee lists for the Italian and English guides.

## Payment Checks
- **Payment history**: Shows all payments with PDF/CSV export.
- **Payment links**: For manual income entries, "Send payment link" emails a Stripe link; **Check payment** asks Stripe whether it was paid and marks it paid automatically.
- **Paid but not updated?** The app re-checks Stripe when the member returns from checkout. If a membership or booking still isn't updated, confirm in the Stripe dashboard that the payment succeeded and that the live webhook (checkout.session.completed → ace-members.eu/api/webhook/stripe) is active.
- Stripe descriptions include the membership year, event or season pass name and the member's name, so payments are easy to match.

## Memberships
- Monitor status and expiry dates in "User Management"; registrations follow the configured membership year.
- Membership cards can be resent in bulk to members renewed for the latest year.

## Troubleshooting
- **Capacity problems**: Check the event's overall capacity and each category's "Tickets available".
- **Wrong event time**: Times are shown in Italian local time; re-open the event to confirm the saved time.`;

    } else {
      content = `# User Help Guide

## Getting Started
- **Sign up**: click "Sign Up", fill in your details, choose your membership year and pay securely by card (Stripe). Your membership card arrives by email.
- **Membership year**: near the end of the year you can choose between the current year and the next one. The price of each year is shown on the form.
- **Log in**: use your email and password. Forgot it? Click "Forgot password" on the login window to receive a reset link.
- **Active membership**: needed to book events and borrow from the library.

## Your Account (click your name, top right)
- **Membership status**: shows "Active Member until 31/12/YYYY" or "Membership expired".
- **Membership card**: download it as a PDF at any time. It shows your membership year.
- **Link partner / ACE member**: type your partner's **complete email address** and click "Check". When the member is found, click "Link". Only one partner can be linked.
- **Payment history**: see all your payments (membership, events, season passes) and download them as PDF or CSV.
- **Change password**.
- **Delete account**: sends a deletion request to the administration, who will process it.

## Renewing Your Membership
- When renewal opens, a **"Renew for YYYY"** button appears on your dashboard.
- Pay by card. Your new card is emailed to you and your status updates automatically.
- If you paid but your status has not changed after a few minutes, please contact the administration.

## Events

### Finding events
- Open the **Events** tab to see **Upcoming Events** (draft events are not shown until they are published).
- Click an event to see its details, prices, venue and date.
- Links or **QR codes** on posters and websites open the event (or its booking form) directly, after you log in.

### Booking
1. Click **Book** on the event.
2. **For yourself**: choose one ticket type (or "No ticket for me" if you only book for others).
3. **Your partner** (if linked): tick the partner box and choose their ticket type. Your partner can also book separately later.
4. **Minors**: add each child with name and age, and pick their ticket type (children's prices appear only here).
5. **Guided visits**: choose one language: **Italian**, **English** or **Both languages**.
6. **Comments**: add any information for the organisers (optional).
7. Click to pay securely by card. You receive a confirmation email with a calendar invite.

**Good to know**
- "Only N places left" appears when 3 places or fewer remain.
- A ticket type marked **Sold out** cannot be chosen; pick another type.
- Bookings close at the booking deadline set for the event.
- If the event is full, you can join the **waiting list**; you'll be contacted if a place becomes free.
- Each person can only have one ticket per event; you'll be told if you or your partner are already booked.

### My Bookings & My Past Events
- **My Bookings** lists your upcoming reservations.
- **My Past Events** shows the events you attended.

## Season Passes
- Passes on sale are shown **below Upcoming Events**.
- A pass covers several events of the season; buy it once and your bookings for the included events are created for you.

## Library & Mediateca
- Click **Consult our Library & Mediateca** to browse books, CDs and DVDs and see which items are available or on loan.
- Loans are managed by the library staff: ask them to borrow or return an item.

## FAQ
**Q: I can't book an event.**
A: Check that your membership is active, the event is published, the booking deadline hasn't passed and tickets are still available.

**Q: My partner can't be found when linking.**
A: Use the exact email address your partner uses for their ACE account (capital letters don't matter).

**Q: I paid but didn't receive a confirmation.**
A: Check your spam folder. Your booking normally appears within a minute; otherwise contact the administration.

**Q: Times in emails or on the site look wrong.**
A: All times are shown in Italian time. Please tell the administration if something looks incorrect.`;
    }
    
    setHelpContent(content);
    setShowHelp(true);
  };

  // Helper functions for books pagination (sorting handled by backend)
  const getPaginatedBooks = () => {
    if (!Array.isArray(books)) return [];
    
    // Books are already sorted by backend, no need to sort again
    if (showAllBooks) {
      return books;
    }
    
    const startIndex = (booksCurrentPage - 1) * booksPerPage;
    const endIndex = startIndex + booksPerPage;
    return books.slice(startIndex, endIndex);
  };
  
  const getTotalPages = () => {
    if (!Array.isArray(books) || showAllBooks) return 1;
    return Math.ceil(books.length / booksPerPage);
  };
  
  const handleBooksPageSizeChange = (newSize) => {
    if (newSize === 'all') {
      setShowAllBooks(true);
      setBooksCurrentPage(1);
    } else {
      setShowAllBooks(false);
      setBooksPerPage(newSize);
      setBooksCurrentPage(1); // Reset to first page when changing page size
    }
  };
  
  const handleBooksPageChange = (newPage) => {
    setBooksCurrentPage(newPage);
    // Scroll to top of books section
    document.querySelector('[data-books-section]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Helper functions for DVDs pagination with sorting
  const getPaginatedDvds = () => {
    if (!Array.isArray(dvds)) return [];
    
    // Sort the DVDs first
    const sortedDvds = sortData(dvds, dvdsSortConfig);
    
    if (showAllDvds) {
      return sortedDvds;
    }
    
    const startIndex = (dvdsCurrentPage - 1) * dvdsPerPage;
    const endIndex = startIndex + dvdsPerPage;
    return sortedDvds.slice(startIndex, endIndex);
  };
  
  const getDvdsTotalPages = () => {
    if (!Array.isArray(dvds) || showAllDvds) return 1;
    return Math.ceil(dvds.length / dvdsPerPage);
  };
  
  const handleDvdsPageSizeChange = (newSize) => {
    if (newSize === 'all') {
      setShowAllDvds(true);
      setDvdsCurrentPage(1);
    } else {
      setShowAllDvds(false);
      setDvdsPerPage(newSize);
      setDvdsCurrentPage(1); // Reset to first page when changing page size
    }
  };
  
  const handleDvdsPageChange = (newPage) => {
    setDvdsCurrentPage(newPage);
    // Scroll to top of dvds section
    document.querySelector('[data-dvds-section]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Helper functions for CDs pagination with sorting
  const getPaginatedCds = () => {
    if (!Array.isArray(cds)) return [];
    
    // Sort the CDs first
    const sortedCds = sortData(cds, cdsSortConfig);
    
    if (showAllCds) {
      return sortedCds;
    }
    
    const startIndex = (cdsCurrentPage - 1) * cdsPerPage;
    const endIndex = startIndex + cdsPerPage;
    return sortedCds.slice(startIndex, endIndex);
  };
  
  const getCdsTotalPages = () => {
    if (!Array.isArray(cds) || showAllCds) return 1;
    return Math.ceil(cds.length / cdsPerPage);
  };
  
  const handleCdsPageSizeChange = (newSize) => {
    if (newSize === 'all') {
      setShowAllCds(true);
      setCdsCurrentPage(1);
    } else {
      setShowAllCds(false);
      setCdsPerPage(newSize);
      setCdsCurrentPage(1); // Reset to first page when changing page size
    }
  };
  
  const handleCdsPageChange = (newPage) => {
    setCdsCurrentPage(newPage);
    // Scroll to top of cds section
    document.querySelector('[data-cds-section]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Sorting functions
  const sortData = (data, sortConfig) => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Handle book_id field fallback - try book_id first, then id
      if (sortConfig.key === 'book_id') {
        aValue = a.book_id || a.id;
        bValue = b.book_id || b.id;
      }
      
      // Handle ID fields with numerical sorting
      if (sortConfig.key.includes('_id') || sortConfig.key === 'id') {
        const aNum = parseInt(aValue) || 0;
        const bNum = parseInt(bValue) || 0;
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }
      
      // Handle text fields with alphabetical sorting
      const aText = (aValue || '').toString().toLowerCase();
      const bText = (bValue || '').toString().toLowerCase();
      
      if (sortConfig.direction === 'asc') {
        return aText.localeCompare(bText);
      } else {
        return bText.localeCompare(aText);
      }
    });
  };

  const handleSort = (key, sortConfig, setSortConfig) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sorting is handled by clickable table headers in table view

  // Database backup management functions
  const createDatabaseBackup = async () => {
    setBackupLoading(true);
    try {
      console.log('🔄 Creating database backup...');
      const response = await axios.post('/admin/backup/create');
      
      if (response.data.success) {
        setBackupData(response.data.backup_data);
        toast.success(`Backup created successfully! ${response.data.summary.total_documents} documents backed up.`);
        console.log('✅ Database backup created successfully');
      }
    } catch (error) {
      console.error('❌ Failed to create backup:', error);
      toast.error(`Failed to create backup: ${error.response?.data?.detail || error.message}`);
    } finally {
      setBackupLoading(false);
    }
  };
  
  const downloadBackup = () => {
    if (!backupData) return;
    
    const dataStr = JSON.stringify(backupData, null, 2);
    const dataBlob = new Blob([dataStr], {type: 'application/json'});
    
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ace_library_backup_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Backup file downloaded successfully!');
  };
  
  const handleRestoreUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const backupData = JSON.parse(e.target.result);
        
        if (!backupData.collections) {
          throw new Error('Invalid backup file format');
        }
        
        const confirmRestore = window.confirm(
          'WARNING: This will replace all current data with the backup data. This action cannot be undone. Are you sure?'
        );
        
        if (!confirmRestore) return;
        
        setRestoreLoading(true);
        
        const response = await axios.post('/admin/backup/restore', backupData);
        
        if (response.data.success) {
          toast.success('Database restored successfully! Please refresh the page.');
          console.log('✅ Database restored successfully');
          
          // Refresh data after restoration
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
        
      } catch (error) {
        console.error('❌ Failed to restore backup:', error);
        toast.error(`Failed to restore backup: ${error.response?.data?.detail || error.message}`);
      } finally {
        setRestoreLoading(false);
      }
    };
    
    reader.readAsText(file);
    // Clear the input
    event.target.value = '';
  };
  
  const fetchCollectionsInfo = async () => {
    try {
      const response = await axios.get('/admin/backup/collections-info');
      setCollectionsInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch collections info:', error);
      toast.error('Failed to fetch database information');
    }
  };
  
  // Fetch collections info when backup dialog opens
  const openBackupDialog = () => {
    setShowBackupDialog(true);
    fetchCollectionsInfo();
  };

  // Helper function to close event bookings (admin only)
  const closeEventBookings = async (eventId, eventTitle) => {
    if (currentUser?.role !== 'Administrator') return;
    
    const confirmed = window.confirm(
      `Are you sure you want to close bookings for "${eventTitle}"?\n\nThis will set the event status to "closed" and prevent new bookings.`
    );
    
    if (confirmed) {
      try {
        await axios.put(`/events/${eventId}`, {
          status: 'closed'
        });
        toast.success('Event bookings closed successfully');
        fetchEvents(); // Refresh events list
      } catch (error) {
        console.error('Failed to close event bookings:', error);
        toast.error('Failed to close event bookings');
      }
    }
  };

  // Helper function to reopen event bookings (admin only)
  const reopenEventBookings = async (eventId, eventTitle) => {
    if (currentUser?.role !== 'Administrator') return;
    
    const confirmed = window.confirm(
      `Are you sure you want to reopen bookings for "${eventTitle}"?\n\nThis will set the event status to "published" and allow new bookings.`
    );
    
    if (confirmed) {
      try {
        await axios.put(`/events/${eventId}`, {
          status: 'published'
        });
        toast.success('Event bookings reopened successfully');
        fetchEvents(); // Refresh events list
      } catch (error) {
        console.error('Failed to reopen event bookings:', error);
        toast.error('Failed to reopen event bookings');
      }
    }
  };
  const [showDashboard, setShowDashboard] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedCoverImage, setSelectedCoverImage] = useState(null);
  const [selectedDvdCoverImage, setSelectedDvdCoverImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [dvdImagePreview, setDvdImagePreview] = useState(null);
  const [eventImagePreview, setEventImagePreview] = useState(null);

  // Helper function to get book cover URL - using event image pattern for public access
  const getBookCoverUrl = (book) => {
    // Priority 1: Check if cover is stored in public event-images path (working pattern)
    if (book.cover_image_s3 && book.cover_image_s3.includes('event-images/')) {
      // Use direct S3 URL for public images (like events)
      return `${process.env.REACT_APP_S3_BUCKET_URL}/${book.cover_image_s3}`;
    }
    
    // Priority 2: Try the backend serving endpoint for private images
    if (book.cover_image_s3) {
      return `${process.env.REACT_APP_BACKEND_URL}/api/s3/serve/${book.cover_image_s3}`;
    }
    
    // Priority 3: Use direct S3 URL if available (might be blocked for book-covers path)
    if (book.cover_image_url) {
      return book.cover_image_url;
    }
    
    // Priority 2: Use local cover_image path
    if (!book.cover_image) return null;
    
    // Extract filename from the static path (e.g., "/static/book_covers/filename.jpg" -> "filename.jpg")
    const filename = book.cover_image.split('/').pop();
    
    // Use the API endpoint with error handling
    return `${process.env.REACT_APP_BACKEND_URL}/api/book-cover/${filename}`;
  };
  
  // Helper function to get DVD cover URL - using event image pattern for public access  
  const getDvdCoverUrl = (dvd) => {
    // Priority 1: Check if cover is stored in public event-images path (working pattern)
    if (dvd.cover_image_s3 && dvd.cover_image_s3.includes('event-images/')) {
      // Use direct S3 URL for public images (like events)
      return `${process.env.REACT_APP_S3_BUCKET_URL}/${dvd.cover_image_s3}`;
    }
    
    // Priority 2: Try the backend serving endpoint for private images
    if (dvd.cover_image_s3) {
      return `${process.env.REACT_APP_BACKEND_URL}/api/s3/serve/${dvd.cover_image_s3}`;
    }
    
    // Priority 3: Use direct S3 URL if available (might be blocked for dvd-covers path)
    if (dvd.cover_image_url) {
      return dvd.cover_image_url;
    }
    
    // Priority 2: Use local cover_image path
    if (!dvd.cover_image) return null;
    
    // Extract filename from the static path (e.g., "/static/dvd_covers/filename.jpg" -> "filename.jpg")
    const filename = dvd.cover_image.split('/').pop();
    
    // Use the API endpoint with error handling
    return `${process.env.REACT_APP_BACKEND_URL}/api/dvd-cover/${filename}`;
  };

  // Helper function to get event image URL with S3 support and fallback handling
  const getEventImageUrlForForm = (event) => {
    // Priority 1: Use S3 images via backend proxy if available
    if (event.images_s3 && event.images_s3.length > 0) {
      const filename = event.images_s3[0].split('/').pop(); // Extract filename from S3 key
      return `${process.env.REACT_APP_BACKEND_URL}/api/images/event/${filename}`;
    }
    
    // Priority 2: Use image URLs directly if available
    if (event.image_urls && event.image_urls.length > 0) {
      const imageUrl = event.image_urls[0];
      const filename = imageUrl.split('/').pop(); // Extract filename from URL
      return `${process.env.REACT_APP_BACKEND_URL}/api/images/event/${filename}`;
    }
    
    // Priority 3: Use local images
    if (event.images && event.images.length > 0) {
      const filename = event.images[0];
      return `${process.env.REACT_APP_BACKEND_URL}/api/images/event/${filename}`;
    }
    
    return null;
  };

  // S3 Upload helper function for book covers
  const uploadBookCoverToS3 = async (bookId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`/books/${bookId}/cover-s3`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('Book cover uploaded to S3:', response.data);
      return response.data;
    } catch (error) {
      console.error('S3 book cover upload failed:', error);
      throw error;
    }
  };
  
  // S3 Upload helper function for DVD covers
  const uploadDvdCoverToS3 = async (dvdId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`/dvds/${dvdId}/cover-s3`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('DVD cover uploaded to S3:', response.data);
      return response.data;
    } catch (error) {
      console.error('S3 DVD cover upload failed:', error);
      throw error;
    }
  };

  // Add error handling for missing DVD cover images
  const handleDvdCoverError = (event, dvd) => {
    console.warn(`DVD cover missing for DVD: ${dvd.title} (ID: ${dvd.id})`);
    event.target.style.display = 'none';
    if (event.target.nextSibling) {
      event.target.nextSibling.style.display = 'flex';
    }
  };
  
  // Add error handling for missing book cover images
  const handleBookCoverError = (event, book) => {
    console.warn(`Book cover missing for book: ${book.book_title} (ID: ${book.id})`);
    event.target.style.display = 'none';
    if (event.target.nextSibling) {
      event.target.nextSibling.style.display = 'flex';
    }
  };
  
  // Search functions for loan form
  const searchUsers = async (query) => {
    if (query.length < 2) {
      setUserSearchResults([]);
      return;
    }
    
    setUserSearchLoading(true);
    try {
      const response = await axios.get(`/search/users`, {
        params: { q: query, limit: 15 }
      });
      setUserSearchResults(response.data.results || []);
    } catch (error) {
      console.error('User search failed:', error);
      setUserSearchResults([]);
    } finally {
      setUserSearchLoading(false);
    }
  };
  
  const searchMedia = async (query, mediaType) => {
    if (query.length < 2 || !mediaType) {
      setMediaSearchResults([]);
      return;
    }
    
    setMediaSearchLoading(true);
    try {
      const response = await axios.get(`/search/media`, {
        params: { q: query, media_type: mediaType, limit: 15 }
      });
      setMediaSearchResults(response.data.results || []);
    } catch (error) {
      console.error('Media search failed:', error);
      setMediaSearchResults([]);
    } finally {
      setMediaSearchLoading(false);
    }
  };
  
  
  const [viewMode, setViewMode] = useState(window.innerWidth < 768 ? "cards" : "table"); // Events: cards on phones, table otherwise
  const [usersViewMode, setUsersViewMode] = useState("cards"); // Default to "cards" for users management
  const [showLoginReport, setShowLoginReport] = useState(false);
  const [usersSearchTerm, setUsersSearchTerm] = useState("");
  const [showLibraryResources, setShowLibraryResources] = useState(false); // Collapsible library resources
  const [isFixingImages, setIsFixingImages] = useState(false);
  const [languageCounts, setLanguageCounts] = useState({});
  const [selectedLanguage, setSelectedLanguage] = useState(""); // Language filter state
  const [selectedDvdCategory, setSelectedDvdCategory] = useState(""); // DVD category filter state
  const [isOrganisersManagementOpen, setIsOrganisersManagementOpen] = useState(false);
  const [isVenuesManagementOpen, setIsVenuesManagementOpen] = useState(false);
  // Sorting states for each collection
  const [booksSortConfig, setBooksSortConfig] = useState({ key: 'book_id', direction: 'desc' }); // Default sort by book_id descending
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [booksHasMore, setBooksHasMore] = useState(false);
  const [booksTotalCount, setBooksTotalCount] = useState(0);
  const [booksLoading, setBooksLoading] = useState(false);
  const [dvdsSortConfig, setDvdsSortConfig] = useState({ key: 'dvd_id', direction: 'desc' }); // Default sort by dvd_id descending
  const [selectedDVDs, setSelectedDVDs] = useState([]);
  const [dvdsHasMore, setDvdsHasMore] = useState(false);
  const [dvdsTotalCount, setDvdsTotalCount] = useState(0);
  const [dvdsLoading, setDvdsLoading] = useState(false);
  const [cdsSortConfig, setCdsSortConfig] = useState({ key: 'cd_id', direction: 'desc' }); // Default sort by cd_id descending
  const [selectedCDs, setSelectedCDs] = useState([]);
  const [cdsHasMore, setCdsHasMore] = useState(false);
  const [cdsTotalCount, setCdsTotalCount] = useState(0);
  const [cdsLoading, setCdsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [loanStatuses, setLoanStatuses] = useState({}); // Cache for media loan status
  // Registration success now handled by dedicated page
  const [showRegistrationCancelled, setShowRegistrationCancelled] = useState(false);

  // Cultural Events state - moved up to fix initialization order
  const [organisers, setOrganisers] = useState([]);
  const [venues, setVenues] = useState([]);
  const [eventCategories, setEventCategories] = useState([]);
  const [viewingEvent, setViewingEvent] = useState(null);
  const [isEventDetailOpen, setIsEventDetailOpen] = useState(false);

  // Additional authentication state
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [welcomeOverlay, setWelcomeOverlay] = useState(null); // { name: string, fading: boolean }
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Password reset state
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isDeleteAccountOpen, setIsDeleteAccountOpen] = useState(false);
  const [paymentHistoryTarget, setPaymentHistoryTarget] = useState(null); // {userId, name} | null
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
  const [resetToken, setResetToken] = useState("");
  
  // Loading states for data fetching
  const [isDataLoading, setIsDataLoading] = useState(false);

  // Axios interceptors for authentication handling
  React.useEffect(() => {
    // Set axios base URL
    axios.defaults.baseURL = API;
    
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        // Always check for the latest token from localStorage and state
        const currentToken = authToken || localStorage.getItem('authToken');
        if (currentToken) {
          config.headers.Authorization = `Bearer ${currentToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Only clear auth on 401 if it's explicitly an auth endpoint failure
        // Don't clear auth on 500 errors or other API failures
        if (error.response?.status === 401 && authToken) {
          const url = error.config?.url || '';
          // Only logout if it's an auth-related 401 (not other API failures)
          if (url.includes('/auth/') || url.includes('/login')) {
            console.warn('Authentication failed, logging out user');
            // Clear auth state
            setAuthToken(null);
            setCurrentUser(null);
            setIsAuthenticated(false);
            localStorage.removeItem('authToken');
            
            // Show login dialog
            toast.error('Your session has expired. Please log in again.');
            setIsLoginOpen(true);
          } else {
            console.warn('API call failed with 401, but keeping user logged in');
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [authToken]);

  // Form states
  const [bookForm, setBookForm] = useState({
    book_id: "", author: "", book_title: "", language: "", comment: "", more_info: "", purchasing_year: "", place: "", category: "none"
  });
  const [cdForm, setCdForm] = useState({
    cd_id: "", title: "", executor: "", category: "", support: "", acquisition_date: ""
  });
  const [dvdForm, setDvdForm] = useState({
    dvd_id: "", title: "", languages: "", actors: "", director: "", production_year: "", purchasing_year: "", category: "", area: ""
  });
  const [userForm, setUserForm] = useState({
    first_name_1: "", first_name_2: "", last_name: "", email: "", mobile: "", landline: "",
    last_year_member: false, member_category: "JRC Internal",
    birth_date: "", birth_place: "", nationality: "", other_nationality: "", address: "",
    postal_code: "", city: "", province: "", country: "Italy", fiscal_code: ""
  });
  const [loanForm, setLoanForm] = useState({
    user_id: "", media_id: "", media_type: "book"
  });
  
  // Search states for loan form
  const [userSearch, setUserSearch] = useState("");
  const [mediaSearch, setMediaSearch] = useState("");
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [mediaSearchResults, setMediaSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [mediaSearchLoading, setMediaSearchLoading] = useState(false);

  // Debounced search functions (placed after state declarations)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (userSearch) {
        searchUsers(userSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearch]);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      if (mediaSearch && loanForm.media_type) {
        searchMedia(mediaSearch, loanForm.media_type);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [mediaSearch, loanForm.media_type]);
  
  // Set initial view mode based on current tab
  useEffect(() => {
    if (currentTab === "events") {
      setViewMode(window.innerWidth < 768 ? "cards" : "table"); // Events: cards on phones, table on larger screens
    } else {
      setViewMode("cards"); // Books, CDs, DVDs default to cards view
    }
  }, []); // Only run on mount

  // Cultural Events form states
  const [eventFormState, setEventFormState] = useState({
    id: "",
    title: "", description_text: "", description_html: "", event_date: "", booking_deadline: "", 
    organiser_ids: [], venue_ids: [], 
    category_tags: [], event_label: "", status: "draft", max_capacity: "", guided_visit: false, comment: "",
    guest_tickets_enabled: false, guest_surcharge: "",
    // Legacy pricing structure
    price_categories: {
      member_price: "",
      minor_price: "",
      senior_price: "",
      young_adult_price: "",
      member_price_enabled: true,
      minor_price_enabled: false,
      senior_price_enabled: false,
      young_adult_price_enabled: false
    },
    seat_types: [],
    // New Opera-style matrix pricing
    matrix_pricing: {
      member_pricing: { galleria_price: "", platea_price: "" },
      minor_pricing: { galleria_price: "", platea_price: "" },
      senior_pricing: { galleria_price: "", platea_price: "" },
      young_adult_pricing: { galleria_price: "", platea_price: "" },
      member_pricing_enabled: true,
      minor_pricing_enabled: false,
      senior_pricing_enabled: false,
      young_adult_pricing_enabled: false,
      galleria_enabled: false,
      platea_enabled: false
    },
    // Custom section labels for matrix pricing
    section_1_label: "Galleria",  // Default label for first section
    section_2_label: "Platea",     // Default label for second section
    pricing_type: "standard", // "standard" | "matrix" | "custom_rows"
    // New model: 1..5 admin-defined ticket types (label + price). Members pick
    // themselves — age is no longer a pricing criterion.
    custom_price_rows: [{ id: null, label: "Adult", amount: "" }]
  });
  const [organiserForm, setOrganiserForm] = useState({
    name: "", email: "", phone: "", bio: "", website: ""
  });

  // Wrapper function to track eventForm changes
  const eventForm = eventFormState;
  const setEventForm = (newState) => {
    console.log('🔍 SETEVENTFORM CALLED:', {
      timestamp: new Date().toLocaleTimeString(),
      caller: new Error().stack.split('\n')[2]?.trim(),
      member_price_before: eventFormState.price_categories?.member_price,
      member_price_after: newState.price_categories?.member_price,
      setting_member_price: newState.price_categories?.member_price,
      full_stack: new Error().stack
    });
    setEventFormState(newState);
  };

  const [venueForm, setVenueForm] = useState({
    name: "", address: "", city: "", capacity: "", contact_info: "", facilities: []
  });

  // Default seat types for events
  const [defaultSeatTypes, setDefaultSeatTypes] = useState([]);

  // Booking system state
  const [bookings, setBookings] = useState([]);
  const [bookingForm, setBookingForm] = useState({
    event_id: "",
    // New sophisticated ticket structure with seat sections
    tickets: {
      current_user_tickets: 0,
      current_user_seat_section: null,  // "galleria" or "platea" for Opera venues
      current_user_seat_type: null,     // Legacy seat type for backward compatibility
      current_user_price: 0.0,
      minor_tickets: [], // Array of ages
      minor_seat_section: null,  // "galleria" or "platea" for Opera venues
      minor_seat_type: null,     // Legacy seat type for backward compatibility
      minor_total_price: 0.0,
      family_members: [], // Array of {first_name, last_name, age}
      family_seat_section: null,  // "galleria" or "platea" for Opera venues
      family_seat_type: null,     // Legacy seat type for backward compatibility
      family_total_price: 0.0
    },
    linked_member_tickets: 0,  // For "Book for Another Member" feature
    booking_for_user_id: null,  // ID of the member being booked for
    guided_visit_required: false,
    guided_visit_language: "",
    total_amount: 0,
    comment: ""
  });
  const [isBookingDialogOpen, setIsBookingDialogOpen] = useState(false);
  const [isSophisticatedBookingOpen, setIsSophisticatedBookingOpen] = useState(false);
  const [isAdminLoanManagementOpen, setIsAdminLoanManagementOpen] = useState(false);
  const [isBookCategoryManagementOpen, setIsBookCategoryManagementOpen] = useState(false);
  const [bookCategories, setBookCategories] = useState([]);
  const [selectedEventForBooking, setSelectedEventForBooking] = useState(null);
  const [bookingCheckoutUrl, setBookingCheckoutUrl] = useState(null);
  const [userBookings, setUserBookings] = useState([]);

  // Map of event_id -> latest pending booking with checkout_url (for "Resume payment" CTA)
  const pendingBookingsByEvent = useMemo(() => {
    const map = {};
    (userBookings || []).forEach(b => {
      if (b.payment_status === 'pending' && b.checkout_url && b.event_id) {
        const existing = map[b.event_id];
        if (!existing || (b.booking_date && b.booking_date > existing.booking_date)) {
          map[b.event_id] = b;
        }
      }
    });
    return map;
  }, [userBookings]);
  const [userLoans, setUserLoans] = useState([]);
  const [eventBookings, setEventBookings] = useState([]);
  const [bookingStats, setBookingStats] = useState(null);
  const [showBookingHistory, setShowBookingHistory] = useState(false);
  const [showLoanHistory, setShowLoanHistory] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [selectedEventForManagement, setSelectedEventForManagement] = useState(null);
  const [isEventBookingsDialogOpen, setIsEventBookingsDialogOpen] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentBookingId, setPaymentBookingId] = useState(null);

  // Loan history export filters state
  const [loanExportFilters, setLoanExportFilters] = useState({
    startDate: '',
    endDate: '',
    status: '',
    userId: ''
  });
  const [showLoanExportDialog, setShowLoanExportDialog] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null
  });

  // Authentication form states
  const [loginForm, setLoginForm] = useState({
    username: "", password: "", remember_me: false
  });
  const [registerForm, setRegisterForm] = useState({
    first_name_1: "", first_name_2: "", last_name: "", email: "", password: "", 
    mobile: "", landline: "",
    last_year_member: false, member_category: "JRC Internal",
    birth_date: "", birth_place: "", nationality: "", other_nationality: "", 
    address: "", postal_code: "", city: "", province: "", country: "Italy", fiscal_code: ""
  });

  // Configure axios defaults
  axios.defaults.baseURL = API;
  
  // Add auth token to requests if available - Enhanced with immediate token setting
  useEffect(() => {
    // Get the most current token from either state or localStorage
    const currentToken = authToken || localStorage.getItem('authToken');
    
    if (currentToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
      console.log('✅ Authorization header set for axios defaults');
    } else {
      delete axios.defaults.headers.common['Authorization'];
      console.log('❌ Authorization header removed from axios defaults');
    }
  }, [authToken]);

  // Check for registration success/cancelled URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    // Only handle registration success if we're on the root path and have registration data
    if (sessionId && window.location.pathname === '/' && sessionStorage.getItem('pendingRegistration')) {
      // Handle successful payment return for REGISTRATION only
      handleRegistrationSuccess(sessionId);
    } else if (window.location.pathname === '/registration-cancelled') {
      setShowRegistrationCancelled(true);
    }
  }, []);

  // Handle registration success after payment - redirect to result page
  const handleRegistrationSuccess = (sessionId) => {
    try {
      console.log('🎉 Registration payment completed, redirecting to result page...', { sessionId });
      
      // Clear stored data
      sessionStorage.removeItem('pendingRegistration');
      sessionStorage.removeItem('registrationSessionId');
      
      // Redirect to registration success page with session ID  
      window.location.href = `/registration-success?session_id=${sessionId}`;
      
    } catch (error) {
      console.error('❌ Error handling registration redirect:', error);
      // Fallback: still redirect to success page, let it handle the error
      window.location.href = `/registration-success?session_id=${sessionId}`;
    }
  };

  // Fetch booking data when user is authenticated and events are loaded.
  // NOTE: The previous implementation here fetched bookings for EVERY event into a shared
  // `eventBookings` state used by both capacity computation AND the bookings table dialog.
  // This caused the dialog to render empty (state shape collision) and stale capacity values.
  // Capacity numbers now come from `eventAvailability` (backend source of truth) so this
  // background fan-out is no longer needed.

  // Authentication functions
  const checkAuthStatus = async () => {
    if (!authToken) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.get('auth/me');
      setCurrentUser(response.data);
      setIsAuthenticated(true);
      
      // Load all tab counts in background - don't block UI
      loadInitialTabCounts(response.data).catch(err => 
        console.error('Background data loading failed:', err)
      );
      
    } catch (error) {
      console.error('Auth check failed:', error);
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  // Function to load initial counts for all tabs
  const loadInitialTabCounts = async (user) => {
    try {
      // Load counts for all tabs simultaneously
      const promises = [
        fetchBooks(), // Books count
        fetchCds(),   // CDs count  
        fetchDvds(),  // DVDs count
        fetchEvents(), // Events count
        fetchNationalities(), // Always needed
        fetchDefaultSeatTypes(), // Needed for event creation
        fetchBookCategories(), // Always needed for book filtering
      ];

      // Add admin-only data if user is administrator
      if (user?.role === 'Administrator') {
        promises.push(
          fetchUsers(),     // Users count
          fetchLoans(),     // Loans count
          fetchOrganisers(), // Needed for events
          fetchVenues()     // Needed for events
        );
      } else {
        // Regular users still need organisers and venues for events display
        promises.push(
          fetchOrganisers(),
          fetchVenues()
        );
      }

      // Execute all fetches in parallel for fast loading
      await Promise.all(promises);
      console.log('Initial tab counts loaded successfully');
      
    } catch (error) {
      console.error('Failed to load initial tab counts:', error);
      // Individual fetch functions already handle their own errors gracefully
    }
  };

  const login = async (credentials) => {
    try {
      // Show loading overlay IMMEDIATELY before any API calls
      setWelcomeOverlay({ name: '', fading: false, loading: true });
      
      const formData = new FormData();
      formData.append('username', credentials.username);
      formData.append('password', credentials.password);

      const response = await axios.post(`/auth/login`, formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });

      const { access_token, user } = response.data;
      
      // Set token in localStorage first
      localStorage.setItem('authToken', access_token);
      
      // Set axios defaults immediately
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Then update state
      setAuthToken(access_token);
      setIsAuthenticated(true);
      
      // Fetch complete user profile with membership data
      let completeUser = null;
      try {
        const userProfileResponse = await axios.get('/auth/me');
        completeUser = userProfileResponse.data;
        setCurrentUser(completeUser);
      } catch (profileError) {
        console.error('Failed to fetch user profile:', profileError);
        // Fallback to login response user data
        setCurrentUser(user);
      }
      
      console.log('🎉 Login successful - token set in localStorage and axios defaults');
      
      // Check for stored URL parameters to process after login
      const storedParams = sessionStorage.getItem('pendingUrlParams');
      if (storedParams) {
        try {
          const params = JSON.parse(storedParams);
          console.log('🔗 Processing stored URL parameters after login:', params);
          
          const validTabs = ['books', 'cds', 'dvds', 'events', 'users', 'loans', 'financials'];
          
          if (params.tab && validTabs.includes(params.tab)) {
            setCurrentTab(params.tab);
          }
          
          if (params.eventId && (params.action === 'book' || params.action === 'view')) {
            setCurrentTab('events');
            setEventBookingParams({eventId: params.eventId, action: params.action});
          }
          
          // Clear stored parameters
          sessionStorage.removeItem('pendingUrlParams');
          
          // Clear URL parameters 
          const newUrl = window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);
          
        } catch (error) {
          console.error('Error processing stored URL parameters:', error);
          sessionStorage.removeItem('pendingUrlParams');
        }
      }
      
      // Clean up payment success URL if present
      if (window.location.pathname.includes('/payment-success')) {
        console.log('🧹 Cleaning up payment success URL after login...');
        window.history.replaceState({}, document.title, '/');
      }
      
      // Update overlay with user's name and close dialog
      const firstName = user.first_name || 'Member';
      setWelcomeOverlay({ name: firstName, fading: false });
      setIsLoginOpen(false);
      
      // Fade out and remove overlay
      setTimeout(() => setWelcomeOverlay(prev => prev ? { ...prev, fading: true } : null), 1800);
      setTimeout(() => setWelcomeOverlay(null), 2400);
      
      // Refresh all data after successful login - pass the user directly since state may not be updated yet
      refreshAllData(completeUser || user).catch(err => console.error('Background data refresh failed:', err));
      
    } catch (error) {
      console.error('Login failed:', error);
      setWelcomeOverlay(null); // Hide overlay on failure
      const errorMessage = error.response?.data?.detail || 'Login failed';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Function to refresh all data
  const refreshAllData = async (userOverride) => {
    try {
      // Use the passed user object (which has the latest role) or fall back to currentUser state
      await loadInitialTabCounts(userOverride || currentUser);
      console.log('All data refreshed successfully after login');
    } catch (error) {
      console.error('Failed to refresh some data after login:', error);
    }
  };

  // Membership renewal functions
  const renewMembership = async () => {
    try {
      console.log('🔄 Starting membership renewal...');
      const response = await axios.post('/membership/renew/create-session');
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
      
    } catch (error) {
      console.error('Membership renewal failed:', error);
      const errorMessage = error.response?.data?.detail || 'Membership renewal failed';
      toast.error(errorMessage);
    }
  };

  const fetchMembershipStatus = async () => {
    try {
      const response = await axios.get('/membership/status');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch membership status:', error);
      return null;
    }
  };

  const register = async (userData) => {
    try {
      console.log('🔄 Starting user registration...', { email: userData.email, hasPaymentSession: !!userData.payment_session_id });
      const response = await axios.post('auth/register', userData);
      console.log('✅ Registration API response:', response.data);
      
      // For membership registration with payment session, keep dialog open until payment completes
      if (userData.payment_session_id) {
        console.log('📝 Membership registration: Pending registration created, payment required');
        // Don't close the dialog - let the payment flow handle it
        return response.data;
      }
      
      // For direct registration without payment (admin-created users), close dialog and show success
      toast.success('Registration successful! Please check your email to verify your account.');
      setIsRegisterOpen(false);
      
      // For demo purposes, auto-verify the user
      if (response.data.verification_token) {
        console.log('Auto-verifying user email...');
        await axios.post(`auth/verify-email?token=${response.data.verification_token}`);
        toast.success('Email verified successfully! You can now log in.');
      }
      
      return response.data;
      
    } catch (error) {
      console.error('❌ Registration error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      const errorMessage = error.response?.data?.detail || 'Registration failed';
      toast.error(errorMessage);
      throw error;
    }
  };

  const logout = () => {
    setAuthToken(null);
    setCurrentUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('authToken');
    delete axios.defaults.headers.common['Authorization'];
    toast.success('Logged out successfully');
  };

  // Idle timeout: auto-logout after 1 hour of inactivity
  useEffect(() => {
    if (!isAuthenticated) return;

    const IDLE_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour
    let timeoutId;

    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        logout();
        toast.info('You have been logged out due to inactivity');
      }, IDLE_TIMEOUT_MS);
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(ev => window.addEventListener(ev, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      clearTimeout(timeoutId);
      events.forEach(ev => window.removeEventListener(ev, resetTimer));
    };
  }, [isAuthenticated]); // logout is stable (no deps)

  const promoteUser = async (userId, role) => {
    try {
      await axios.post(`users/${userId}/promote`, { user_id: userId, role });
      toast.success(`User promoted to ${role} successfully`);
      fetchUsers(searchTerm); // Refresh users list
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to promote user';
      toast.error(errorMessage);
      throw error;
    }
  };

  // User management functions for admin
  // Helper function to convert DD/MM/YYYY to YYYY-MM-DD for date input
  const convertDateForInput = (dateStr) => {
    if (!dateStr) return '';
    
    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    
    // Convert DD/MM/YYYY to YYYY-MM-DD
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
      const [day, month, year] = dateStr.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    return dateStr;
  };

  // Helper function to convert YYYY-MM-DD to DD/MM/YYYY for storage
  const convertDateForStorage = (dateStr) => {
    if (!dateStr) return '';
    
    // If already in DD/MM/YYYY format, return as is
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
      return dateStr;
    }
    
    // Convert YYYY-MM-DD to DD/MM/YYYY
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-');
      return `${day}/${month}/${year}`;
    }
    
    return dateStr;
  };

  const handleEditUser = (user) => {
    setEditingItem(user);
    setUserForm({
      first_name_1: user.first_name_1 || user.first_name || "", // Handle both old and new field names
      first_name_2: user.first_name_2 || "",
      last_name: user.last_name,
      email: user.email,
      mobile: user.mobile || "",
      landline: user.landline || "",
      last_year_member: user.last_year_member,
      member_category: user.member_category,
      birth_date: convertDateForInput(user.birth_date), // Convert to YYYY-MM-DD format
      birth_place: user.birth_place,
      nationality: user.nationality,
      other_nationality: user.other_nationality || "",
      address: user.address,
      postal_code: user.postal_code,
      city: user.city,
      province: user.province || "",
      country: user.country || "Italy",
      fiscal_code: user.fiscal_code
    });
    setCurrentTab('users'); // Ensure we're on users tab
    setIsDialogOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`/users/${userId}`);
      toast.success("User deleted successfully");
      fetchUsers(searchTerm); // Refresh users list
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete user';
      toast.error(errorMessage);
    }
  };

  const handlePromoteUser = async (userId) => {
    try {
      await promoteUser(userId, 'Administrator');
    } catch (error) {
      // Error already handled in promoteUser function
    }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    try {
      // Prepare user data with converted birth_date
      const userData = {
        ...userForm,
        birth_date: convertDateForStorage(userForm.birth_date) // Convert back to DD/MM/YYYY
      };
      
      if (editingItem) {
        await axios.put(`/users/${editingItem.id}`, userData);
        toast.success("User updated successfully!");
      } else {
        // Generate a strong random temporary password (admin-created users).
        // The admin sees it once in the success toast and must communicate it to the member;
        // the member should change it on first login.
        const randomBytes = crypto.getRandomValues(new Uint8Array(9));
        const tempPassword = "Tmp-" + btoa(String.fromCharCode(...randomBytes))
          .replace(/[+/=]/g, "")
          .slice(0, 10) + "!" + Math.floor(Math.random() * 100);
        await axios.post('users', {
          ...userData,
          password: tempPassword
        });
        toast.success(`User added — temp password: ${tempPassword} (share with the member, ask them to change it on first login)`, { duration: 20000 });
      }
      
      await fetchUsers(searchTerm);
      closeDialog();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to save user';
      toast.error(errorMessage);
      console.error(error);
    }
  };

  // Wrapper functions for UsersTabContent component
  const handleEditUserForComponent = async (user, updatedData) => {
    try {
      // Convert birth_date format if provided
      const userData = {
        ...updatedData,
        birth_date: updatedData.birth_date ? convertDateForStorage(updatedData.birth_date) : user.birth_date
      };
      
      await axios.put(`/users/${user.id}`, userData);
      toast.success("User updated successfully!");
      await fetchUsers(searchTerm);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update user';
      toast.error(errorMessage);
      throw error;
    }
  };

  const handleDeleteUserForComponent = async (userId) => {
    try {
      await axios.delete(`/users/${userId}`);
      toast.success("User deleted successfully");
      fetchUsers(searchTerm);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete user';
      toast.error(errorMessage);
      throw error;
    }
  };

  const handlePromoteUserForComponent = async (userId, role) => {
    try {
      await axios.post(`users/${userId}/promote`, { user_id: userId, role });
      toast.success(`User promoted to ${role} successfully`);
      fetchUsers(searchTerm);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to promote user';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Handle cover images fix
  const handleFixCoverImages = async () => {
    if (!currentUser || currentUser.role !== 'Administrator') {
      toast.error('Only administrators can fix cover images');
      return;
    }

    setIsFixingImages(true);
    
    try {
      console.log('🔧 Starting cover images migration...');
      toast.info('Starting cover images migration. This may take a few minutes...');

      const response = await axios.post('/admin/migrate-book-covers');
      
      if (response.status === 200) {
        const result = response.data;
        console.log('✅ Migration completed:', result);
        
        toast.success(
          `Cover images fixed! Processed: ${result.total_processed}, Success: ${result.successful}, Failed: ${result.failed}`,
          { duration: 8000 }
        );
        
        // Refresh the books and DVDs to show updated covers
        await fetchBooks();
        await fetchDvds();
        
        // Show detailed results if there were any failures
        if (result.failed > 0) {
          toast.warning(`${result.failed} covers could not be migrated. Check console for details.`);
          console.warn('Failed migrations:', result.results.filter(r => r.status === 'failed'));
        }
      }
    } catch (error) {
      console.error('❌ Cover images migration failed:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to fix cover images';
      toast.error(`Migration failed: ${errorMessage}`);
    } finally {
      setIsFixingImages(false);
    }
  };

  const handleBookSubmit = async (e) => {
    e.preventDefault();
    try {
      let bookResponse;
      
      if (editingItem) {
        // For updates, we'll update book data first, then handle S3 upload separately
        const formData = new FormData();
        
        // Add all non-empty fields to FormData (without cover_image for S3 workflow)
        if (bookForm.book_id) formData.append('book_update_id', bookForm.book_id);
        if (bookForm.author) formData.append('author', bookForm.author);
        if (bookForm.book_title) formData.append('book_title', bookForm.book_title);
        if (bookForm.language) formData.append('language', bookForm.language);
        if (bookForm.comment) formData.append('comment', bookForm.comment);
        if (bookForm.more_info) formData.append('more_info', bookForm.more_info);
        if (bookForm.purchasing_year) formData.append('purchasing_year', bookForm.purchasing_year);
        if (bookForm.place) formData.append('place', bookForm.place);
        if (bookForm.category && bookForm.category !== "none") formData.append('category', bookForm.category);
        
        // Update book without image first
        bookResponse = await axios.put(`/books/${editingItem.id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        // Handle S3 cover image upload separately if selected
        if (selectedCoverImage) {
          try {
            await uploadBookCoverToS3(editingItem.id, selectedCoverImage);
            toast.success("Book updated successfully with new cover uploaded to cloud!");
          } catch (s3Error) {
            console.error('S3 upload failed:', s3Error);
            toast.success("Book updated successfully, but cover upload failed. Please try uploading the cover again.");
          }
        } else {
          toast.success("Book updated successfully!");
        }
      } else {
        // For creation, create book first without image, then upload to S3
        const formData = new FormData();
        
        // Add all book form fields (without cover_image for S3 workflow)
        formData.append('book_id', bookForm.book_id);
        formData.append('author', bookForm.author);
        formData.append('book_title', bookForm.book_title);
        if (bookForm.language) formData.append('language', bookForm.language);
        if (bookForm.comment) formData.append('comment', bookForm.comment);
        if (bookForm.more_info) formData.append('more_info', bookForm.more_info);
        if (bookForm.purchasing_year) formData.append('purchasing_year', bookForm.purchasing_year);
        if (bookForm.place) formData.append('place', bookForm.place);
        if (bookForm.category && bookForm.category !== "none") formData.append('category', bookForm.category);
        
        // Create book first
        bookResponse = await axios.post('books', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        // Handle S3 cover image upload separately if selected
        if (selectedCoverImage && bookResponse.data.id) {
          try {
            await uploadBookCoverToS3(bookResponse.data.id, selectedCoverImage);
            toast.success("Book created successfully with cover uploaded to cloud!");
          } catch (s3Error) {
            console.error('S3 upload failed:', s3Error);
            toast.success("Book created successfully, but cover upload failed. Please try uploading the cover again.");
          }
        } else {
          toast.success("Book added successfully!");
        }
      }
      
      await fetchBooks(searchTerm, selectedCategory, selectedLanguage);
      closeDialog();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to save book';
      toast.error(errorMessage);
      console.error(error);
    }
  };

  const handleCdSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`/cds/${editingItem.id}`, cdForm);
        toast.success("CD updated successfully!");
      } else {
        await axios.post('cds', cdForm);
        toast.success("CD added successfully!");
      }
      
      await fetchCds(searchTerm);
      closeDialog();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to save CD';
      toast.error(errorMessage);
      console.error(error);
    }
  };

  const handleDvdSubmit = async (e) => {
    e.preventDefault();
    try {
      let dvdResponse;
      
      if (editingItem) {
        // For updates, use JSON format like the original
        const updateData = {
          dvd_id: dvdForm.dvd_id,
          title: dvdForm.title,
          languages: dvdForm.languages || "",
          actors: dvdForm.actors || "",
          director: dvdForm.director || "",
          production_year: dvdForm.production_year || "",
          purchasing_year: dvdForm.purchasing_year || "",
          category: dvdForm.category || "",
          area: dvdForm.area || ""
        };
        
        // Update DVD with JSON data
        dvdResponse = await axios.put(`/dvds/${editingItem.id}`, updateData);
        
        // Handle S3 cover image upload separately if selected
        if (selectedDvdCoverImage) {
          try {
            await uploadDvdCoverToS3(editingItem.id, selectedDvdCoverImage);
            toast.success("DVD updated successfully with new cover uploaded to cloud!");
          } catch (s3Error) {
            console.error('S3 upload failed:', s3Error);
            toast.success("DVD updated successfully, but cover upload failed. Please try uploading the cover again.");
          }
        } else {
          toast.success("DVD updated successfully!");
        }
      } else {
        // For creation, use JSON format like the original  
        const createData = {
          dvd_id: dvdForm.dvd_id,
          title: dvdForm.title,
          languages: dvdForm.languages || "",
          actors: dvdForm.actors || "",
          director: dvdForm.director || "",
          production_year: dvdForm.production_year || "",
          purchasing_year: dvdForm.purchasing_year || "",
          category: dvdForm.category || "",
          area: dvdForm.area || ""
        };
        
        // Create DVD first
        dvdResponse = await axios.post('dvds', createData);
        
        // Handle S3 cover image upload separately if selected
        if (selectedDvdCoverImage && dvdResponse.data.id) {
          try {
            await uploadDvdCoverToS3(dvdResponse.data.id, selectedDvdCoverImage);
            toast.success("DVD created successfully with cover uploaded to cloud!");
          } catch (s3Error) {
            console.error('S3 upload failed:', s3Error);
            toast.success("DVD created successfully, but cover upload failed. Please try uploading the cover again.");
          }
        } else {
          toast.success("DVD added successfully!");
        }
      }
      
      await fetchDvds(searchTerm);
      closeDialog();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to save DVD';
      toast.error(errorMessage);
      console.error(error);
    }
  };

  // Handle export books to Excel (admin only)
  const handleExportBooks = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error('Please sign in again to export books');
        return;
      }

      // Verify admin access
      try {
        const testResponse = await axios.get('auth/me', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (testResponse.data.role !== 'Administrator') {
          toast.error('Admin privileges required for books export');
          return;
        }
      } catch (authError) {
        toast.error('Authentication failed. Please sign in again as administrator.');
        return;
      }

      const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:]/g, '').replace('T', '_');
      const exportUrl = `${axios.defaults.baseURL}/export/books-page?auth_token=${encodeURIComponent(token)}&t=${Date.now()}`;
      
      const exportWindow = window.open(exportUrl, '_blank', 'width=800,height=600');
      
      if (exportWindow) {
        toast.success(`📚 Opening Books export page! Expected filename: ACE_Books_Export_${timestamp}.xlsx`, {
          duration: 8000
        });
      } else {
        toast.error('Popup blocked! Please allow popups and try again.', {
          duration: 8000
        });
      }
      
    } catch (error) {
      console.error('Books export error:', error);
      toast.error('Failed to export books. Please try again.');
    }
  };

  // Handle export CDs to Excel (admin only)
  const handleExportCDs = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error('Please sign in again to export CDs');
        return;
      }

      // Verify admin access
      try {
        const testResponse = await axios.get('auth/me', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (testResponse.data.role !== 'Administrator') {
          toast.error('Admin privileges required for CDs export');
          return;
        }
      } catch (authError) {
        toast.error('Authentication failed. Please sign in again as administrator.');
        return;
      }

      const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:]/g, '').replace('T', '_');
      const exportUrl = `${axios.defaults.baseURL}/export/cds-page?auth_token=${encodeURIComponent(token)}&t=${Date.now()}`;
      
      const exportWindow = window.open(exportUrl, '_blank', 'width=800,height=600');
      
      if (exportWindow) {
        toast.success(`💿 Opening CDs export page! Expected filename: ACE_CDs_Export_${timestamp}.xlsx`, {
          duration: 8000
        });
      } else {
        toast.error('Popup blocked! Please allow popups and try again.', {
          duration: 8000
        });
      }
      
    } catch (error) {
      console.error('CDs export error:', error);
      toast.error('Failed to export CDs. Please try again.');
    }
  };

  // Handle export DVDs to Excel (admin only)
  const handleExportDVDs = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error('Please sign in again to export DVDs');
        return;
      }

      // Verify admin access
      try {
        const testResponse = await axios.get('auth/me', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (testResponse.data.role !== 'Administrator') {
          toast.error('Admin privileges required for DVDs export');
          return;
        }
      } catch (authError) {
        toast.error('Authentication failed. Please sign in again as administrator.');
        return;
      }

      const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:]/g, '').replace('T', '_');
      const exportUrl = `${axios.defaults.baseURL}/export/dvds-page?auth_token=${encodeURIComponent(token)}&t=${Date.now()}`;
      
      const exportWindow = window.open(exportUrl, '_blank', 'width=800,height=600');
      
      if (exportWindow) {
        toast.success(`📀 Opening DVDs export page! Expected filename: ACE_DVDs_Export_${timestamp}.xlsx`, {
          duration: 8000
        });
      } else {
        toast.error('Popup blocked! Please allow popups and try again.', {
          duration: 8000
        });
      }
      
    } catch (error) {
      console.error('DVDs export error:', error);
      toast.error('Failed to export DVDs. Please try again.');
    }
  };

  const handleLoanSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        // Loan editing not typically supported - loans are usually returned instead
        toast.info("Loans cannot be edited. Use return functionality instead.");
      } else {
        await axios.post('loans', loanForm);
        toast.success("Loan created successfully!");
      }
      
      await fetchLoans();
      fetchLoanStatuses();
      closeDialog();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to create loan';
      toast.error(errorMessage);
      console.error(error);
    }
  };

  // Handle export users to Excel (admin only) - Simple direct approach
  const handleExportUsers = async () => {
    try {
      // Get the auth token
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error('Please sign in again to export users');
        return;
      }

      // First verify we have admin access
      try {
        const testResponse = await axios.get('auth/me', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (testResponse.data.role !== 'Administrator') {
          toast.error('Admin privileges required for user export');
          return;
        }
      } catch (authError) {
        toast.error('Authentication failed. Please sign in again as administrator.');
        return;
      }

      // Generate timestamped filename for display
      const now = new Date();
      const timestamp = now.getFullYear().toString() + 
                       (now.getMonth() + 1).toString().padStart(2, '0') + 
                       now.getDate().toString().padStart(2, '0') + '_' +
                       now.getHours().toString().padStart(2, '0') + 
                       now.getMinutes().toString().padStart(2, '0') + 
                       now.getSeconds().toString().padStart(2, '0');

      // Create the direct download URL (we know this endpoint works)
      const downloadUrl = `${axios.defaults.baseURL}/export/users-file?auth_token=${encodeURIComponent(token)}`;
      
      // Open in new window - let the browser handle the download naturally
      const downloadWindow = window.open(downloadUrl, '_blank', 'width=800,height=600');
      
      if (downloadWindow) {
        toast.success(`📊 Excel export opened in new window! Expected filename: ACE_Users_Export_${timestamp}.xlsx`, {
          duration: 8000
        });
        toast.info('💡 In the new window: If file doesn\'t download automatically, right-click and choose "Save As" or use Cmd+S to save', {
          duration: 10000
        });
      } else {
        toast.error('Popup blocked! Please allow popups and try again.', {
          duration: 8000,
          action: {
            label: 'Open Download',
            onClick: () => window.open(downloadUrl, '_blank')
          }
        });
      }
      
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export users. Please try again.');
    }
  };

  const handleExportLoanHistory = async (filters = {}) => {
    const { startDate, endDate, status, userId } = filters;
    
    try {
      // Get auth token
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error('Please sign in again to export loan history');
        return;
      }

      toast.info('Starting loan history export...');

      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (status) params.append('status', status);
      if (userId) params.append('user_id', userId);
      params.append('auth_token', token);
      
      const queryString = params.toString();
      const downloadUrl = `${axios.defaults.baseURL}/export/loan-history?${queryString}`;
      
      // Create a hidden iframe to trigger download
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = downloadUrl;
      document.body.appendChild(iframe);
      
      // Remove iframe after download starts
      setTimeout(() => {
        try {
          document.body.removeChild(iframe);
        } catch (e) {
          // Ignore if already removed
        }
      }, 3000);

      toast.success('Loan history export started! Check your downloads folder.');
      
    } catch (error) {
      console.error('Loan history export error:', error);
      toast.error('Failed to export loan history. Please try again.');
    }
  };

  const handleReturnLoan = async (loanId) => {
    try {
      await axios.put(`/loans/${loanId}/return`);
      toast.success("Loan returned successfully!");
      await fetchLoans();
      // Refresh data to update loan statuses
      await fetchBooks(searchTerm, selectedCategory, selectedLanguage);
      await fetchCds(searchTerm);  
      await fetchDvds(searchTerm);
      fetchLoanStatuses();
    } catch (error) {
      console.error("Return loan error:", error);
      
      // Handle different types of error responses
      let errorMessage = 'Failed to return loan';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail.map(err => err.msg || err).join(', ');
          } else {
            errorMessage = JSON.stringify(errorData.detail);
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else {
          errorMessage = `Error: ${error.response.status} ${error.response.statusText}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleClearLoanHistory = async () => {
    try {
      const response = await axios.delete('/loans/history');
      const { deleted_count } = response.data;
      toast.success(`Cleared ${deleted_count} loan records from history`);
      await fetchLoans();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to clear loan history';
      toast.error(errorMessage);
      console.error(error);
    }
  };

  const handleBulkUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(csv|xlsx|xls)$/i)) {
      toast.error('Please select a CSV or Excel file');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const endpoint = currentTab === "books" ? "/books/bulk-upload" : 
                     currentTab === "cds" ? "/cds/bulk-upload" : 
                     "/dvds/bulk-upload";
      
      const response = await axios.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const result = response.data;
      toast.success(`Bulk upload completed! Created: ${result.created_count}, Errors: ${result.error_count}`);
      
      // Refresh the current tab data
      if (currentTab === "books") {
        await fetchBooks(searchTerm, selectedCategory, selectedLanguage);
      } else if (currentTab === "cds") {
        await fetchCds(searchTerm, selectedCategory);
      } else if (currentTab === "dvds") {
        await fetchDvds(searchTerm, selectedDvdCategory);
      }

      // Display errors if any
      if (result.errors && result.errors.length > 0) {
        console.log("Upload errors:", result.errors);
        toast.info(`Some items had errors. Check console for details.`);
      }

    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to upload file';
      toast.error(errorMessage);
      console.error(error);
    }

    // Clear the file input
    event.target.value = '';
  };

  // Cultural Events handlers
  // Dashboard refresh function
  const [dashboardRefreshTrigger, setDashboardRefreshTrigger] = useState(0);
  
  const triggerDashboardRefresh = () => {
    setDashboardRefreshTrigger(prev => prev + 1);
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    try {
      // Initialize price_categories if undefined
      const priceCategories = eventForm.price_categories || {
        member_price: "",
        minor_price: "",
        senior_price: "",
        young_adult_price: "",
        member_price_enabled: true,
        minor_price_enabled: false,
        senior_price_enabled: false,
        young_adult_price_enabled: false
      };

      // Process price categories - convert empty strings to null and ensure proper formatting
      const processedPriceCategories = {
        member_price: priceCategories.member_price ? parseFloat(priceCategories.member_price) : null,
        minor_price: priceCategories.minor_price ? parseFloat(priceCategories.minor_price) : null,
        senior_price: priceCategories.senior_price ? parseFloat(priceCategories.senior_price) : null,
        young_adult_price: priceCategories.young_adult_price ? parseFloat(priceCategories.young_adult_price) : null,
        member_price_enabled: priceCategories.member_price_enabled || false,
        minor_price_enabled: priceCategories.minor_price_enabled || false,
        senior_price_enabled: priceCategories.senior_price_enabled || false,
        young_adult_price_enabled: priceCategories.young_adult_price_enabled || false
      };

      // Process seat types - only include enabled ones with proper data
      const processedSeatTypes = (eventForm.seat_types || [])
        .filter(seatType => seatType.enabled)
        .map(seatType => ({
          id: seatType.id,
          name: seatType.name || seatType.id,
          enabled: true,
          price: parseFloat(seatType.price) || 0.0
        }));

      // Process matrix pricing if using Opera-style pricing
      let processedMatrixPricing = null;
      if (eventForm.pricing_type === "matrix" && eventForm.matrix_pricing) {
        processedMatrixPricing = {
          member_pricing: {
            galleria_price: eventForm.matrix_pricing.member_pricing?.galleria_price ? parseFloat(eventForm.matrix_pricing.member_pricing.galleria_price) : null,
            platea_price: eventForm.matrix_pricing.member_pricing?.platea_price ? parseFloat(eventForm.matrix_pricing.member_pricing.platea_price) : null
          },
          minor_pricing: {
            galleria_price: eventForm.matrix_pricing.minor_pricing?.galleria_price ? parseFloat(eventForm.matrix_pricing.minor_pricing.galleria_price) : null,
            platea_price: eventForm.matrix_pricing.minor_pricing?.platea_price ? parseFloat(eventForm.matrix_pricing.minor_pricing.platea_price) : null
          },
          senior_pricing: {
            galleria_price: eventForm.matrix_pricing.senior_pricing?.galleria_price ? parseFloat(eventForm.matrix_pricing.senior_pricing.galleria_price) : null,
            platea_price: eventForm.matrix_pricing.senior_pricing?.platea_price ? parseFloat(eventForm.matrix_pricing.senior_pricing.platea_price) : null
          },
          young_adult_pricing: {
            galleria_price: eventForm.matrix_pricing.young_adult_pricing?.galleria_price ? parseFloat(eventForm.matrix_pricing.young_adult_pricing.galleria_price) : null,
            platea_price: eventForm.matrix_pricing.young_adult_pricing?.platea_price ? parseFloat(eventForm.matrix_pricing.young_adult_pricing.platea_price) : null
          },
          member_pricing_enabled: eventForm.matrix_pricing.member_pricing_enabled || false,
          minor_pricing_enabled: eventForm.matrix_pricing.minor_pricing_enabled || false,
          senior_pricing_enabled: eventForm.matrix_pricing.senior_pricing_enabled || false,
          young_adult_pricing_enabled: eventForm.matrix_pricing.young_adult_pricing_enabled || false,
          galleria_enabled: eventForm.matrix_pricing.galleria_enabled || false,
          platea_enabled: eventForm.matrix_pricing.platea_enabled || false
        };
      }

      // Process custom rows if using the new pricing model. Validation is
      // enforced server-side; here we just coerce amounts to floats.
      let processedCustomRows = null;
      if (eventForm.pricing_type === "custom_rows") {
        processedCustomRows = (eventForm.custom_price_rows || [])
          .filter(r => (r.label || "").trim() !== "")
          .map(r => ({
            id: r.id || undefined,
            label: (r.label || "").trim(),
            amount: parseFloat(r.amount) || 0,
            capacity: r.capacity === "" || r.capacity == null ? null : parseInt(r.capacity, 10),
            minors_only: !!r.minors_only,
          }));
      }

      const eventData = {
        ...eventForm,
        event_date: eventForm.event_date ? new Date(eventForm.event_date).toISOString() : null,
        booking_deadline: eventForm.booking_deadline ? new Date(eventForm.booking_deadline).toISOString() : null,
        max_capacity: eventForm.max_capacity ? parseInt(eventForm.max_capacity) : null,
        guest_surcharge: eventForm.guest_surcharge ? parseFloat(eventForm.guest_surcharge) : 0,
        category_tags: Array.isArray(eventForm.category_tags) ? eventForm.category_tags : 
                      eventForm.category_tags ? eventForm.category_tags.split(',').map(tag => tag.trim()) : [],
        price_categories: (eventForm.pricing_type === "standard" || eventForm.pricing_type === "legacy") ? processedPriceCategories : null,
        matrix_pricing: processedMatrixPricing,
        seat_types: (eventForm.pricing_type === "standard" || eventForm.pricing_type === "legacy") ? processedSeatTypes : [],
        // Send pricing_type so the backend can tag the event as custom_rows;
        // include custom_price_rows only for the new model.
        pricing_type: eventForm.pricing_type === "custom_rows" ? "custom_rows" : null,
        custom_price_rows: processedCustomRows || [],
      };

      // Remove frontend-only fields from the data sent to the backend
      const { imageFile, ...eventDataToSend } = eventData;

      let response;
      if (editingItem) {
        response = await axios.put(`/events/${editingItem.id}`, eventDataToSend);
        toast.success("Event updated successfully!");
      } else {
        response = await axios.post('events', eventDataToSend);
        toast.success("Event created successfully!");
      }

      // Handle image upload if a file was selected
      if (imageFile && response.data.id) {
        try {
          await handleEventImageUpload(response.data.id, imageFile);
        } catch (imageError) {
          console.error("Image upload failed:", imageError);
          // Don't fail the entire event creation if image upload fails
        }
      }
      
      await fetchEvents(searchTerm, selectedCategory);
      
      // Trigger dashboard refresh for admin users
      if (isAdmin(effectiveUser)) {
        triggerDashboardRefresh();
        
        // Show success message with dashboard info for new events
        if (!editingItem) {
          toast.success(
            <div>
              <p><strong>Event created successfully!</strong></p>
              <p>Check the Admin Dashboard to manage finances and add expenses.</p>
              <button 
                onClick={() => {
                  setShowDashboard(true);
                  toast.dismiss();
                }}
                className="mt-2 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
              >
                Go to Dashboard
              </button>
            </div>, {
            duration: 8000
          });
        }
      }
      
      closeDialog();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to save event';
      toast.error(errorMessage);
      console.error(error);
    }
  };

  const handleOrganiserSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`organisers/${editingItem.id}`, organiserForm);
        toast.success("Organiser updated successfully!");
      } else {
        await axios.post('organisers', organiserForm);
        toast.success("Organiser created successfully!");
      }
      
      await fetchOrganisers();
      closeDialog();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to save organiser';
      toast.error(errorMessage);
      console.error(error);
    }
  };

  const handleVenueSubmit = async (e) => {
    e.preventDefault();
    try {
      const venueData = {
        ...venueForm,
        capacity: venueForm.capacity ? parseInt(venueForm.capacity) : null,
        facilities: Array.isArray(venueForm.facilities) ? venueForm.facilities : 
                   venueForm.facilities ? venueForm.facilities.split(',').map(f => f.trim()) : []
      };

      if (editingItem) {
        await axios.put(`venues/${editingItem.id}`, venueData);
        toast.success("Venue updated successfully!");
      } else {
        await axios.post('venues', venueData);
        toast.success("Venue created successfully!");
      }
      
      await fetchVenues();
      closeDialog();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to save venue';
      toast.error(errorMessage);
      console.error(error);
    }
  };

  // Handle delete venue
  const handleDeleteVenue = async (venue) => {
    if (!window.confirm(`Are you sure you want to delete venue "${venue.name}"?`)) return;
    
    try {
      await axios.delete(`/venues/${venue.id}`);
      toast.success("Venue deleted successfully!");
      await fetchVenues();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete venue';
      toast.error(errorMessage);
    }
  };

  // Handle delete organiser
  const handleDeleteOrganiser = async (organiser) => {
    if (!window.confirm(`Are you sure you want to delete organiser "${organiser.name}"?`)) return;
    
    try {
      await axios.delete(`/organisers/${organiser.id}`);
      toast.success("Organiser deleted successfully!");
      await fetchOrganisers();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete organiser';
      toast.error(errorMessage);
    }
  };

  // Handle edit venue
  const handleEditVenue = (venue) => {
    setEditingItem(venue);
    setVenueForm({
      name: venue.name,
      address: venue.address || "",
      city: venue.city || "",
      capacity: venue.capacity || "",
      contact_info: venue.contact_info || "",
      facilities: venue.facilities || []
    });
    setCurrentTab("venue");
    setIsDialogOpen(true);
    setIsVenuesManagementOpen(false);
  };

  // Handle edit organiser
  const handleEditOrganiser = (organiser) => {
    setEditingItem(organiser);
    setOrganiserForm({
      name: organiser.name,
      email: organiser.email || "",
      phone: organiser.phone || "",
      bio: organiser.bio || "",
      website: organiser.website || ""
    });
    setCurrentTab("organiser");
    setIsDialogOpen(true);
    setIsOrganisersManagementOpen(false);
  };

  const handleViewEvent = (event) => {
    setViewingEvent(event);
    setIsEventDetailOpen(true);
  };

  const handleCloseEventDetail = () => {
    setViewingEvent(null);
    setIsEventDetailOpen(false);
  };

  // S3 Upload helper function for event images
  const uploadEventImageToS3 = async (eventId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`/events/${eventId}/image-s3`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('Event image uploaded to S3:', response.data);
      return response.data;
    } catch (error) {
      console.error('S3 event image upload failed:', error);
      throw error;
    }
  };

  const handleEventImageUpload = async (eventId, file) => {
    try {
      // Use S3 upload instead of local upload
      const response = await uploadEventImageToS3(eventId, file);
      toast.success("Image uploaded to cloud successfully!");
      return response.s3_key;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to upload image to cloud';
      toast.error(errorMessage);
      throw error;
    }
  };

  // ============= BOOKING SYSTEM FUNCTIONS =============

  // Fetch default seat types for events
  const fetchDefaultSeatTypes = async () => {
    try {
      const response = await axios.get('events/seat-types');
      setDefaultSeatTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch default seat types:', error);
      // Set default values if API fails
      setDefaultSeatTypes([
        { id: "galleria", name: "Galleria", enabled: false, price: 0.0 },
        { id: "platea", name: "Platea", enabled: false, price: 0.0 },
        { id: "first_level", name: "First Level", enabled: false, price: 0.0 },
        { id: "second_level", name: "Second Level", enabled: false, price: 0.0 },
        { id: "vip", name: "VIP Section", enabled: false, price: 0.0 }
      ]);
    }
  };

  // Calculate pricing for sophisticated booking system
  const calculateEventPricing = async (eventId, tickets, bookingForUserId = null) => {
    try {
      console.log('Making API request to calculate pricing...', { eventId, tickets, bookingForUserId });
      const payload = { 
        ...tickets,
        booking_for_user_id: bookingForUserId 
      };
      const response = await axios.post(`/events/${eventId}/calculate-price`, payload);
      console.log('Pricing calculation response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to calculate pricing:', error);
      console.error('Request details:', { 
        url: `/events/${eventId}/calculate-price`, 
        tickets,
        bookingForUserId,
        baseURL: axios.defaults.baseURL 
      });
      throw error;
    }
  };

  // Fetch user's booking history
  const fetchUserBookings = async () => {
    try {
      const response = await axios.get('bookings/my-bookings');
      setUserBookings(response.data || []);
    } catch (error) {
      console.error("Failed to fetch user bookings:", error);
      setUserBookings([]);
    }
  };

  // Fetch user loans (similar to user bookings)
  const fetchUserLoans = async () => {
    try {
      if (!currentUser) return;
      const response = await axios.get(`/loans/user/${currentUser.id}?include_returned=true`);
      setUserLoans(response.data || []);
    } catch (error) {
      console.error("Failed to fetch user loans:", error);
      setUserLoans([]);
    }
  };

  const [isEventBookingsLoading, setIsEventBookingsLoading] = useState(false);
  const [isCapacityChartOpen, setIsCapacityChartOpen] = useState(false);
  const [isDocumentRepositoryOpen, setIsDocumentRepositoryOpen] = useState(false);
  const [isSeasonPassAdminOpen, setIsSeasonPassAdminOpen] = useState(false);
  const [isAdminSettingsOpen, setIsAdminSettingsOpen] = useState(false);

  // Deep-link: ?open=documents (or #documents) auto-opens the Document Repository
  // dialog once the user is signed in. Useful for linking from external sites.
  useEffect(() => {
    if (!currentUser) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const wantsDocs =
        params.get('open') === 'documents' ||
        (window.location.hash || '').toLowerCase() === '#documents';
      if (wantsDocs) {
        setIsDocumentRepositoryOpen(true);
        // Clean the URL so a refresh doesn't keep re-opening it
        params.delete('open');
        const newSearch = params.toString();
        const newUrl =
          window.location.pathname +
          (newSearch ? `?${newSearch}` : '') +
          (window.location.hash === '#documents' ? '' : window.location.hash);
        window.history.replaceState({}, '', newUrl);
      }
    } catch (e) {
      // best-effort, ignore
    }
  }, [currentUser]);
  const [waitlistDialogEvent, setWaitlistDialogEvent] = useState(null);
  const [waitlistCounts, setWaitlistCounts] = useState({});
  const [manualBookingEvent, setManualBookingEvent] = useState(null);

  // Fetch bookings for specific event (admin only)
  const fetchEventBookings = async (eventId) => {
    setIsEventBookingsLoading(true);
    setEventBookings([]); // clear stale data so skeleton renders cleanly
    try {
      const response = await axios.get(`events/${eventId}/bookings`);
      setEventBookings(response.data || []);
    } catch (error) {
      console.error("Failed to fetch event bookings:", error);
      setEventBookings([]);
    } finally {
      setIsEventBookingsLoading(false);
    }
  };

  // Fetch booking statistics for event (admin only)
  const fetchBookingStats = async (eventId) => {
    try {
      const response = await axios.get(`events/${eventId}/booking-stats`);
      setBookingStats(response.data);
    } catch (error) {
      console.error("Failed to fetch booking stats:", error);
      setBookingStats(null);
    }
  };

  // Open booking dialog for specific event
  const handleBookEvent = (event) => {
    setSelectedEventForBooking(event);
    setBookingForm({
      event_id: event.id,
      full_price_tickets: 0,
      reduced_price_tickets: 0,
      guided_visit_required: false,
      guided_visit_language: "",
      total_amount: 0
    });
    setIsBookingDialogOpen(true);
  };

  // Custom-rows booking dialog state (new pricing model — replaces the
  // legacy sophisticated dialog for events with pricing_type='custom_rows').
  const [isCustomRowsBookingOpen, setIsCustomRowsBookingOpen] = useState(false);

  // Open the right booking dialog for a given event.
  const handleSophisticatedBookEvent = (event) => {
    setSelectedEventForBooking(event);
    // New custom_rows pricing → new dialog, no legacy form initialisation needed.
    if (event?.pricing_type === "custom_rows") {
      setIsCustomRowsBookingOpen(true);
      return;
    }
    setBookingForm({
      event_id: event.id,
      tickets: {
        current_user_tickets: 1,
        current_user_seat_section: null,  // "galleria" or "platea" for Opera venues
        current_user_seat_type: null,     // Legacy seat type for backward compatibility
        current_user_price: 0.0,
        minor_tickets: [],
        minor_seat_section: null,  // "galleria" or "platea" for Opera venues
        minor_seat_type: null,     // Legacy seat type for backward compatibility
        minor_total_price: 0.0,
        family_members: [],
        family_seat_section: null,  // "galleria" or "platea" for Opera venues
        family_seat_type: null,     // Legacy seat type for backward compatibility
        family_total_price: 0.0,
        guest_tickets: [],
        guest_seat_section: null,
        guest_total_price: 0.0
      },
      guided_visit_required: false,
      guided_visit_language: "",
      total_amount: 0,
      comment: ""
    });
    setIsSophisticatedBookingOpen(true);
  };

  // Calculate booking total based on ticket quantities
  const calculateBookingTotal = () => {
    if (!selectedEventForBooking || !bookingForm) return;
    
    const fullPrice = selectedEventForBooking.cost_euro_full || 0;
    const reducedPrice = selectedEventForBooking.cost_euro_reduced || 0;
    
    const fullAmount = fullPrice * bookingForm.full_price_tickets;
    const reducedAmount = reducedPrice * bookingForm.reduced_price_tickets;
    const total = fullAmount + reducedAmount;
    
    setBookingForm(prev => ({
      ...prev,
      total_amount: total
    }));
  };

  // Handle sophisticated booking form submission
  const handleSophisticatedBookingSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedEventForBooking) {
      toast.error("No event selected for booking");
      return;
    }

    const tickets = bookingForm.tickets;
    const totalTickets = tickets.current_user_tickets + 
                        tickets.minor_tickets.length + 
                        tickets.family_members.length;
    
    if (totalTickets <= 0) {
      toast.error("Please select at least one ticket");
      return;
    }

    // Validate family member details
    for (let i = 0; i < tickets.family_members.length; i++) {
      const member = tickets.family_members[i];
      if (!member.first_name.trim() || !member.last_name.trim() || !member.age) {
        toast.error(`Please complete all details for Family Member ${i + 1}`);
        return;
      }
    }

    try {
      // Use the sophisticated booking endpoint
      const bookingData = {
        event_id: selectedEventForBooking.id,
        tickets: tickets,
        guided_visit_required: bookingForm.guided_visit_required,
        guided_visit_language: bookingForm.guided_visit_language || null,
        payment_method: "stripe", // Default to Stripe for now
        notes: bookingForm.comment || null,
        booking_for_user_id: bookingForm.booking_for_user_id || null,  // Target user if booking for another member
        linked_member_tickets: bookingForm.linked_member_tickets || 0  // Number of tickets for linked member
      };

      const response = await axios.post('bookings/sophisticated', bookingData);
      
      if (response.data.checkout_url) {
        // Store booking info for success tracking
        localStorage.setItem('pending_booking_id', response.data.booking_id);
        localStorage.setItem('pending_session_id', response.data.session_id);
        
        // Store booking info for return handling
        sessionStorage.setItem('pendingBookingId', response.data.booking_id);
        sessionStorage.setItem('pendingSessionId', response.data.session_id);
        
        // Redirect to Stripe checkout in same window (not popup)
        window.location.href = response.data.checkout_url;
      } else {
        toast.success("Booking created successfully!");
        setIsSophisticatedBookingOpen(false);
        // Refresh user bookings and events data
        await fetchUserBookings();
        await fetchEvents();
      }
      
    } catch (error) {
      console.error('Sophisticated booking failed:', error);
      const detail = error.response?.data?.detail;

      // Handle structured 409 for an existing pending booking → offer to resume
      if (error.response?.status === 409 && typeof detail === 'object' && detail?.code === 'PENDING_BOOKING_EXISTS') {
        const message = detail.message || 'You already have a pending booking for this event.';
        if (detail.checkout_url) {
          toast.error(message + ' Redirecting you to complete payment…', { duration: 5000 });
          setTimeout(() => {
            window.location.href = detail.checkout_url;
          }, 1500);
        } else {
          toast.error(message);
        }
        return;
      }

      const errorMessage = typeof detail === 'string' ? detail : (detail?.message || 'Booking failed');
      toast.error(errorMessage);
    }
  };

  // Handle booking form submission and Stripe checkout
  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedEventForBooking) {
      toast.error("No event selected for booking");
      return;
    }

    const totalTickets = bookingForm.full_price_tickets + bookingForm.reduced_price_tickets;
    if (totalTickets <= 0) {
      toast.error("Please select at least one ticket");
      return;
    }

    try {
      if (editingBooking) {
        // Update existing booking
        console.log('Updating booking:', editingBooking.id);
        const updateData = {
          full_price_tickets: bookingForm.full_price_tickets,
          reduced_price_tickets: bookingForm.reduced_price_tickets,
          guided_visit_required: bookingForm.guided_visit_required,
          guided_visit_language: bookingForm.guided_visit_language || null
        };
        
        // Add admin comments if user is administrator
        if (isAdmin(effectiveUser)) {
          updateData.admin_comments = bookingForm.admin_comments || null;
        }
        
        await axios.put(`/bookings/${editingBooking.id}`, updateData);
        toast.success('Booking updated successfully');
        
        // Close dialog and refresh data
        setIsBookingDialogOpen(false);
        setEditingBooking(null);
        
        // Refresh booking data
        if (selectedEventForManagement) {
          await fetchEventBookings(selectedEventForManagement.id);
          await fetchBookingStats(selectedEventForManagement.id);
        }
        // Also refresh user bookings for main page display
        await fetchUserBookings();
        
        return; // Exit early for updates
      }

      // Create new booking - existing checkout logic
      const checkoutData = {
        event_id: selectedEventForBooking.id,
        full_price_tickets: bookingForm.full_price_tickets,
        reduced_price_tickets: bookingForm.reduced_price_tickets,
        guided_visit_required: bookingForm.guided_visit_required,
        guided_visit_language: bookingForm.guided_visit_language || null,
        success_url: `${window.location.origin}/booking-success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/booking-cancelled`
      };

      const response = await axios.post('bookings/checkout', checkoutData);
      
      if (response.data.checkout_url) {
        // Update success URL with booking_id
        const updatedSuccessUrl = `${window.location.origin}/booking-success?session_id={CHECKOUT_SESSION_ID}&booking_id=${response.data.booking_id}`;
        
        // For now we can't modify the Stripe session after creation, 
        // so we'll store the booking_id in localStorage as a fallback
        localStorage.setItem('pending_booking_id', response.data.booking_id);
        localStorage.setItem('pending_session_id', response.data.session_id);
        
        // Store booking info for return handling
        sessionStorage.setItem('pendingBookingId', response.data.booking_id);
        sessionStorage.setItem('pendingSessionId', response.data.session_id);
        
        // Redirect to Stripe checkout in same window (not popup)
        window.location.href = response.data.checkout_url;
      } else {
        toast.error("Failed to create checkout session");
      }
    } catch (error) {
      const isEdit = !!editingBooking;
      const errorMessage = error.response?.data?.detail || (isEdit ? 'Failed to update booking' : 'Failed to create booking');
      toast.error(errorMessage);
      console.error("Booking error:", error);
    }
  };

  // Close booking dialog
  const closeBookingDialog = () => {
    setIsBookingDialogOpen(false);
    setEditingBooking(null);
    setSelectedEventForBooking(null);
    setBookingForm({
      event_id: "",
      full_price_tickets: 0,
      reduced_price_tickets: 0,
      guided_visit_required: false,
      guided_visit_language: "",
      total_amount: 0,
      comment: ""
    });
  };

  // Handle edit booking
  // Event booking management
  const handleViewEventBookings = async (event) => {
    try {
      setSelectedEventForManagement(event);
      // Open the dialog FIRST so the loading skeleton is visible while data fetches
      setIsEventBookingsDialogOpen(true);

      // Fire fetches in parallel — fetchEventBookings flips isEventBookingsLoading synchronously
      fetchEventBookings(event.id);
      fetchBookingStats(event.id);
    } catch (error) {
      console.error('Failed to load event bookings:', error);
      toast.error('Failed to load event bookings');
    }
  };

  const handleEditBooking = (booking) => {
    console.log('Edit booking clicked:', booking);
    
    if (!booking || !booking.id) {
      console.error('Invalid booking data:', booking);
      toast.error('Invalid booking data');
      return;
    }

    // Find the event for this booking from selectedEventForManagement
    if (!selectedEventForManagement) {
      console.error('No event selected for management');
      toast.error('Event data not available');
      return;
    }

    // Clear any existing editing state first
    setEditingBooking(null);
    
    // Set the event for booking dialog
    setSelectedEventForBooking(selectedEventForManagement);
    
    console.log('Full booking object for editing:', booking);
    
    // Set up the booking form for editing with proper data mapping
    const editFormData = {
      event_id: booking.event_id,
      tickets: {
        // Map from booking.tickets structure to form structure
        current_user_tickets: booking.tickets?.current_user_tickets || booking.full_price_tickets || 0,
        current_user_seat_section: booking.tickets?.current_user_seat_section || booking.seat_section || null,
        current_user_seat_type: booking.tickets?.current_user_seat_type || booking.seat_type || null,
        current_user_price: booking.tickets?.current_user_price || parseFloat(booking.full_price_amount) || 0.0,
        minor_tickets: booking.tickets?.minor_tickets || [], 
        minor_seat_section: booking.tickets?.minor_seat_section || null,
        minor_seat_type: booking.tickets?.minor_seat_type || null,
        minor_total_price: booking.tickets?.minor_total_price || parseFloat(booking.reduced_price_amount) || 0.0,
        family_members: booking.tickets?.family_members || [],
        family_seat_section: booking.tickets?.family_seat_section || null,
        family_seat_type: booking.tickets?.family_seat_type || null,
        family_total_price: booking.tickets?.family_total_price || 0.0
      },
      guided_visit_required: booking.guided_visit_required || false,
      guided_visit_language: booking.guided_visit_language || "",
      total_amount: parseFloat(booking.total_amount) || 0,
      comment: booking.comment || booking.notes || "",
      admin_comments: booking.admin_comments || "",
      // Legacy fields for backward compatibility
      full_price_tickets: booking.full_price_tickets || booking.tickets?.current_user_tickets || 0,
      reduced_price_tickets: booking.reduced_price_tickets || (booking.tickets?.minor_tickets ? booking.tickets.minor_tickets.length : 0) || 0
    };
    
    console.log('Setting edit form data with proper mapping:', editFormData);
    setBookingForm(editFormData);
    
    // Set editing state immediately - no delay needed
    setEditingBooking(booking);
    setIsBookingDialogOpen(true); // Use regular booking dialog for editing (has admin comments)
    console.log('Edit dialog opened for booking:', booking.id, 'Edit mode will be:', !!booking);
  };

  // Handle delete booking
  const handleDeleteBooking = async (booking) => {
    console.log('Delete booking clicked:', booking);
    
    if (!booking || !booking.id) {
      console.error('Invalid booking data:', booking);
      toast.error('Invalid booking data');
      return;
    }

    if (!booking.user_name) {
      console.error('Missing user_name in booking:', booking);
      booking.user_name = 'Unknown User';
    }

    // Show custom confirmation dialog instead of browser confirm
    setConfirmDialog({
      isOpen: true,
      title: '🗑️ Delete Booking',
      message: `Are you sure you want to delete this booking?

👤 Customer: ${booking.user_name}
📧 Email: ${booking.user_email || 'N/A'}
🎫 Tickets: ${booking.full_price_tickets || 0} full + ${booking.reduced_price_tickets || 0} reduced

⚠️ This action cannot be undone!`,
      onConfirm: () => performDeleteBooking(booking),
      onCancel: () => {
        console.log('Delete cancelled by user');
        setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null });
      }
    });
  };

  // Perform actual deletion after confirmation
  const performDeleteBooking = async (booking) => {
    try {
      console.log('Performing delete for booking:', booking.id);
      const response = await axios.delete(`/bookings/${booking.id}`);
      console.log('Delete response:', response);
      
      toast.success('Booking deleted successfully');
      
      // Close confirmation dialog
      setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null });
      
      // Refresh booking data
      if (selectedEventForManagement && selectedEventForManagement.id) {
        console.log('Refreshing booking data for event:', selectedEventForManagement.id);
        await fetchEventBookings(selectedEventForManagement.id);
        await fetchBookingStats(selectedEventForManagement.id);
        console.log('Booking data refreshed');
      } else {
        console.error('No selectedEventForManagement available for refresh');
      }
    } catch (error) {
      console.error('Delete booking error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete booking';
      toast.error(errorMessage);
      
      // Close confirmation dialog even on error
      setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null });
    }
  };

  // Handle update booking status
  const handleUpdateBookingStatus = async (booking, newStatus) => {
    try {
      await axios.put(`/bookings/${booking.id}`, {
        payment_status: newStatus,
        payment_date: newStatus === 'paid' ? new Date().toISOString() : null
      });
      toast.success(`Booking status updated to ${newStatus}`);
      // Refresh booking data
      if (selectedEventForManagement) {
        await fetchEventBookings(selectedEventForManagement.id);
        await fetchBookingStats(selectedEventForManagement.id);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update booking status';
      toast.error(errorMessage);
    }
  };

  // Handle ticket quantity changes
  const handleTicketQuantityChange = (type, value) => {
    const quantity = Math.max(0, parseInt(value) || 0);
    setBookingForm(prev => ({
      ...prev,
      [type]: quantity
    }));
    // Total will be recalculated by useEffect
  };

  // Edit handlers for CDs and DVDs
  const handleEditCd = (cd) => {
    setEditingItem(cd);
    setCdForm({
      cd_id: cd.cd_id || "",
      title: cd.title || "",
      executor: cd.executor || "",
      category: cd.category || "",
      support: cd.support || "",
      acquisition_date: cd.acquisition_date || ""
    });
    setIsDialogOpen(true);
  };

  const handleEditDvd = (dvd) => {
    setEditingItem(dvd);
    setSelectedDvdCoverImage(null);
    setDvdImagePreview(null);
    setDvdForm({
      dvd_id: dvd.dvd_id,
      title: dvd.title,
      languages: dvd.languages || "",
      actors: dvd.actors || "",
      director: dvd.director || "",
      production_year: dvd.production_year || "",
      purchasing_year: dvd.purchasing_year || "",
      category: dvd.category || "",
      area: dvd.area || ""
    });
    setIsDialogOpen(true);
  };

  // Book edit handler
  const handleEditBook = (book) => {
    setEditingItem(book);
    setBookForm({
      book_id: book.book_id || "",
      author: book.author || "",
      book_title: book.book_title || "",
      language: book.language || "",
      comment: book.comment || "",
      more_info: book.more_info || "",
      purchasing_year: book.purchasing_year || "",
      place: book.place || "",
      category: book.category || "none"  // Map empty category to "none"
    });
    
    // Set existing cover image preview if it exists
    if (book.cover_image || book.cover_image_s3) {
      const imageUrl = getBookCoverUrl(book);
      setImagePreview(imageUrl);
    } else {
      setImagePreview(null);
    }
    setSelectedCoverImage(null); // No new image selected yet
    
    setIsDialogOpen(true);
  };

  // Delete handlers
  const handleDeleteCd = async (cdId) => {
    try {
      await axios.delete(`/cds/${cdId}`);
      toast.success("CD deleted successfully!");
      await fetchCds(searchTerm);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete CD';
      toast.error(errorMessage);
    }
  };

  const handleDeleteDvd = async (dvdId) => {
    try {
      await axios.delete(`/dvds/${dvdId}`);
      toast.success("DVD deleted successfully!");
      await fetchDvds(searchTerm);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete DVD';
      toast.error(errorMessage);
    }
  };

  // Book delete handler
  const handleDeleteBook = async (bookId) => {
    try {
      await axios.delete(`/books/${bookId}`);
      toast.success("Book deleted successfully!");
      await fetchBooks(searchTerm, selectedCategory, selectedLanguage);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete book';
      toast.error(errorMessage);
    }
  };

  // Bulk delete handlers
  const handleBulkDeleteBooks = async () => {
    if (selectedBooks.length === 0) {
      toast.error("Please select books to delete");
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: '🗑️ Delete Selected Books',
      message: `Are you sure you want to delete ${selectedBooks.length} book(s)?
      
⚠️ This action cannot be undone!`,
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null });
        
        try {
          let successCount = 0;
          let failCount = 0;
          
          for (const bookId of selectedBooks) {
            try {
              await axios.delete(`/books/${bookId}`);
              successCount++;
            } catch (error) {
              failCount++;
              console.error(`Failed to delete book ${bookId}:`, error);
            }
          }
          
          if (successCount > 0) {
            toast.success(`${successCount} book(s) deleted successfully!`);
          }
          if (failCount > 0) {
            toast.error(`Failed to delete ${failCount} book(s)`);
          }
          
          setSelectedBooks([]);
          await fetchBooks(searchTerm, selectedCategory, selectedLanguage);
        } catch (error) {
          toast.error("Error during bulk delete");
        }
      },
      onCancel: () => {
        setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null });
      }
    });
  };

  const handleBulkDeleteCDs = async () => {
    if (selectedCDs.length === 0) {
      toast.error("Please select CDs to delete");
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: '🗑️ Delete Selected CDs',
      message: `Are you sure you want to delete ${selectedCDs.length} CD(s)?
      
⚠️ This action cannot be undone!`,
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null });
        
        try {
          let successCount = 0;
          let failCount = 0;
          
          for (const cdId of selectedCDs) {
            try {
              await axios.delete(`/cds/${cdId}`);
              successCount++;
            } catch (error) {
              failCount++;
              console.error(`Failed to delete CD ${cdId}:`, error);
            }
          }
          
          if (successCount > 0) {
            toast.success(`${successCount} CD(s) deleted successfully!`);
          }
          if (failCount > 0) {
            toast.error(`Failed to delete ${failCount} CD(s)`);
          }
          
          setSelectedCDs([]);
          await fetchCds(searchTerm);
        } catch (error) {
          toast.error("Error during bulk delete");
        }
      },
      onCancel: () => {
        setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null });
      }
    });
  };

  const handleBulkDeleteDVDs = async () => {
    if (selectedDVDs.length === 0) {
      toast.error("Please select DVDs to delete");
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: '🗑️ Delete Selected DVDs',
      message: `Are you sure you want to delete ${selectedDVDs.length} DVD(s)?
      
⚠️ This action cannot be undone!`,
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null });
        
        try {
          let successCount = 0;
          let failCount = 0;
          
          for (const dvdId of selectedDVDs) {
            try {
              await axios.delete(`/dvds/${dvdId}`);
              successCount++;
            } catch (error) {
              failCount++;
              console.error(`Failed to delete DVD ${dvdId}:`, error);
            }
          }
          
          if (successCount > 0) {
            toast.success(`${successCount} DVD(s) deleted successfully!`);
          }
          if (failCount > 0) {
            toast.error(`Failed to delete ${failCount} DVD(s)`);
          }
          
          setSelectedDVDs([]);
          await fetchDvds(searchTerm);
        } catch (error) {
          toast.error("Error during bulk delete");
        }
      },
      onCancel: () => {
        setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null });
      }
    });
  };

  // Fetch loan statuses for all media items in one request
  const fetchLoanStatuses = async () => {
    try {
      const response = await axios.get('media/loan-statuses');
      setLoanStatuses(response.data || {});
    } catch (error) {
      console.error("Failed to fetch loan statuses:", error);
    }
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    
    // Clear image previews and selection states
    setImagePreview(null);
    setDvdImagePreview(null);
    setEventImagePreview(null);
    setSelectedCoverImage(null);
    setSelectedDvdCoverImage(null);
    
    // Clear loan search states
    setUserSearch("");
    setMediaSearch("");
    setUserSearchResults([]);
    setMediaSearchResults([]);
    setSelectedUser(null);
    setSelectedMedia(null);
    setUserSearchLoading(false);
    setMediaSearchLoading(false);
    
    // Reset forms only when not editing (to prevent overriding edit data)
    if (!editingItem && !isEditFormReady) {
      setBookForm({
        book_id: "",
        author: "",
        book_title: "",
        language: "",
        comment: "",
        more_info: "",
        purchasing_year: "",
        place: "",
        category: ""
      });
      setCdForm({
        cd_id: "", title: "", executor: "", category: "", support: "", acquisition_date: ""
      });
      setEventForm({
        id: "",
        title: "", description_text: "", description_html: "", event_date: "", booking_deadline: "", 
        organiser_ids: [], venue_ids: [], 
        category_tags: [], event_label: "", status: "draft", max_capacity: "", guided_visit: false, comment: "",
    guest_tickets_enabled: false, guest_surcharge: "",
        // Legacy pricing structure - ensure proper defaults
        price_categories: {
          member_price: "",
          minor_price: "",
          senior_price: "",
          young_adult_price: "",
          member_price_enabled: true,
          minor_price_enabled: false,
          senior_price_enabled: false,
          young_adult_price_enabled: false
        },
        seat_types: [],
        // New Opera-style matrix pricing
        matrix_pricing: {
          member_pricing: { galleria_price: "", platea_price: "" },
          minor_pricing: { galleria_price: "", platea_price: "" },
          senior_pricing: { galleria_price: "", platea_price: "" },
          young_adult_pricing: { galleria_price: "", platea_price: "" },
          member_pricing_enabled: true,
          minor_pricing_enabled: false,
          senior_pricing_enabled: false,
          young_adult_pricing_enabled: false,
          galleria_enabled: false,
          platea_enabled: false
        },
        pricing_type: "standard", // Default to "standard" instead of "legacy"
        imageFile: null
      });
      setLoanForm({
        user_id: "", media_id: "", media_type: "book"
      });
    }
    
    // Clear editing state and form ready flag AFTER potential form reset
    setEditingItem(null);
    setIsEditFormReady(false);
  };

  // Minimal fetch functions to fix compilation (will be expanded later)
  const fetchBooks = async (search = "", category = "", language = "", append = false, sortConfig = booksSortConfig) => {
    try {
      const limit = 200; // Load 200 books at a time
      const currentPage = append ? Math.floor(books.length / limit) + 1 : 1;
      
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      if (language) params.append('language', language);
      params.append('page', currentPage);
      params.append('limit', limit);
      params.append('sort_by', sortConfig.key);
      params.append('sort_order', sortConfig.direction);
      
      console.log(`📚 Fetching books page ${currentPage} (${append ? 'loading more' : 'initial load'}), sorted by ${sortConfig.key} ${sortConfig.direction}...`);
      const response = await axios.get(`books?${params.toString()}`);
      
      // Handle paginated response
      if (response.data && response.data.items && Array.isArray(response.data.items)) {
        const newBooks = response.data.items;
        
        if (append) {
          setBooks(prev => [...prev, ...newBooks]);
          console.log(`✅ Loaded ${newBooks.length} more books (total: ${books.length + newBooks.length}/${response.data.total})`);
        } else {
          setBooks(newBooks);
          console.log(`✅ Loaded ${newBooks.length} books (total available: ${response.data.total})`);
        }
        
        // Store if there are more books to load
        setBooksHasMore(response.data.has_more);
        setBooksTotalCount(response.data.total);
      } else if (Array.isArray(response.data)) {
        // Fallback for old response format
        setBooks(response.data);
        setBooksHasMore(false);
        setBooksTotalCount(response.data.length);
      } else {
        console.warn('⚠️ Unexpected response format:', response.data);
        if (!append) setBooks([]);
      }
    } catch (error) {
      console.error("❌ Failed to fetch books:", error);
      if (!append) setBooks([]);
    }
  };

  const fetchCds = async (search = "", category = "", append = false) => {
    try {
      const limit = 200;
      const currentPage = append ? Math.floor(cds.length / limit) + 1 : 1;
      
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      params.append('page', currentPage);
      params.append('limit', limit);
      
      console.log(`💿 Fetching CDs page ${currentPage} (${append ? 'loading more' : 'initial load'})...`);
      const response = await axios.get(`cds?${params.toString()}`);
      
      if (response.data && response.data.items && Array.isArray(response.data.items)) {
        const newCds = response.data.items;
        
        if (append) {
          setCds(prev => [...prev, ...newCds]);
          console.log(`✅ Loaded ${newCds.length} more CDs (total: ${cds.length + newCds.length}/${response.data.total})`);
        } else {
          setCds(newCds);
          console.log(`✅ Loaded ${newCds.length} CDs (total available: ${response.data.total})`);
        }
        
        setCdsHasMore(response.data.has_more);
        setCdsTotalCount(response.data.total);
      } else if (Array.isArray(response.data)) {
        setCds(response.data);
        setCdsHasMore(false);
        setCdsTotalCount(response.data.length);
      } else {
        console.warn('⚠️ Unexpected CD response format');
        if (!append) setCds([]);
      }
    } catch (error) {
      console.error("❌ Failed to fetch CDs:", error);
      if (!append) setCds([]);
    }
  };

  const fetchDvds = async (search = "", category = "", append = false) => {
    try {
      const limit = 200;
      const currentPage = append ? Math.floor(dvds.length / limit) + 1 : 1;
      
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      params.append('page', currentPage);
      params.append('limit', limit);
      
      console.log(`📀 Fetching DVDs page ${currentPage} (${append ? 'loading more' : 'initial load'})...`);
      const response = await axios.get(`dvds?${params.toString()}`);
      
      if (response.data && response.data.items && Array.isArray(response.data.items)) {
        const newDvds = response.data.items;
        
        if (append) {
          setDvds(prev => [...prev, ...newDvds]);
          console.log(`✅ Loaded ${newDvds.length} more DVDs (total: ${dvds.length + newDvds.length}/${response.data.total})`);
        } else {
          setDvds(newDvds);
          console.log(`✅ Loaded ${newDvds.length} DVDs (total available: ${response.data.total})`);
        }
        
        setDvdsHasMore(response.data.has_more);
        setDvdsTotalCount(response.data.total);
      } else if (Array.isArray(response.data)) {
        setDvds(response.data);
        setDvdsHasMore(false);
        setDvdsTotalCount(response.data.length);
      } else {
        console.warn('⚠️ Unexpected DVD response format');
        if (!append) setDvds([]);
      }
    } catch (error) {
      console.error("❌ Failed to fetch DVDs:", error);
      if (!append) setDvds([]);
    }
  };

  const fetchUsers = async (search = "") => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const response = await axios.get(`users?${params.toString()}`);
      let usersData = response.data || [];
      try {
        const rec = (await axios.get('admin/membership-payment-dates')).data || {};
        usersData = usersData.map((u) => ({
          ...u,
          membership_years: rec.years?.[u.id] || (u.membership_year ? [u.membership_year] : []),
          membership_paid_dates: rec.dates?.[u.id] || {},
        }));
      } catch (e) { /* non-admins: keep plain list */ }
      setUsers(usersData);
      console.log(`✅ Users fetched successfully: ${usersData.length} users`);
      console.log('Users data:', usersData);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      console.error("Users error details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      // Don't clear users on error - keep existing data
      // setUsers([]);
    }
  };

  const fetchLoans = async () => {
    try {
      const response = await axios.get('loans');
      setLoans(response.data || []);
    } catch (error) {
      console.error("Failed to fetch loans:", error);
      setLoans([]);
    }
  };

  const fetchBookCategories = async () => {
    try {
      const response = await axios.get('/books/categories');
      setBookCategories(response.data || []);
    } catch (error) {
      console.error("Failed to fetch book categories:", error);
      setBookCategories([]);
    }
  };

  const fetchLanguageCounts = async () => {
    // Placeholder for now
    setLanguageCounts({});
  };

  const fetchNationalities = async () => {
    try {
      const response = await axios.get('nationalities');
      setNationalities(response.data.nationalities || []);
    } catch (error) {
      console.error("Failed to fetch nationalities:", error);
      setNationalities([]);
    }
  };

  // Fetch remaining ticket availability for events with max_capacity
  const fetchEventAvailability = async (eventsData) => {
    const eventsWithCapacity = (eventsData || events).filter(e => e.max_capacity);
    if (eventsWithCapacity.length === 0) return;
    const avail = {};
    await Promise.all(eventsWithCapacity.map(async (event) => {
      try {
        const res = await axios.get(`events/${event.id}/availability`);
        avail[event.id] = res.data;
      } catch {
        // ignore per-event errors
      }
    }));
    setEventAvailability(prev => ({ ...prev, ...avail }));
  };

  // Cultural Events fetch functions
  const fetchBookedEventIds = async () => {
    try {
      const res = await axios.get('events/my-booking-status');
      setBookedEventIds(res.data?.booked_event_ids || []);
    } catch {
      // Ignore - user might not be logged in
    }
  };

  const fetchEvents = async (search = "", category = "") => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      const response = await axios.get(`events?${params.toString()}`);
      const eventsData = response.data || [];
      setEvents(eventsData);
      console.log(`✅ Events fetched successfully: ${eventsData.length} events`);
      
      // Fetch user's booking status and availability for these events
      fetchBookedEventIds();
      fetchUserBookings();
      fetchEventAvailability(eventsData);
    } catch (error) {
      console.error("Failed to fetch events:", error);
      console.error("Error details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      // Don't clear events on error - keep existing data
      // setEvents([]);
    }
  };

  const fetchOrganisers = async (search = "") => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const response = await axios.get(`organisers?${params.toString()}`);
      setOrganisers(response.data || []);
    } catch (error) {
      console.error("Failed to fetch organisers:", error);
      setOrganisers([]);
    }
  };

  const fetchVenues = async (search = "") => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const response = await axios.get(`venues?${params.toString()}`);
      setVenues(response.data || []);
    } catch (error) {
      console.error("Failed to fetch venues:", error);
      setVenues([]);
    }
  };

  const fetchEventCategories = async () => {
    try {
      const response = await axios.get('events/categories');
      setEventCategories(response.data.categories || []);
    } catch (error) {
      console.error("Failed to fetch event categories:", error);
      setEventCategories([]);
    }
  };

  // Fetch waitlist counts (admin only) keyed by event_id
  const fetchWaitlistCounts = async () => {
    try {
      const res = await axios.get('admin/waitlist-counts');
      setWaitlistCounts(res.data || {});
    } catch (error) {
      console.error("Failed to fetch waitlist counts:", error);
      setWaitlistCounts({});
    }
  };

  // Fetch books when language filter changes
  useEffect(() => {
    if (currentTab === "books") {
      fetchBooks(searchTerm, selectedCategory, selectedLanguage);
    }
  }, [selectedLanguage]);

  // Refetch books when sort changes
  useEffect(() => {
    if (currentTab === "books" && books.length > 0) {
      console.log('📊 Books sort changed, refetching from page 1...');
      fetchBooks(searchTerm, selectedCategory, selectedLanguage, false, booksSortConfig);
    }
  }, [booksSortConfig]);

  // Admin-only: fetch waitlist counts once the admin user is hydrated and events are loaded.
  // Kept separate from fetchEvents to avoid the auth-header race condition during initial boot.
  useEffect(() => {
    if (isAdmin(effectiveUser) && events.length > 0) {
      fetchWaitlistCounts();
    }
  }, [currentUser?.id, currentUser?.role, events.length]);

  // Fetch DVDs when category filter changes
  useEffect(() => {
    if (currentTab === "dvds") {
      fetchDvds(searchTerm, selectedDvdCategory);
    }
  }, [selectedDvdCategory]);

  // Fetch data on component mount and when dependencies change
  useEffect(() => {
    console.log('Current tab changed to:', currentTab);
    if (currentTab === "books") {
      fetchBooks(searchTerm, selectedCategory, selectedLanguage);
      fetchLanguageCounts();
    } else if (currentTab === "cds") {
      fetchCds(searchTerm, selectedCategory);
    } else if (currentTab === "dvds") {
      fetchDvds(searchTerm, selectedDvdCategory);
    } else if (currentTab === "users") {
      fetchUsers(searchTerm);
    } else if (currentTab === "loans") {
      fetchLoans();
      fetchUsers(); // Also fetch users for loan form dropdown
    } else if (currentTab === "events") {
      fetchEvents(searchTerm, selectedCategory);
      fetchOrganisers(); // Fetch organisers for event form
      fetchVenues(); // Fetch venues for event form
      fetchEventCategories(); // Fetch categories for filtering
    }
    
  }, [currentTab, searchTerm, selectedCategory]);

  // Fetch loan statuses when books, CDs, or DVDs are loaded
  useEffect(() => {
    const hasItems = (Array.isArray(books) && books.length > 0) || 
                    (Array.isArray(cds) && cds.length > 0) || 
                    (Array.isArray(dvds) && dvds.length > 0);
    
    console.log(`🔍 Media items changed - Books: ${books?.length || 0}, CDs: ${cds?.length || 0}, DVDs: ${dvds?.length || 0}`);
    
    if (hasItems) {
      console.log('⏱️ Scheduling loan status fetch...');
      // Add a small delay to ensure all media items are rendered
      const timer = setTimeout(() => {
        console.log('🔄 Fetching loan statuses now...');
        fetchLoanStatuses();
      }, 500);
      
      return () => clearTimeout(timer);
    } else {
      console.warn('⚠️ No media items available to fetch loan statuses');
    }
  }, [books?.length, cds?.length, dvds?.length]);

  // Check authentication on component mount
  useEffect(() => {
    fetchNationalities();
    checkAuthStatus();
  }, []);

  // Recalculate booking total when booking form or selected event changes
  useEffect(() => {
    if (selectedEventForBooking && bookingForm) {
      const fullPrice = parseFloat(selectedEventForBooking.cost_euro_full) || 0;
      const reducedPrice = parseFloat(selectedEventForBooking.cost_euro_reduced) || 0;
      
      const fullAmount = fullPrice * (parseInt(bookingForm.full_price_tickets) || 0);
      const reducedAmount = reducedPrice * (parseInt(bookingForm.reduced_price_tickets) || 0);
      const total = fullAmount + reducedAmount;
      
      // Only update if the total has changed to avoid infinite loops
      if (bookingForm.total_amount !== total) {
        setBookingForm(prev => ({
          ...prev,
          total_amount: total
        }));
      }
    }
  }, [selectedEventForBooking, bookingForm.full_price_tickets, bookingForm.reduced_price_tickets]);

  // Handle payment success URL (called when user returns from successful Stripe checkout)
  // Runs on mount AND whenever isAuthenticated flips to true (so it fires after auth/me completes).
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    let bookingId = urlParams.get('booking_id');

    // If booking_id not in URL, check localStorage
    if (!bookingId) {
      bookingId = localStorage.getItem('pending_booking_id');
      const storedSessionId = localStorage.getItem('pending_session_id');

      // Verify session IDs match if both are available
      if (sessionId && storedSessionId && sessionId !== storedSessionId) {
        console.log('Session ID mismatch, skipping payment success handling');
        return;
      }
    }

    if (!sessionId) return;

    // Match any booking-success path Stripe might redirect to.
    const path = window.location.pathname;
    const isBookingSuccessPath =
      path.includes('/bookings/payment-success') ||
      path === '/booking-success' ||
      (path === '/' && !sessionStorage.getItem('pendingRegistration'));

    if (!isBookingSuccessPath) return;

    // Guard against double processing across re-renders / multiple effect runs
    const processedKey = `processed_payment_${sessionId}`;
    if (sessionStorage.getItem(processedKey)) return;

    // Wait until auth is confirmed so confirm-payment is sent with auth header AND
    // the success dialog (rendered inside the authenticated tree) actually appears.
    if (!isAuthenticated) return;

    sessionStorage.setItem(processedKey, '1');

    if (bookingId) {
      console.log('Payment success detected:', { sessionId, bookingId, path });
      handlePaymentSuccess(sessionId, bookingId);
    } else {
      console.log('Payment success detected with session ID only:', { sessionId, path });
      handlePaymentSuccess(sessionId, bookingId);
    }

    // Clean up the URL so a refresh doesn't show /bookings/payment-success again
    window.history.replaceState({}, document.title, '/');

    // Clear localStorage and sessionStorage staging keys
    localStorage.removeItem('pending_booking_id');
    localStorage.removeItem('pending_session_id');
    sessionStorage.removeItem('pendingBookingId');
    sessionStorage.removeItem('pendingSessionId');
  }, [isAuthenticated]);

  // Handle payment success
  const handlePaymentSuccess = async (sessionId, bookingId) => {
    try {
      console.log('Processing payment success for booking:', bookingId);
      
      // Immediately confirm payment since user returned from successful Stripe checkout
      try {
        const confirmResponse = await axios.post(`bookings/${bookingId}/confirm-payment`);
        if (confirmResponse.data.payment_status === 'paid') {
          console.log('✅ Payment immediately confirmed as paid!');
          setPaymentBookingId(bookingId);
          setPaymentSuccess(true);
          setIsSophisticatedBookingOpen(false); // Close the booking dialog
          toast.success('Payment successful! Your booking is confirmed and email confirmation has been sent.');
          
          // Refresh booked event IDs and availability so badges update
          fetchBookedEventIds();
          fetchEventAvailability();
          
          // Navigate to events tab and scroll to All Cultural Events
          setCurrentTab('events');
          setTimeout(() => {
            const allEventsSection = document.getElementById('all-cultural-events');
            if (allEventsSection) {
              allEventsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 500);
          
          return true;
        }
      } catch (error) {
        console.log('⚠️ Immediate confirmation failed, falling back to verification:', error);
      }
      
      // Fallback to the original verification process if immediate confirmation fails
      const verifyPaymentStatus = async (retryCount = 0) => {
        try {
          const verifyResponse = await axios.post(`bookings/${bookingId}/verify-payment`);
          
          if (verifyResponse.data.payment_status === 'paid') {
            console.log('✅ Payment verified as completed!');
            setPaymentBookingId(bookingId);
            setPaymentSuccess(true);
            setIsSophisticatedBookingOpen(false); // Close the booking dialog
            toast.success('🎉 Payment successful! Your booking is confirmed and email confirmation has been sent.');
            
            // Refresh booked event IDs so badges update
            fetchBookedEventIds();
            
            // Navigate to events tab and scroll to All Cultural Events
            setCurrentTab('events');
            setTimeout(() => {
              const allEventsSection = document.getElementById('all-cultural-events');
              if (allEventsSection) {
                allEventsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 500);
            
            return true;
          }
          
          // Check booking status as backup
          const bookingResponse = await axios.get(`bookings/${bookingId}`);
          const booking = bookingResponse.data;
          
          if (booking.payment_status === 'paid') {
            console.log('✅ Payment confirmed as paid via booking check');
            setPaymentBookingId(bookingId);
            setPaymentSuccess(true);
            setIsSophisticatedBookingOpen(false); // Close the booking dialog
            toast.success('🎉 Payment successful! Your booking is confirmed and email confirmation has been sent.');
            
            // Navigate to events tab and scroll to All Cultural Events
            setCurrentTab('events');
            setTimeout(() => {
              const allEventsSection = document.getElementById('all-cultural-events');
              if (allEventsSection) {
                allEventsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 500);
            
            return true;
          } else if (retryCount < 8) { // Try up to 8 times (24 seconds total)
            console.log(`Payment still pending, retrying in 3 seconds... (attempt ${retryCount + 1}/8)`);
            setTimeout(() => verifyPaymentStatus(retryCount + 1), 3000);
            return false;
          } else {
            console.log('Payment verification timeout - showing success anyway');
            // After timeout, assume payment was successful since user completed checkout
            setPaymentBookingId(bookingId);
            setPaymentSuccess(true);
            setIsSophisticatedBookingOpen(false); // Close the booking dialog
            toast.success('🎉 Payment completed! Your booking confirmation and email notification may take a few minutes to appear.');
            return true;
          }
        } catch (error) {
          console.error(`Payment verification attempt ${retryCount + 1} failed:`, error);
          
          if (retryCount < 3) {
            setTimeout(() => verifyPaymentStatus(retryCount + 1), 3000);
          } else {
            // After multiple failures, assume payment was successful
            console.log('Payment verification failed multiple times - assuming success');
            setPaymentBookingId(bookingId);
            setPaymentSuccess(true);
            setIsSophisticatedBookingOpen(false); // Close the booking dialog
            toast.success('Payment completed! If status doesn\'t update, please contact support.');
          }
          return false;
        }
      };
      
      // Start payment verification as backup
      await verifyPaymentStatus();
      
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      
    } catch (error) {
      console.error('Payment success handling error:', error);
      toast.error('There was an issue processing your payment confirmation.');
    }
  };

  // Handle manual payment verification for users
  const handleVerifyPayment = async (booking) => {
    try {
      console.log('Manual payment verification for booking:', booking.id);
      toast.info('Checking payment status...');
      
      const response = await axios.post(`bookings/${booking.id}/verify-payment`);
      
      if (response.data.payment_status === 'paid') {
        toast.success('Payment status updated to paid!');
        // Refresh user bookings
        await fetchUserBookings();
      } else {
        toast.info('Payment is still pending. If you completed payment, please wait a few minutes and try again.');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to verify payment';
      toast.error(errorMessage);
    }
  };

  // Handle user deleting their own booking
  const handleUserDeleteBooking = async (booking) => {
    console.log('User delete booking clicked:', booking);
    
    setConfirmDialog({
      isOpen: true,
      title: '🗑️ Delete Your Booking',
      message: `Are you sure you want to delete this booking?

🎫 Event: ${booking.event_title || 'Unknown Event'}
📅 Event Date: ${new Date(booking.event_date).toLocaleDateString()}
💰 Amount: €${booking.total_amount}

⚠️ This action cannot be undone!`,
      onConfirm: () => performUserDeleteBooking(booking),
      onCancel: () => {
        console.log('User delete cancelled');
        setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null });
      }
    });
  };

  // Perform user booking deletion
  const performUserDeleteBooking = async (booking) => {
    try {
      console.log('Performing user delete for booking:', booking.id);
      const response = await axios.delete(`/bookings/${booking.id}`);
      console.log('User delete response:', response);
      
      toast.success('Your booking has been deleted');
      
      // Close confirmation dialog
      setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null });
      
      // Refresh user bookings
      await fetchUserBookings();
      
    } catch (error) {
      console.error('User delete booking error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete booking';
      toast.error(errorMessage);
      
      // Close confirmation dialog even on error
      setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null });
    }
  };

  // Handle user updating their payment status
  const handleUserUpdatePaymentStatus = async (booking, newStatus) => {
    try {
      console.log('User updating payment status:', booking.id, 'to', newStatus);
      const response = await axios.put(`/bookings/${booking.id}/payment-status?payment_status=${newStatus}`);
      console.log('User payment status update response:', response);
      
      toast.success(`Payment status updated to ${newStatus}`);
      
      // Refresh user bookings
      await fetchUserBookings();
      
    } catch (error) {
      console.error('User payment status update error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to update payment status';
      toast.error(errorMessage);
    }
  };

  // Show loading screen while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Library className="h-12 w-12 text-slate-600 mx-auto mb-4 animate-pulse" />
          <h2 className="text-xl font-semibold text-slate-700 mb-2">Member's Portal</h2>
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="container mx-auto px-2 sm:px-4 py-8 sm:py-16 flex flex-col items-center justify-center min-h-screen">
          <div className="text-center mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_ace-event-manager/artifacts/ybpoj5od_ace%20logo.png"
                alt="Associazione Culturale Europea (ACE)" 
                className="h-24 sm:h-28 lg:h-36 w-auto object-contain flex-shrink-0 drop-shadow-lg"
                style={{
                  filter: 'drop-shadow(0 8px 16px rgba(59, 130, 246, 0.3))'
                }}
              />
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 bg-clip-text text-transparent leading-tight">Associazione Culturale Europea - APS</h1>
                <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent mt-1 leading-tight">Member's Portal</h2>
              </div>
            </div>
          </div>

          <Card className="w-full max-w-md mx-4 sm:mx-0 shadow-xl border-0 bg-white/95 backdrop-blur-sm">
            <CardHeader className="text-center px-4 sm:px-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
              <CardTitle className="text-xl sm:text-2xl font-bold">Welcome</CardTitle>
              <p className="text-sm sm:text-base text-blue-100">Sign in to book cultural events and access your media and book collection</p>
            </CardHeader>
            <CardContent className="space-y-4 px-4 sm:px-6 py-6">
              <Button 
                className="w-full bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-800 hover:to-slate-900 h-12 text-sm sm:text-base shadow-lg transform hover:scale-105 transition-all duration-200" 
                onClick={() => setIsLoginOpen(true)}
              >
                <LogIn className="h-4 w-4 mr-2" />
                Sign In
              </Button>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-slate-500">Or</span>
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-sm text-slate-600 mb-3">
                  <span className="font-semibold text-purple-700">REGISTER</span> to become an ACE member
                </p>
                <Button 
                  variant="outline" 
                  className="w-full border-2 border-purple-300 text-purple-700 hover:bg-gradient-to-r hover:from-purple-50 hover:to-indigo-50 hover:border-purple-400 h-12 shadow-md transform hover:scale-105 transition-all duration-200" 
                  onClick={() => setIsRegisterOpen(true)}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Register Now
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Auth Forms */}
          <LoginForm
            isOpen={isLoginOpen}
            onClose={() => setIsLoginOpen(false)}
            onLogin={login}
            loginForm={loginForm}
            setLoginForm={setLoginForm}
            onForgotPassword={() => setIsForgotPasswordOpen(true)}
          />

          <RegisterForm
            isOpen={isRegisterOpen}
            onClose={() => setIsRegisterOpen(false)}
            onRegister={register}
            registerForm={registerForm}
            setRegisterForm={setRegisterForm}
            nationalities={nationalities}
          />

          {/* Password Reset Forms */}
          <ForgotPasswordForm
            isOpen={isForgotPasswordOpen}
            onClose={() => setIsForgotPasswordOpen(false)}
          />

          <ResetPasswordForm
            isOpen={isResetPasswordOpen}
            onClose={() => {
              setIsResetPasswordOpen(false);
              setResetToken("");
            }}
            token={resetToken}
          />
        </div>
      </div>
    );
  }

  // Registration success is now handled by dedicated RegistrationResult page
  // Remove old inline success page

  // Render registration cancelled page
  if (showRegistrationCancelled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xl">!</span>
              </div>
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Registration Cancelled</h1>
            <p className="text-gray-600 mb-6">
              Your membership registration was cancelled. You can try again anytime.
            </p>
            
            <div className="space-y-3">
              <Button 
                onClick={() => {
                  setShowRegistrationCancelled(false);
                  setIsRegisterOpen(true);
                }}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                Try Again
              </Button>
              
              <Button 
                variant="outline" 
                onClick={() => setShowRegistrationCancelled(false)}
                className="w-full"
              >
                Back to Homepage
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render dashboard if requested
  if (showDashboard) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div className="flex items-center gap-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ace-event-manager/artifacts/ybpoj5od_ace%20logo.png"
                  alt="Associazione Culturale Europea (ACE)" 
                  className="h-12 w-auto object-contain"
                />
                <div>
                  <h1 className="text-3xl font-bold text-blue-700">Associazione Culturale Europea</h1>
                  <p className="text-blue-600 font-semibold">ACE Member's Portal</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Button
                  onClick={() => setShowDashboard(false)}
                  variant="outline"
                >
                  ← Back to Main App
                </Button>
                <UserMenu 
                  currentUser={effectiveUser}
                  nationalities={nationalities}
                  onLogout={logout}
                  onPromoteUser={promoteUser}
                  onViewBookings={async () => {
                    await fetchUserBookings();
                    setShowBookingHistory(true);
                    setShowDashboard(false);
                  }}
                  onViewLoans={async () => {
                    await fetchUserLoans();
                    setShowLoanHistory(true);
                    setShowDashboard(false);
                  }}
                  onViewDashboard={() => {
                    setShowDashboard(true);
                  }}
                  onChangePassword={() => setIsChangePasswordOpen(true)}
                  onExportMyData={async () => {
                    try {
                      const res = await axios.post("/auth/export-my-data", null, { responseType: "blob" });
                      const url = URL.createObjectURL(res.data);
                      const a = document.createElement("a");
                      a.href = url;
                      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
                      a.download = `my_ace_data_${stamp}.xlsx`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      URL.revokeObjectURL(url);
                      toast.success("Your data export has been downloaded.");
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Failed to export your data.");
                    }
                  }}
                  onDeleteMyAccount={() => setIsDeleteAccountOpen(true)}
                  onViewPaymentHistory={() => setPaymentHistoryTarget({ userId: "me", name: "" })}
                />
              </div>
            </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Dashboard 
            currentUser={currentUser} 
            handleExportLoanHistory={handleExportLoanHistory}
            refreshTrigger={dashboardRefreshTrigger}
            authToken={authToken}
          />
        </div>
      </div>
    );
  }

  // Main authenticated application
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Welcome Overlay */}
      {welcomeOverlay && (
        <div
          className={`fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-br from-indigo-600 via-blue-700 to-purple-700 transition-opacity duration-500 ${welcomeOverlay.fading ? 'opacity-0' : 'opacity-100'}`}
          data-testid="welcome-overlay"
        >
          <div className="text-center">
            <div className="mb-6">
              <svg className="mx-auto h-16 w-16 text-white animate-spin" style={{ animationDuration: '1.5s' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2 animate-pulse">
              {welcomeOverlay.name ? `Welcome back, ${welcomeOverlay.name}!` : 'Signing in...'}
            </h2>
            <p className="text-blue-200 text-lg">
              {welcomeOverlay.name ? 'Loading your dashboard...' : 'Please wait'}
            </p>
          </div>
        </div>
      )}

      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {/* Header */}
        <div className="flex flex-wrap sm:flex-nowrap justify-between items-center gap-2 mb-4 sm:mb-8">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <img 
              src="https://customer-assets.emergentagent.com/job_ace-event-manager/artifacts/ybpoj5od_ace%20logo.png"
              alt="Associazione Culturale Europea (ACE)" 
              className="h-8 sm:h-12 w-auto object-contain shrink-0"
            />
            <div className="min-w-0">
              <h1 className="text-base sm:text-2xl lg:text-3xl font-bold text-blue-700 leading-tight truncate">
                <span className="hidden sm:inline">Associazione Culturale Europea</span>
                <span className="sm:hidden">ACE Portal</span>
              </h1>
              <p className="hidden sm:block text-blue-600 font-semibold text-sm">ACE Member's Portal</p>
            </div>
          </div>
          
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {/* View-as-Member Toggle (visible only to real admins) */}
            {currentUser?.role === 'Administrator' && (
              <Button
                variant={viewAsMember ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewAsMember(v => !v)}
                className={`flex items-center gap-1.5 px-2 sm:px-3 ${viewAsMember ? 'bg-purple-600 hover:bg-purple-700 text-white border-purple-600' : 'border-purple-300 text-purple-700 hover:bg-purple-50'}`}
                title={viewAsMember ? 'Currently viewing as a regular member. Click to switch back to your admin view.' : 'Switch to the member view so you can book events for yourself like a regular member.'}
                data-testid="toggle-view-as-member"
              >
                <UserCheck className="h-4 w-4" />
                <span className="hidden sm:inline">{viewAsMember ? 'Viewing as member' : 'View as member'}</span>
              </Button>
            )}

            {/* Help Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => showHelpGuide(isAdmin(effectiveUser) ? 'admin' : 'user')}
              className="flex items-center gap-1 px-2 sm:px-3"
              title="Help"
            >
              <HelpCircle className="h-4 w-4" />
              <span className="hidden sm:inline">Help</span>
            </Button>
            
            {/* System Documentation Button (Admin Only) */}
            {hasPermission(effectiveUser, 'system_docs') && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => showHelpGuide('system')}
                className="hidden sm:flex items-center gap-1"
                title="System Docs"
              >
                <FileText className="h-4 w-4" />
                System Docs
              </Button>
            )}
            
            {/* Database Backup Button (Admin Only) */}
            {hasPermission(effectiveUser, 'backup') && (
              <Button
                variant="outline"
                size="sm"
                onClick={openBackupDialog}
                className="hidden sm:flex items-center gap-1 text-green-700 border-green-300 hover:bg-green-50"
                title="DB Backup"
              >
                <Download className="h-4 w-4" />
                DB Backup
              </Button>
            )}
            
            {/* User Menu */}
            <UserMenu 
            currentUser={effectiveUser}
            nationalities={nationalities}
            onLogout={logout}
            onPromoteUser={promoteUser}
            onViewBookings={async () => {
              await fetchUserBookings();
              setShowBookingHistory(true);
            }}
            onViewLoans={async () => {
              await fetchUserLoans();
              setShowLoanHistory(true);
            }}
            onViewDashboard={() => {
              setShowDashboard(true);
            }}
            onChangePassword={() => setIsChangePasswordOpen(true)}
            onExportMyData={async () => {
              try {
                const res = await axios.post("/auth/export-my-data", null, { responseType: "blob" });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url;
                const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
                a.download = `my_ace_data_${stamp}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
                toast.success("Your data export has been downloaded.");
              } catch (err) {
                toast.error(err.response?.data?.detail || "Failed to export your data.");
              }
            }}
            onDeleteMyAccount={() => setIsDeleteAccountOpen(true)}
            onViewPaymentHistory={() => setPaymentHistoryTarget({ userId: "me", name: "" })}
          />
          </div>
        </div>

        {/* Welcome Dashboard for Logged-in Users */}
        <div className="mb-4 sm:mb-6">
          <Card className="bg-gradient-to-r from-indigo-100 via-blue-50 to-purple-100 border-indigo-200 shadow-lg">
            <CardHeader className="text-center pb-3 sm:pb-4 px-3 sm:px-6 bg-gradient-to-r from-indigo-600/10 to-purple-600/10 rounded-t-lg">
              <CardTitle className="text-lg sm:text-xl lg:text-2xl bg-gradient-to-r from-indigo-700 to-purple-700 bg-clip-text text-transparent font-bold">Welcome to Your ACE Dashboard</CardTitle>
              <p className="text-xs sm:text-sm lg:text-base text-slate-700 mt-2 leading-relaxed">
                As a valued ACE member, you have access to our complete cultural ecosystem. 
                Explore our rich collections, book exciting events, and immerse yourself in arts and culture. 
                Your membership opens doors to knowledge, entertainment, and community experiences.
              </p>
            </CardHeader>
            <CardContent className="px-3 sm:px-6 py-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
                {/* BOOK YOUR NEXT CULTURAL EVENTS - 2/3 width */}
                <div className="lg:col-span-2">
                  <div 
                    className="h-full text-center p-3 sm:p-4 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl border-2 border-blue-400 hover:shadow-2xl hover:from-blue-700 hover:to-indigo-800 transition-all duration-300 cursor-pointer transform hover:scale-[1.02] relative overflow-hidden group"
                    onClick={() => {
                      setCurrentTab('events');
                      // Scroll to the events dashboard after the tab content mounts (helpful on mobile)
                      setTimeout(() => {
                        const target = document.getElementById('cultural-events-dashboard-top');
                        if (target) {
                          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        } else {
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      }, 80);
                    }}
                    data-testid="book-events-cta"
                  >
                    {/* Background decoration */}
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>
                    
                    <div className="relative z-10">
                      <div className="text-2xl sm:text-3xl lg:text-4xl mb-2">🎭</div>
                      <h3 className="text-base sm:text-lg lg:text-xl font-bold text-white mb-1 uppercase tracking-wide">
                        Book Your Next Cultural Events
                      </h3>
                      <p className="text-xs sm:text-sm text-blue-100 leading-relaxed">
                        Discover and reserve tickets for concerts, exhibitions, theater performances, and exclusive cultural experiences
                      </p>
                      <div className="mt-2 inline-flex items-center gap-2 text-white font-semibold text-xs sm:text-sm">
                        <Calendar className="h-4 w-4" />
                        <span>Explore {getUpcomingEvents().length} Upcoming Events</span>
                        <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right column - Split into two sections - 1/3 width */}
                <div className="lg:col-span-1 flex flex-col gap-3 sm:gap-4">
                  {/* CONSULT OUR LIBRARY AND MEDIATECA - Top half (green) */}
                  <div 
                    className="flex-1 text-center p-3 sm:p-4 bg-gradient-to-br from-emerald-600 to-teal-700 rounded-xl border-2 border-emerald-400 hover:shadow-2xl hover:from-emerald-700 hover:to-teal-800 transition-all duration-300 cursor-pointer transform hover:scale-[1.02] relative overflow-hidden group"
                    onClick={() => {
                      setShowLibraryResources(true);
                      setCurrentTab('books');
                      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 80);
                    }}
                    data-testid="consult-library-cta"
                  >
                    {/* Background decoration */}
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/20 to-teal-400/20 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>
                    
                    <div className="relative z-10">
                      <div className="text-2xl sm:text-3xl mb-1 sm:mb-2">📚</div>
                      <h3 className="text-sm sm:text-base lg:text-lg font-bold text-white mb-1 uppercase tracking-wide">
                        Consult Our Library & Mediateca
                      </h3>
                      <p className="text-xs text-emerald-100 leading-relaxed">
                        Browse our extensive collection
                      </p>
                      <div className="mt-2 inline-flex items-center gap-1 text-white font-semibold text-xs">
                        <Library className="h-3 w-3" />
                        <span>{books.length + dvds.length + cds.length} Items</span>
                        <ChevronRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>

                  {/* ACE USER MANAGEMENT - Bottom half (grey) - Admin only */}
                  {hasPermission(effectiveUser, 'users') && (
                    <div 
                      className="flex-1 text-center p-3 sm:p-4 bg-gradient-to-br from-slate-600 to-slate-700 rounded-xl border-2 border-slate-400 hover:shadow-2xl hover:from-slate-700 hover:to-slate-800 transition-all duration-300 cursor-pointer transform hover:scale-[1.02] relative overflow-hidden group"
                      onClick={() => {
                        setCurrentTab('users');
                        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 80);
                      }}
                      data-testid="user-management-cta"
                    >
                      {/* Background decoration */}
                      <div className="absolute inset-0 bg-gradient-to-r from-slate-400/20 to-slate-500/20 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>
                      
                      <div className="relative z-10">
                        <div className="text-2xl sm:text-3xl mb-1 sm:mb-2">👥</div>
                        <h3 className="text-sm sm:text-base lg:text-lg font-bold text-white mb-1 uppercase tracking-wide">
                          ACE User Management
                        </h3>
                        <p className="text-xs text-slate-100 leading-relaxed">
                          Manage members and users
                        </p>
                        <div className="mt-2 inline-flex items-center gap-1 text-white font-semibold text-xs">
                          <Users className="h-3 w-3" />
                          <span>{users.length} Users</span>
                          <ChevronRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* DOCUMENT REPOSITORY - visible to all verified users */}
                  <div
                    className="flex-1 text-center p-3 sm:p-4 bg-gradient-to-br from-indigo-600 to-violet-700 rounded-xl border-2 border-indigo-400 hover:shadow-2xl hover:from-indigo-700 hover:to-violet-800 transition-all duration-300 cursor-pointer transform hover:scale-[1.02] relative overflow-hidden group"
                    onClick={() => setIsDocumentRepositoryOpen(true)}
                    data-testid="document-repository-cta"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-indigo-400/20 to-violet-400/20 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>
                    <div className="relative z-10">
                      <div className="text-2xl sm:text-3xl mb-1 sm:mb-2">📁</div>
                      <h3 className="text-sm sm:text-base lg:text-lg font-bold text-white mb-1 uppercase tracking-wide">
                        Document Repository
                      </h3>
                      <p className="text-xs text-indigo-100 leading-relaxed">
                        Bylaws, minutes, newsletters & shared files
                      </p>
                      <div className="mt-2 inline-flex items-center gap-1 text-white font-semibold text-xs">
                        <FileText className="h-3 w-3" />
                        <span>{isAdmin(effectiveUser) ? 'Browse & upload' : 'Browse files'}</span>
                        <ChevronRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>

                  {/* SEASON PASS MANAGEMENT — Admin only */}
                  {isAdmin(effectiveUser) && (
                    <div
                      className="flex-1 text-center p-3 sm:p-4 bg-gradient-to-br from-purple-600 to-fuchsia-700 rounded-xl border-2 border-purple-400 hover:shadow-2xl hover:from-purple-700 hover:to-fuchsia-800 transition-all duration-300 cursor-pointer transform hover:scale-[1.02] relative overflow-hidden group"
                      onClick={() => setIsSeasonPassAdminOpen(true)}
                      data-testid="season-passes-cta"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-purple-400/20 to-fuchsia-400/20 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>
                      <div className="relative z-10">
                        <div className="text-2xl sm:text-3xl mb-1 sm:mb-2">🎼</div>
                        <h3 className="text-sm sm:text-base lg:text-lg font-bold text-white mb-1 uppercase tracking-wide">
                          Season Passes
                        </h3>
                        <p className="text-xs text-purple-100 leading-relaxed">
                          Multi-event subscriptions (concerts, theatre…)
                        </p>
                        <div className="mt-2 inline-flex items-center gap-1 text-white font-semibold text-xs">
                          <span>Manage passes</span>
                          <ChevronRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* GLOBAL SETTINGS — Admin only */}
                  {isAdmin(effectiveUser) && (
                    <div
                      className="flex-1 text-center p-3 sm:p-4 bg-gradient-to-br from-slate-600 to-slate-800 rounded-xl border-2 border-slate-400 hover:shadow-2xl hover:from-slate-700 hover:to-slate-900 transition-all duration-300 cursor-pointer transform hover:scale-[1.02] relative overflow-hidden group"
                      onClick={() => setIsAdminSettingsOpen(true)}
                      data-testid="admin-settings-cta"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-slate-400/20 to-slate-500/20 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>
                      <div className="relative z-10">
                        <div className="text-2xl sm:text-3xl mb-1 sm:mb-2">⚙️</div>
                        <h3 className="text-sm sm:text-base lg:text-lg font-bold text-white mb-1 uppercase tracking-wide">
                          Global Settings
                        </h3>
                        <p className="text-xs text-slate-200 leading-relaxed">
                          Disclaimer & shared strings
                        </p>
                        <div className="mt-2 inline-flex items-center gap-1 text-white font-semibold text-xs">
                          <span>Edit</span>
                          <ChevronRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* EVENT FINANCIAL MANAGEMENT - Admin only */}
                  {hasPermission(effectiveUser, 'financials') && (
                    <div 
                      className="flex-1 text-center p-3 sm:p-4 bg-gradient-to-br from-amber-600 to-orange-700 rounded-xl border-2 border-amber-400 hover:shadow-2xl hover:from-amber-700 hover:to-orange-800 transition-all duration-300 cursor-pointer transform hover:scale-[1.02] relative overflow-hidden group"
                      onClick={() => setCurrentTab('financials')}
                      data-testid="financials-nav-card"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-amber-400/20 to-orange-400/20 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>
                      <div className="relative z-10">
                        <div className="text-2xl sm:text-3xl mb-1 sm:mb-2">💰</div>
                        <h3 className="text-sm sm:text-base lg:text-lg font-bold text-white mb-1 uppercase tracking-wide">
                          Event Financials
                        </h3>
                        <p className="text-xs text-amber-100 leading-relaxed">
                          Income, expenses & profit/loss
                        </p>
                        <div className="mt-2 inline-flex items-center gap-1 text-white font-semibold text-xs">
                          <DollarSign className="h-3 w-3" />
                          <span>View Reports</span>
                          <ChevronRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Rest of the application content would go here */}
        <div className="space-y-8">
          {/* Tabs Navigation */}
          <Tabs value={currentTab} onValueChange={handleTabChange} className="space-y-4 sm:space-y-6">
            {/* Show Library Resources tabs when active */}
            {showLibraryResources && (currentTab === 'books' || currentTab === 'dvds' || currentTab === 'cds' || currentTab === 'loans') && (
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-4 rounded-lg border border-emerald-200 shadow-md">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Library className="h-5 w-5 text-emerald-700" />
                    <h3 className="text-lg font-semibold text-emerald-900">Library & Mediateca Resources</h3>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowLibraryResources(false);
                      setCurrentTab('events');
                    }}
                    className="text-emerald-700 hover:text-emerald-900 hover:bg-emerald-100"
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Back to Events
                  </Button>
                </div>
                
                <TabsList className="grid grid-cols-3 sm:grid-cols-4 gap-2 w-full bg-white p-2 rounded-lg">
                  <TabsTrigger 
                    value="books" 
                    className="flex items-center gap-2 py-3 px-4 text-sm bg-slate-100 text-slate-600 hover:bg-slate-200 hover:shadow-md data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all shadow-sm"
                  >
                    <Book className="h-4 w-4" />
                    <span className="font-medium">Books</span>
                    <Badge variant="secondary" className="ml-auto">
                      {books.length}
                    </Badge>
                  </TabsTrigger>
                  
                  <TabsTrigger 
                    value="dvds" 
                    className="flex items-center gap-2 py-3 px-4 text-sm bg-slate-100 text-slate-600 hover:bg-slate-200 hover:shadow-md data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all shadow-sm"
                  >
                    <Film className="h-4 w-4" />
                    <span className="font-medium">DVDs</span>
                    <Badge variant="secondary" className="ml-auto">
                      {dvds.length}
                    </Badge>
                  </TabsTrigger>
                  
                  <TabsTrigger 
                    value="cds" 
                    className="flex items-center gap-2 py-3 px-4 text-sm bg-slate-100 text-slate-600 hover:bg-slate-200 hover:shadow-md data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all shadow-sm"
                  >
                    <Music className="h-4 w-4" />
                    <span className="font-medium">CDs</span>
                    <Badge variant="secondary" className="ml-auto">
                      {cds.length}
                    </Badge>
                  </TabsTrigger>
                  
                  {/* Loans tab (Admin only) */}
                  {hasPermission(effectiveUser, 'loans') && (
                    <TabsTrigger 
                      value="loans" 
                      className="flex items-center gap-2 py-3 px-4 text-sm bg-slate-100 text-slate-600 hover:bg-slate-200 hover:shadow-md data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all shadow-sm"
                    >
                      <CheckCircle className="h-4 w-4" />
                      <span className="font-medium">Loans</span>
                      <Badge variant="secondary" className="ml-auto">
                        {loans.length}
                      </Badge>
                    </TabsTrigger>
                  )}
                </TabsList>
              </div>
            )}

            {/* Show Events tab when not in library view - HIDDEN, navigation via boxes above */}
            {false && (!showLibraryResources || currentTab === 'events') && (
              <div className="overflow-x-auto -mx-2 px-2">
                <div className="flex flex-col sm:flex-row gap-2 w-full">
                  {/* Main Navigation */}
                  <TabsList className="grid grid-cols-1 sm:grid-cols-auto h-auto gap-1 sm:gap-2 w-full sm:w-auto">
                    {/* Cultural Events - Primary Tab */}
                    <TabsTrigger value="events" className="flex items-center gap-2 py-3 px-4 text-sm bg-blue-50 hover:bg-blue-100 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                      <Calendar className="h-4 w-4" />
                      <span className="font-medium">Cultural Events Dashboard</span>
                      <Badge variant="secondary" className="ml-auto">
                        {events.length}
                      </Badge>
                    </TabsTrigger>
                  </TabsList>

                  {/* Admin Users Management - Enhanced Tab */}
                  {hasPermission(effectiveUser, 'users') && (
                    <TabsList className="grid grid-cols-1 gap-1 w-full sm:w-auto">
                      <TabsTrigger value="users" className="flex items-center gap-2 py-3 px-4 text-sm bg-emerald-50 hover:bg-emerald-100 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
                        <Users className="h-4 w-4" />
                        <span className="font-medium">User Management</span>
                        <Badge variant="secondary" className="ml-auto">
                          {users.length}
                        </Badge>
                      </TabsTrigger>
                    </TabsList>
                  )}
                </div>
              </div>
            )}

            {/* Search Controls - Only show for tabs that don't have embedded controls */}
            {currentTab !== 'books' && currentTab !== 'cds' && currentTab !== 'dvds' && currentTab !== 'financials' && (
              <div className="flex gap-4 items-center">
                <div className="relative flex-1 max-w-md">
                  <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
                  <Input
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                {/* View Mode Toggle - only for events (not users or loans) */}
                {currentTab !== 'users' && currentTab !== 'loans' && (
                <div className="flex border border-slate-200 rounded-lg">
                  <Button
                    variant={viewMode === "cards" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("cards")}
                    className="rounded-r-none"
                    title="Card view"
                  >
                    <Grid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === "table" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("table")}
                    className="rounded-l-none border-l"
                    title="Table view"
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>
                )}
                
                {/* Admin-only Add buttons - only for non-library tabs */}
                {isAdmin(effectiveUser) && (
                  <div className="flex gap-2">
                    {/* Add Button for events, users, loans */}
                    <Button 
                      className="bg-slate-700 hover:bg-slate-800"
                    onClick={() => {
                      setEditingItem(null); // Clear editing state for new item
                      setIsDialogOpen(true); // Open the dialog
                      
                      // Reset forms based on current tab
                      if (currentTab === "books") {
                        setBookForm({
                          book_id: "", author: "", book_title: "", language: "", 
                          comment: "", more_info: "", purchasing_year: "", place: "", category: "none"
                        });
                      } else if (currentTab === "cds") {
                        setCdForm({
                          title: "", artist: "", album: "", genre: "", year: "", duration: "", track_count: ""
                        });
                      } else if (currentTab === "dvds") {
                        setDvdForm({
                          dvd_id: "", title: "", languages: "", actors: "", director: "", 
                          production_year: "", purchasing_year: "", category: "", area: ""
                        });
                      } else if (currentTab === "users") {
                        setUserForm({
                          first_name: "", last_name: "", email: "", phone: "", role: "Regular User", 
                          street: "", city: "", postal_code: "", country: "", department: "", 
                          position: "", employee_id: "", last_year_member: false, member_category: "JRC Internal",
                          password: ""
                        });
                      } else if (currentTab === "loans") {
                        setLoanForm({
                          user_id: "", media_id: "", media_type: "book"
                        });
                        // Ensure users are loaded for the dropdown
                        fetchUsers();
                      } else if (currentTab === "events") {
                        setEventForm({
                          title: "", description_text: "", description_html: "", event_date: "", booking_deadline: "", 
                          organiser_ids: [], venue_ids: [], 
                          category_tags: [], event_label: "", status: "draft", max_capacity: "", guided_visit: false, imageFile: null,
                          // New sophisticated pricing structure
                          price_categories: {
                            member_price: "",
                            minor_price: "",
                            senior_price: "",
                            young_adult_price: "",
                            member_price_enabled: true,
                            minor_price_enabled: false,
                            senior_price_enabled: false,
                            young_adult_price_enabled: false
                          },
                          seat_types: []
                        });
                        // Ensure organisers and venues are loaded
                        fetchOrganisers();
                        fetchVenues();
                      }
                    }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add {currentTab === "users" ? "User" : 
                           currentTab === "loans" ? "Loan" : 
                           currentTab === "events" ? "Event" : "Item"}
                    </Button>
                    
                    {/* Organiser and Venue Management buttons - only for Events tab */}
                  {currentTab === "events" && (
                    <>
                      <Button 
                        variant="outline"
                        className="border-blue-200 text-blue-700 hover:bg-blue-50"
                        onClick={() => {
                          setEditingItem(null);
                          setOrganiserForm({
                            name: "", email: "", phone: "", bio: "", website: ""
                          });
                          setCurrentTab("organiser"); // Temporary tab to show organiser form
                          setIsDialogOpen(true);
                        }}
                      >
                        <Users className="h-4 w-4 mr-2" />
                        Add Organiser
                      </Button>
                      
                      <Button 
                        variant="outline"
                        className="border-blue-300 text-blue-800 hover:bg-blue-100"
                        onClick={() => setIsOrganisersManagementOpen(true)}
                      >
                        <Users className="h-4 w-4 mr-2" />
                        Manage Organisers
                      </Button>
                      
                      <Button 
                        variant="outline"
                        className="border-green-200 text-green-700 hover:bg-green-50"
                        onClick={() => {
                          setEditingItem(null);
                          setVenueForm({
                            name: "", address: "", city: "", capacity: "", contact_info: "", facilities: []
                          });
                          setCurrentTab("venue"); // Temporary tab to show venue form
                          setIsDialogOpen(true);
                        }}
                      >
                        <Building2 className="h-4 w-4 mr-2" />
                        Add Venue
                      </Button>
                      
                      <Button 
                        variant="outline"
                        className="border-green-300 text-green-800 hover:bg-green-100"
                        onClick={() => setIsVenuesManagementOpen(true)}
                      >
                        <Building2 className="h-4 w-4 mr-2" />
                        Manage Venues
                      </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Tab Content */}
            <TabsContent value="books" data-books-section>
              {/* Tab Description */}
              <div className="mb-4 sm:mb-6">
                <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                  <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-6">
                    <CardTitle className="text-base sm:text-lg font-semibold text-green-800 flex items-center gap-2">
                      📚 <span className="leading-tight">Explore Our Library Collection</span>
                    </CardTitle>
                    <p className="text-xs sm:text-sm text-slate-700 mb-2 leading-relaxed">
                      Access thousands of books across multiple languages and genres.
                    </p>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 sm:p-3 mb-2 sm:mb-3">
                      <p className="text-xs sm:text-sm font-medium text-amber-800 leading-relaxed">
                        📍 <strong>Books can be borrowed from our bookshop in Building 1 every Monday and Thursday from 9:30 a.m. to 12:30 p.m.</strong>
                      </p>
                    </div>
                    <p className="text-xs sm:text-sm text-slate-700 mb-2">ACE Members can:</p>
                    <ul className="text-xs sm:text-sm text-slate-600 space-y-1 ml-3 sm:ml-4">
                      <li>• Search and filter by language, category, and author</li>
                      <li>• Browse new acquisitions and popular titles</li>
                      <li>• Request to borrow books for home reading</li>
                      <li>• View availability and loan periods</li>
                      <li>• Discover recommendations based on your interests</li>
                    </ul>
                  </CardHeader>
                </Card>
              </div>
              
              {/* Search and Controls - Books specific */}
              <div className="mb-4 sm:mb-6 flex flex-wrap items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-white rounded-lg border border-slate-200 shadow-sm">
                {/* Search Bar */}
                <div className="relative flex-1 min-w-[150px] sm:min-w-[200px]">
                  <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
                  <Input
                    placeholder="Search books..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                {/* View Mode Toggle */}
                <div className="flex border border-slate-200 rounded-lg">
                  <Button
                    variant={viewMode === "cards" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("cards")}
                    className="rounded-r-none"
                    title="Card view"
                  >
                    <Grid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === "table" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("table")}
                    className="rounded-l-none border-l"
                    title="Table view"
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* Admin Controls */}
                {isAdmin(effectiveUser) && (
                  <div className="flex flex-wrap gap-2">
                    {/* Download Template Button */}
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        window.open(`${process.env.REACT_APP_BACKEND_URL}/books/template`, '_blank');
                      }}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Template
                    </Button>
                    
                    {/* Bulk Upload Button */}
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        document.getElementById('bulk-upload-input').click();
                      }}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Bulk Upload
                    </Button>
                    
                    {/* Add Book Button */}
                    <Button 
                      size="sm"
                      className="bg-slate-700 hover:bg-slate-800"
                      onClick={() => {
                        setEditingItem(null);
                        setIsDialogOpen(true);
                        setBookForm({
                          book_id: "", author: "", book_title: "", language: "", 
                          comment: "", more_info: "", purchasing_year: "", place: "", category: "none"
                        });
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Book
                    </Button>
                    
                    {/* Bulk Delete button */}
                    <Button 
                      variant="destructive"
                      size="sm"
                      onClick={handleBulkDeleteBooks}
                      disabled={selectedBooks.length === 0}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Selected ({selectedBooks.length})
                    </Button>
                  </div>
                )}
              </div>
              
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Books Collection</CardTitle>
                    <div className="flex items-center gap-2">
                      {/* Fix Cover Images Button */}
                      {isAdmin(effectiveUser) && (
                        <Button
                          onClick={handleFixCoverImages}
                          variant="outline"
                          className="flex items-center gap-2 text-blue-700 border-blue-300 hover:bg-blue-50"
                          disabled={isFixingImages}
                        >
                          {isFixingImages ? (
                            <>
                              <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                              Fixing...
                            </>
                          ) : (
                            <>
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              Fix Book Covers
                            </>
                          )}
                        </Button>
                      )}
                      {isAdmin(effectiveUser) && (
                        <Button
                          onClick={handleExportBooks}
                          variant="outline"
                          className="flex items-center gap-2 text-green-700 border-green-300 hover:bg-green-50"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                          </svg>
                          Export Books to Excel
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="relative">
                  {/* Loading Overlay */}
                  {isDataLoading && currentTab === 'books' && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-700"></div>
                        <span className="text-slate-600">Loading books...</span>
                      </div>
                    </div>
                  )}
                  {/* Language Filter Buttons */}
                  <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-slate-50 rounded-lg">
                    <h3 className="text-xs sm:text-sm font-semibold text-slate-700 mb-2 sm:mb-3">Filter by Language</h3>
                    <div className="flex flex-wrap gap-1 sm:gap-2 lg:gap-3">
                      <Button
                        variant={selectedLanguage === "" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedLanguage("")}
                        className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium px-2 sm:px-3 lg:px-4 py-2 sm:py-3 h-8 sm:h-10 lg:h-auto"
                      >
                        <span className="text-sm sm:text-lg lg:text-2xl">🌍</span> 
                        <span className="hidden sm:inline">All Languages</span>
                        <span className="sm:hidden">All</span>
                      </Button>
                      <Button
                        variant={selectedLanguage === "French" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedLanguage("French")}
                        className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium px-2 sm:px-3 lg:px-4 py-2 sm:py-3 h-8 sm:h-10 lg:h-auto"
                      >
                        <span className="text-sm sm:text-lg lg:text-2xl">🇫🇷</span> French
                      </Button>
                      <Button
                        variant={selectedLanguage === "English" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedLanguage("English")}
                        className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium px-2 sm:px-3 lg:px-4 py-2 sm:py-3 h-8 sm:h-10 lg:h-auto"
                      >
                        <span className="text-sm sm:text-lg lg:text-2xl">🇬🇧</span> English
                      </Button>
                      <Button
                        variant={selectedLanguage === "Italian" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedLanguage("Italian")}
                        className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium px-2 sm:px-3 lg:px-4 py-2 sm:py-3 h-8 sm:h-10 lg:h-auto"
                      >
                        <span className="text-sm sm:text-lg lg:text-2xl">🇮🇹</span> Italian
                      </Button>
                      <Button
                        variant={selectedLanguage === "German" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedLanguage("German")}
                        className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium px-2 sm:px-3 lg:px-4 py-2 sm:py-3 h-8 sm:h-10 lg:h-auto"
                      >
                        <span className="text-sm sm:text-lg lg:text-2xl">🇩🇪</span> German
                      </Button>
                      <Button
                        variant={selectedLanguage === "Spanish" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedLanguage("Spanish")}
                        className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium px-2 sm:px-3 lg:px-4 py-2 sm:py-3 h-8 sm:h-10 lg:h-auto"
                      >
                        <span className="text-sm sm:text-lg lg:text-2xl">🇪🇸</span> Spanish
                      </Button>
                      <Button
                        variant={selectedLanguage === "Dutch" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedLanguage("Dutch")}
                        className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium px-2 sm:px-3 lg:px-4 py-2 sm:py-3 h-8 sm:h-10 lg:h-auto"
                      >
                        <span className="text-sm sm:text-lg lg:text-2xl">🇳🇱</span> Dutch
                      </Button>
                    </div>
                  </div>

                  {/* Category Filter Buttons */}
                  <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 rounded-lg">
                    <div className="flex justify-between items-center mb-2 sm:mb-3">
                      <h3 className="text-xs sm:text-sm font-semibold text-slate-700">Filter by Category</h3>
                      {isAdmin(effectiveUser) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setIsBookCategoryManagementOpen(true)}
                          className="text-xs text-blue-600 hover:text-blue-700 h-6 px-2"
                        >
                          Manage Categories
                        </Button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 sm:gap-2">
                      <Button
                        variant={selectedCategory === "" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedCategory("")}
                        className="text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2 h-8 sm:h-9"
                      >
                        All Categories
                      </Button>
                      {bookCategories
                        .filter(category => category.is_active)
                        .sort((a, b) => a.display_order - b.display_order)
                        .map((category) => (
                          <Button
                            key={category.id}
                            variant={selectedCategory === category.name ? "default" : "outline"}
                            size="sm"
                            onClick={() => setSelectedCategory(category.name)}
                            className="text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2 h-8 sm:h-9"
                            title={category.description || category.name}
                          >
                            {category.name.length > 20 ? category.name.substring(0, 17) + '...' : category.name}
                          </Button>
                        ))}
                    </div>
                  </div>

                  {/* Pagination Controls and Book Count */}
                  {Array.isArray(books) && books.length > 0 && (
                    <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        {/* Book Count and Page Info */}
                        <div className="text-sm text-slate-600">
                          {showAllBooks ? (
                            <span>Showing all <strong>{books.length}</strong> books</span>
                          ) : (
                            <span>
                              Showing <strong>{Math.min((booksCurrentPage - 1) * booksPerPage + 1, books.length)}</strong> to{' '}
                              <strong>{Math.min(booksCurrentPage * booksPerPage, books.length)}</strong> of{' '}
                              <strong>{books.length}</strong> books
                              {getTotalPages() > 1 && (
                                <span className="ml-2 text-slate-500">
                                  (Page {booksCurrentPage} of {getTotalPages()})
                                </span>
                              )}
                            </span>
                          )}
                        </div>

                        {/* Items per page selector */}
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-slate-600">Books per page:</span>
                          <div className="flex gap-2">
                            {[20, 50, 100].map((size) => (
                              <Button
                                key={size}
                                size="sm"
                                variant={!showAllBooks && booksPerPage === size ? "default" : "outline"}
                                onClick={() => handleBooksPageSizeChange(size)}
                                className="h-8 px-3 text-xs"
                              >
                                {size}
                              </Button>
                            ))}
                            <Button
                              size="sm"
                              variant={showAllBooks ? "default" : "outline"}
                              onClick={() => handleBooksPageSizeChange('all')}
                              className="h-8 px-3 text-xs"
                            >
                              All
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Page Navigation */}
                      {!showAllBooks && getTotalPages() > 1 && (
                        <div className="mt-4 flex justify-center items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleBooksPageChange(booksCurrentPage - 1)}
                            disabled={booksCurrentPage === 1}
                            className="h-8 px-3"
                          >
                            ← Previous
                          </Button>
                          
                          {/* Page Numbers */}
                          <div className="flex gap-1">
                            {Array.from({ length: Math.min(5, getTotalPages()) }, (_, i) => {
                              let pageNum;
                              if (getTotalPages() <= 5) {
                                pageNum = i + 1;
                              } else if (booksCurrentPage <= 3) {
                                pageNum = i + 1;
                              } else if (booksCurrentPage >= getTotalPages() - 2) {
                                pageNum = getTotalPages() - 4 + i;
                              } else {
                                pageNum = booksCurrentPage - 2 + i;
                              }
                              
                              return (
                                <Button
                                  key={pageNum}
                                  size="sm"
                                  variant={booksCurrentPage === pageNum ? "default" : "outline"}
                                  onClick={() => handleBooksPageChange(pageNum)}
                                  className="h-8 w-8 p-0 text-xs"
                                >
                                  {pageNum}
                                </Button>
                              );
                            })}
                          </div>
                          
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleBooksPageChange(booksCurrentPage + 1)}
                            disabled={booksCurrentPage === getTotalPages()}
                            className="h-8 px-3"
                          >
                            Next →
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {books.length === 0 ? (
                    <div className="text-center py-12">
                      <Book className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No books found</p>
                    </div>
                  ) : (
                    <>
                      {viewMode === "table" ? (
                        /* Table View */
                        <div className="overflow-x-auto -mx-2 sm:mx-0">
                          <div className="inline-block min-w-full align-middle">
                            <div className="rounded-lg border border-slate-200 bg-white">
                              <Table className="min-w-full">
                                <TableHeader>
                                  <TableRow>
                                    {isAdmin(effectiveUser) && (
                                      <TableHead className="w-12">
                                        <input
                                          type="checkbox"
                                          checked={selectedBooks.length === getPaginatedBooks().length && getPaginatedBooks().length > 0}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setSelectedBooks(getPaginatedBooks().map(book => book.id));
                                            } else {
                                              setSelectedBooks([]);
                                            }
                                          }}
                                          className="w-4 h-4 rounded border-slate-300"
                                        />
                                      </TableHead>
                                    )}
                                    <TableHead className="w-12 sm:w-16">Cover</TableHead>
                                    <TableHead 
                                      className="w-16 sm:w-20 cursor-pointer hover:bg-slate-50 select-none"
                                      onClick={() => handleSort('book_id', booksSortConfig, setBooksSortConfig)}
                                    >
                                      <div className="flex items-center gap-1">
                                        ID
                                        {booksSortConfig.key === 'book_id' && (
                                          booksSortConfig.direction === 'asc' ? (
                                            <ChevronUp className="h-4 w-4" />
                                          ) : (
                                            <ChevronDown className="h-4 w-4" />
                                          )
                                        )}
                                      </div>
                                    </TableHead>
                                    <TableHead 
                                      className="min-w-[150px] cursor-pointer hover:bg-slate-50 select-none"
                                      onClick={() => handleSort('book_title', booksSortConfig, setBooksSortConfig)}
                                    >
                                      <div className="flex items-center gap-1">
                                        Title
                                        {booksSortConfig.key === 'book_title' && (
                                          booksSortConfig.direction === 'asc' ? (
                                            <ChevronUp className="h-4 w-4" />
                                          ) : (
                                            <ChevronDown className="h-4 w-4" />
                                          )
                                        )}
                                      </div>
                                    </TableHead>
                                    <TableHead 
                                      className="min-w-[120px] hidden sm:table-cell cursor-pointer hover:bg-slate-50 select-none"
                                      onClick={() => handleSort('author', booksSortConfig, setBooksSortConfig)}
                                    >
                                      <div className="flex items-center gap-1">
                                        Author
                                        {booksSortConfig.key === 'author' && (
                                          booksSortConfig.direction === 'asc' ? (
                                            <ChevronUp className="h-4 w-4" />
                                          ) : (
                                            <ChevronDown className="h-4 w-4" />
                                          )
                                        )}
                                      </div>
                                    </TableHead>
                                    <TableHead className="w-20 hidden md:table-cell">Language</TableHead>
                                    <TableHead className="w-24 hidden lg:table-cell">Category</TableHead>
                                    <TableHead className="w-16 hidden sm:table-cell">Year</TableHead>
                                    <TableHead className="w-20">Status</TableHead>
                                    <TableHead className="w-24 hidden sm:table-cell">Link</TableHead>
                                    {isAdmin(effectiveUser) && (
                                      <TableHead className="w-24">Actions</TableHead>
                                    )}
                                  </TableRow>
                                </TableHeader>
                            <TableBody>
                              {getPaginatedBooks().map((book, index) => (
                                <TableRow 
                                  key={book.id}
                                  className={index % 2 === 0 ? "bg-white" : "bg-slate-50 hover:bg-slate-100"}
                                >
                                  {isAdmin(effectiveUser) && (
                                    <TableCell className="w-12">
                                      <input
                                        type="checkbox"
                                        checked={selectedBooks.includes(book.id)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedBooks([...selectedBooks, book.id]);
                                          } else {
                                            setSelectedBooks(selectedBooks.filter(id => id !== book.id));
                                          }
                                        }}
                                        className="w-4 h-4 rounded border-slate-300"
                                      />
                                    </TableCell>
                                  )}
                                  <TableCell className="w-12 sm:w-16">
                                    {(book.cover_image || book.cover_image_s3) ? (
                                      <img
                                        src={getBookCoverUrl(book)}
                                        alt={`Cover of ${book.book_title}`}
                                        className="w-8 h-10 sm:w-12 sm:h-16 object-cover rounded border border-slate-200 shadow-sm"
                                        onError={(e) => handleBookCoverError(e, book)}
                                      />
                                    ) : null}
                                    <div 
                                      className={`w-8 h-10 sm:w-12 sm:h-16 ${(book.cover_image || book.cover_image_s3) ? 'hidden' : 'flex'} items-center justify-center bg-slate-100 rounded border border-slate-200 text-slate-400`}
                                    >
                                      <Book className="h-4 w-4 sm:h-6 sm:w-6" />
                                    </div>
                                  </TableCell>
                                  <TableCell className="font-medium text-xs sm:text-sm">{book.book_id || book.id}</TableCell>
                                  <TableCell className="font-semibold text-xs sm:text-sm">
                                    <div className="max-w-[300px] break-words">
                                      {book.book_title}
                                    </div>
                                  </TableCell>
                                  <TableCell className="hidden sm:table-cell text-xs sm:text-sm">
                                    <div className="max-w-[200px] break-words">
                                      {book.author}
                                    </div>
                                  </TableCell>
                                  <TableCell className="hidden md:table-cell">
                                    <Badge variant="outline" className="text-xs">
                                      {book.language}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="hidden lg:table-cell text-xs sm:text-sm">
                                    <div className="max-w-[100px] truncate">
                                      {book.category}
                                    </div>
                                  </TableCell>
                                  <TableCell className="hidden sm:table-cell text-xs sm:text-sm">{book.purchasing_year}</TableCell>
                                  <TableCell>
                                    {loanStatuses[book.id]?.is_loaned ? (
                                      <div className="space-y-1">
                                        <Badge variant="destructive" className="text-xs">
                                          <Clock className="h-3 w-3 mr-1" />
                                          On Loan
                                        </Badge>
                                        <p className="text-xs text-slate-600">
                                          {loanStatuses[book.id].borrower}
                                        </p>
                                      </div>
                                    ) : (
                                      <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                                        Available
                                      </Badge>
                                    )}
                                  </TableCell>
                                  <TableCell className="hidden sm:table-cell">
                                    {book.more_info ? (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="text-xs h-7 px-2 border-blue-300 text-blue-700 hover:bg-blue-50"
                                        onClick={() => window.open(book.more_info, '_blank', 'noopener,noreferrer')}
                                      >
                                        <ExternalLink className="h-3 w-3 mr-1" />
                                        More Info
                                      </Button>
                                    ) : (
                                      <span className="text-xs text-slate-400">—</span>
                                    )}
                                  </TableCell>
                                  {isAdmin(effectiveUser) && (
                                    <TableCell>
                                      <div className="flex gap-1">
                                        <Button size="sm" variant="ghost" title="Edit book" onClick={() => handleEditBook(book)}>
                                          <Edit className="h-4 w-4" />
                                        </Button>
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          title="Delete book"
                                          disabled={loanStatuses[book.id]?.is_loaned}
                                          onClick={() => handleDeleteBook(book.id)}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    </div>
                      ) : (
                        /* Card View */
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
                          {getPaginatedBooks().map((book) => (
                            <Card key={book.id} className="hover:shadow-lg transition-shadow">
                              {/* Book Cover Image */}
                              <div className="relative h-48 bg-slate-100 rounded-t-lg overflow-hidden flex items-center justify-center">
                                {(book.cover_image || book.cover_image_s3) ? (
                                  <img
                                    src={getBookCoverUrl(book)}
                                    alt={`Cover of ${book.book_title}`}
                                    className="max-w-[120px] max-h-[180px] object-contain shadow-lg rounded"
                                    onError={(e) => handleBookCoverError(e, book)}
                                  />
                                ) : null}
                                <div 
                                  className={`${(book.cover_image || book.cover_image_s3) ? 'hidden' : 'flex'} w-full h-full items-center justify-center bg-slate-100 text-slate-400`}
                                >
                                  <Book className="h-16 w-16" />
                                </div>
                                
                                {/* Status Badge Overlay */}
                                <div className="absolute top-2 right-2">
                                  {loanStatuses[book.id]?.is_loaned ? (
                                    <Badge variant="destructive" className="text-xs">
                                      <Clock className="h-3 w-3 mr-1" />
                                      On Loan
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-xs text-green-600 border-green-300 bg-white">
                                      Available
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              
                              <CardHeader className="pb-3">
                                <div className="flex justify-between items-start">
                                  <div className="flex items-center gap-2">
                                    <div className="font-medium text-sm text-slate-500">
                                      #{book.book_id || book.id}
                                    </div>
                                  </div>
                                  {isAdmin(effectiveUser) && (
                                    <div className="flex gap-1">
                                      <Button size="sm" variant="ghost" title="Edit book" onClick={() => handleEditBook(book)}>
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        title="Delete book"
                                        disabled={loanStatuses[book.id]?.is_loaned}
                                        onClick={() => handleDeleteBook(book.id)}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                                <CardTitle className="text-lg">{book.book_title}</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-3 text-sm">
                                  <div>
                                    <span className="font-medium text-slate-700">Author:</span>
                                    <p className="text-slate-600">{book.author}</p>
                                  </div>
                                  
                                  <div className="flex gap-2 flex-wrap">
                                    <Badge variant="outline">{book.language}</Badge>
                                    {book.category && <Badge variant="secondary">{book.category}</Badge>}
                                    {book.purchasing_year && <Badge variant="outline">{book.purchasing_year}</Badge>}
                                  </div>

                                  {book.comment && (
                                    <div>
                                      <span className="font-medium text-slate-700">Comment:</span>
                                      <p className="text-slate-600">{book.comment}</p>
                                    </div>
                                  )}

                                  {book.place && (
                                    <div>
                                      <span className="font-medium text-slate-700">Place:</span>
                                      <p className="text-slate-600">{book.place}</p>
                                    </div>
                                  )}

                                  {/* More Info Link */}
                                  {book.more_info && (
                                    <div>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
                                        onClick={() => window.open(book.more_info, '_blank', 'noopener,noreferrer')}
                                      >
                                        <ExternalLink className="h-4 w-4 mr-2" />
                                        More Info
                                      </Button>
                                    </div>
                                  )}

                                  {/* Loan Details (if on loan) */}
                                  {loanStatuses[book.id]?.is_loaned && (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-2 mt-2">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="text-sm text-red-700 font-medium">
                                          Borrowed by: {loanStatuses[book.id].borrower}
                                        </span>
                                      </div>
                                      <div className="text-xs text-red-600">
                                        Due: {new Date(loanStatuses[book.id].due_date).toLocaleDateString('en-GB')}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                      
                      {/* Load More Button */}
                      {booksHasMore && (
                        <div className="mt-6 pt-4 border-t border-slate-200 text-center">
                          <Button
                            onClick={() => fetchBooks(searchTerm, selectedCategory, selectedLanguage, true)}
                            disabled={booksLoading}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-8"
                          >
                            {booksLoading ? 'Loading...' : `Load More Books (${books.length} of ${booksTotalCount})`}
                          </Button>
                          <p className="text-sm text-slate-500 mt-2">
                            Showing {books.length} of {booksTotalCount} books
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="cds" data-cds-section>
              {/* Tab Description */}
              <div className="mb-4 sm:mb-6">
                <Card className="bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200">
                  <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-6">
                    <CardTitle className="text-base sm:text-lg font-semibold text-orange-800 flex items-center gap-2">
                      🎵 <span className="leading-tight">Musical Journey Collection</span>
                    </CardTitle>
                    <p className="text-xs sm:text-sm text-slate-700 mb-2 sm:mb-3 leading-relaxed">
                      Immerse yourself in our diverse music library spanning all genres and eras.
                    </p>
                    <p className="text-xs sm:text-sm text-slate-700 mb-2">Members enjoy:</p>
                    <ul className="text-xs sm:text-sm text-slate-600 space-y-1 ml-3 sm:ml-4">
                      <li>• Browse by musical category and executor/artist</li>
                      <li>• Discover rare recordings and popular albums</li>
                      <li>• Borrow CDs for personal listening</li>
                      <li>• Explore different musical cultures and styles</li>
                      <li>• Find both vintage and contemporary music</li>
                    </ul>
                  </CardHeader>
                </Card>
              </div>
              
              {/* Search and Controls - CDs specific */}
              <div className="mb-4 sm:mb-6 flex flex-wrap items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-white rounded-lg border border-slate-200 shadow-sm">
                {/* Search Bar */}
                <div className="relative flex-1 min-w-[150px] sm:min-w-[200px]">
                  <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
                  <Input
                    placeholder="Search CDs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                {/* View Mode Toggle */}
                <div className="flex border border-slate-200 rounded-lg">
                  <Button
                    variant={viewMode === "cards" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("cards")}
                    className="rounded-r-none"
                    title="Card view"
                  >
                    <Grid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === "table" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("table")}
                    className="rounded-l-none border-l"
                    title="Table view"
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* Admin Controls */}
                {isAdmin(effectiveUser) && (
                  <div className="flex flex-wrap gap-2">
                    {/* Download Template Button */}
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        window.open(`${process.env.REACT_APP_BACKEND_URL}/cds/template`, '_blank');
                      }}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Template
                    </Button>
                    
                    {/* Bulk Upload Button */}
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        document.getElementById('bulk-upload-input').click();
                      }}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Bulk Upload
                    </Button>
                    
                    {/* Add CD Button */}
                    <Button 
                      size="sm"
                      className="bg-slate-700 hover:bg-slate-800"
                      onClick={() => {
                        setEditingItem(null);
                        setIsDialogOpen(true);
                        setCdForm({
                          title: "", artist: "", album: "", genre: "", year: "", duration: "", track_count: ""
                        });
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add CD
                    </Button>
                    
                    {/* Bulk Delete button */}
                    <Button 
                      variant="destructive"
                      size="sm"
                      onClick={handleBulkDeleteCDs}
                      disabled={selectedCDs.length === 0}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Selected ({selectedCDs.length})
                    </Button>
                  </div>
                )}
              </div>
              
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>CDs Collection</CardTitle>
                    {isAdmin(effectiveUser) && (
                      <Button
                        onClick={handleExportCDs}
                        variant="outline"
                        className="flex items-center gap-2 text-green-700 border-green-300 hover:bg-green-50"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                        </svg>
                        Export CDs to Excel
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* CDs Pagination Controls and Count */}
                  {Array.isArray(cds) && cds.length > 0 && (
                    <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        {/* CD Count and Page Info */}
                        <div className="text-sm text-slate-600">
                          {showAllCds ? (
                            <span>Showing all <strong>{cds.length}</strong> CDs</span>
                          ) : (
                            <span>
                              Showing <strong>{Math.min((cdsCurrentPage - 1) * cdsPerPage + 1, cds.length)}</strong> to{' '}
                              <strong>{Math.min(cdsCurrentPage * cdsPerPage, cds.length)}</strong> of{' '}
                              <strong>{cds.length}</strong> CDs
                              {getCdsTotalPages() > 1 && (
                                <span className="ml-2 text-slate-500">
                                  (Page {cdsCurrentPage} of {getCdsTotalPages()})
                                </span>
                              )}
                            </span>
                          )}
                        </div>

                        {/* Items per page selector */}
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-slate-600">CDs per page:</span>
                          <div className="flex gap-2">
                            {[20, 50, 100].map((size) => (
                              <Button
                                key={size}
                                size="sm"
                                variant={!showAllCds && cdsPerPage === size ? "default" : "outline"}
                                onClick={() => handleCdsPageSizeChange(size)}
                                className="h-8 px-3 text-xs"
                              >
                                {size}
                              </Button>
                            ))}
                            <Button
                              size="sm"
                              variant={showAllCds ? "default" : "outline"}
                              onClick={() => handleCdsPageSizeChange('all')}
                              className="h-8 px-3 text-xs"
                            >
                              All
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Page Navigation */}
                      {!showAllCds && getCdsTotalPages() > 1 && (
                        <div className="mt-4 flex justify-center items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCdsPageChange(cdsCurrentPage - 1)}
                            disabled={cdsCurrentPage === 1}
                            className="h-8 px-3"
                          >
                            ← Previous
                          </Button>
                          
                          {/* Page Numbers */}
                          <div className="flex gap-1">
                            {Array.from({ length: Math.min(5, getCdsTotalPages()) }, (_, i) => {
                              let pageNum;
                              if (getCdsTotalPages() <= 5) {
                                pageNum = i + 1;
                              } else if (cdsCurrentPage <= 3) {
                                pageNum = i + 1;
                              } else if (cdsCurrentPage >= getCdsTotalPages() - 2) {
                                pageNum = getCdsTotalPages() - 4 + i;
                              } else {
                                pageNum = cdsCurrentPage - 2 + i;
                              }
                              
                              return (
                                <Button
                                  key={pageNum}
                                  size="sm"
                                  variant={cdsCurrentPage === pageNum ? "default" : "outline"}
                                  onClick={() => handleCdsPageChange(pageNum)}
                                  className="h-8 w-8 p-0 text-xs"
                                >
                                  {pageNum}
                                </Button>
                              );
                            })}
                          </div>
                          
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCdsPageChange(cdsCurrentPage + 1)}
                            disabled={cdsCurrentPage === getCdsTotalPages()}
                            className="h-8 px-3"
                          >
                            Next →
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {cds.length === 0 ? (
                    <div className="text-center py-12">
                      <Music className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No CDs found</p>
                    </div>
                  ) : (
                    <>
                      {viewMode === "table" ? (
                        /* Table View */
                        <div className="rounded-lg border border-slate-200 bg-white">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                {isAdmin(effectiveUser) && (
                                  <TableHead className="w-12">
                                    <input
                                      type="checkbox"
                                      checked={selectedCDs.length === getPaginatedCds().length && getPaginatedCds().length > 0}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedCDs(getPaginatedCds().map(cd => cd.id));
                                        } else {
                                          setSelectedCDs([]);
                                        }
                                      }}
                                      className="w-4 h-4 rounded border-slate-300"
                                    />
                                  </TableHead>
                                )}
                                <TableHead 
                                  className="w-16 sm:w-20 cursor-pointer hover:bg-slate-50 select-none"
                                  onClick={() => handleSort('cd_id', cdsSortConfig, setCdsSortConfig)}
                                >
                                  <div className="flex items-center gap-1">
                                    ID
                                    {cdsSortConfig.key === 'cd_id' && (
                                      cdsSortConfig.direction === 'asc' ? (
                                        <ChevronUp className="h-4 w-4" />
                                      ) : (
                                        <ChevronDown className="h-4 w-4" />
                                      )
                                    )}
                                  </div>
                                </TableHead>
                                <TableHead className="min-w-[150px]">Title</TableHead>
                                <TableHead 
                                  className="min-w-[120px] hidden sm:table-cell cursor-pointer hover:bg-slate-50 select-none"
                                  onClick={() => handleSort('executor', cdsSortConfig, setCdsSortConfig)}
                                >
                                  <div className="flex items-center gap-1">
                                    Executor
                                    {cdsSortConfig.key === 'executor' && (
                                      cdsSortConfig.direction === 'asc' ? (
                                        <ChevronUp className="h-4 w-4" />
                                      ) : (
                                        <ChevronDown className="h-4 w-4" />
                                      )
                                    )}
                                  </div>
                                </TableHead>
                                <TableHead className="w-24 hidden md:table-cell">Category</TableHead>
                                <TableHead className="w-20 hidden lg:table-cell">Support</TableHead>
                                <TableHead className="w-28 hidden lg:table-cell">Acquisition Date</TableHead>
                                <TableHead className="w-20">Status</TableHead>
                                {isAdmin(effectiveUser) && (
                                  <TableHead className="w-24">Actions</TableHead>
                                )}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {getPaginatedCds().map((cd) => (
                                <TableRow key={cd.id}>
                                  {isAdmin(effectiveUser) && (
                                    <TableCell className="w-12">
                                      <input
                                        type="checkbox"
                                        checked={selectedCDs.includes(cd.id)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedCDs([...selectedCDs, cd.id]);
                                          } else {
                                            setSelectedCDs(selectedCDs.filter(id => id !== cd.id));
                                          }
                                        }}
                                        className="w-4 h-4 rounded border-slate-300"
                                      />
                                    </TableCell>
                                  )}
                                  <TableCell className="font-medium text-xs sm:text-sm">{cd.cd_id || cd.id}</TableCell>
                                  <TableCell className="font-semibold text-xs sm:text-sm">
                                    <div className="max-w-[300px] break-words">
                                      {cd.title}
                                    </div>
                                  </TableCell>
                                  <TableCell className="hidden sm:table-cell text-xs sm:text-sm">
                                    <div className="max-w-[200px] break-words">
                                      {cd.executor}
                                    </div>
                                  </TableCell>
                                  <TableCell className="hidden md:table-cell">
                                    <Badge variant="outline" className="text-xs">
                                      {cd.category}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="hidden lg:table-cell text-xs sm:text-sm">
                                    <div className="max-w-[80px] truncate">
                                      {cd.support}
                                    </div>
                                  </TableCell>
                                  <TableCell className="hidden lg:table-cell text-xs sm:text-sm">
                                    {cd.acquisition_date ? new Date(cd.acquisition_date).toLocaleDateString('en-GB') : '-'}
                                  </TableCell>
                                  <TableCell>
                                    {loanStatuses[cd.id]?.is_loaned ? (
                                      <div className="space-y-1">
                                        <Badge variant="destructive" className="text-xs">
                                          <Clock className="h-3 w-3 mr-1" />
                                          On Loan
                                        </Badge>
                                        <p className="text-xs text-slate-600">
                                          {loanStatuses[cd.id].borrower}
                                        </p>
                                      </div>
                                    ) : (
                                      <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                                        Available
                                      </Badge>
                                    )}
                                  </TableCell>
                                  {isAdmin(effectiveUser) && (
                                    <TableCell>
                                      <div className="flex gap-1">
                                        <Button size="sm" variant="ghost" title="Edit CD" onClick={() => handleEditCd(cd)}>
                                          <Edit className="h-4 w-4" />
                                        </Button>
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          title="Delete CD"
                                          disabled={loanStatuses[cd.id]?.is_loaned}
                                          onClick={() => handleDeleteCd(cd.id)}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        /* Card View */
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
                          {getPaginatedCds().map((cd) => (
                            <Card key={cd.id} className="hover:shadow-lg transition-shadow">
                              <CardHeader className="pb-3">
                                <div className="flex justify-between items-start">
                                  <div className="flex items-center gap-2">
                                    <Music className="h-5 w-5 text-slate-600" />
                                    <div className="font-medium text-sm text-slate-500">
                                      #{cd.cd_id || cd.id}
                                    </div>
                                  </div>
                                  {isAdmin(effectiveUser) && (
                                    <div className="flex gap-1">
                                      <Button size="sm" variant="ghost" title="Edit CD" onClick={() => handleEditCd(cd)}>
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        title="Delete CD"
                                        disabled={loanStatuses[cd.id]?.is_loaned}
                                        onClick={() => handleDeleteCd(cd.id)}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                                <CardTitle className="text-lg">{cd.title}</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-3 text-sm">
                                  <div>
                                    <span className="font-medium text-slate-700">Executor:</span>
                                    <p className="text-slate-600">{cd.executor}</p>
                                  </div>
                                  
                                  {cd.category && (
                                    <div>
                                      <span className="font-medium text-slate-700">Category:</span>
                                      <p className="text-slate-600">{cd.category}</p>
                                    </div>
                                  )}
                                  
                                  {cd.support && (
                                    <div>
                                      <span className="font-medium text-slate-700">Support:</span>
                                      <p className="text-slate-600">{cd.support}</p>
                                    </div>
                                  )}

                                  {cd.acquisition_date && (
                                    <div>
                                      <span className="font-medium text-slate-700">Acquisition Date:</span>
                                      <p className="text-slate-600">
                                        {new Date(cd.acquisition_date).toLocaleDateString('en-GB')}
                                      </p>
                                    </div>
                                  )}

                                  {/* Loan Status */}
                                  {loanStatuses[cd.id]?.is_loaned ? (
                                    <div className="bg-red-100 border border-red-300 rounded-lg p-2 mt-2">
                                      <div className="flex items-center gap-2">
                                        <Badge variant="destructive" className="text-xs">
                                          <Clock className="h-3 w-3 mr-1" />
                                          On Loan
                                        </Badge>
                                        <span className="text-sm text-red-700 font-medium">
                                          {loanStatuses[cd.id].borrower}
                                        </span>
                                      </div>
                                      <div className="text-xs text-red-600 mt-1">
                                        Due: {new Date(loanStatuses[cd.id].due_date).toLocaleDateString('en-GB')}
                                      </div>
                                    </div>
                                  ) : (
                                    <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                                      Available
                                    </Badge>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                      
                      {/* Load More CDs Button */}
                      {cdsHasMore && (
                        <div className="mt-6 pt-4 border-t border-slate-200 text-center">
                          <Button
                            onClick={() => fetchCds(searchTerm, selectedCategory, true)}
                            disabled={cdsLoading}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-8"
                          >
                            {cdsLoading ? 'Loading...' : `Load More CDs (${cds.length} of ${cdsTotalCount})`}
                          </Button>
                          <p className="text-sm text-slate-500 mt-2">
                            Showing {cds.length} of {cdsTotalCount} CDs
                          </p>
                        </div>
                      )}
                      
                      {/* Bottom CD Pagination Controls */}
                      {Array.isArray(cds) && cds.length > 0 && !showAllCds && getCdsTotalPages() > 1 && (
                        <div className="mt-6 pt-4 border-t border-slate-200">
                          <div className="flex justify-center items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCdsPageChange(cdsCurrentPage - 1)}
                              disabled={cdsCurrentPage === 1}
                              className="h-8 px-3"
                            >
                              ← Previous
                            </Button>
                            
                            <span className="text-sm text-slate-600 mx-4">
                              Page {cdsCurrentPage} of {getCdsTotalPages()}
                            </span>
                            
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCdsPageChange(cdsCurrentPage + 1)}
                              disabled={cdsCurrentPage === getCdsTotalPages()}
                              className="h-8 px-3"
                            >
                              Next →
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="dvds" data-dvds-section>
              {/* Tab Description */}
              <div className="mb-4 sm:mb-6">
                <Card className="bg-gradient-to-r from-red-50 to-pink-50 border-red-200">
                  <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-6">
                    <CardTitle className="text-base sm:text-lg font-semibold text-red-800 flex items-center gap-2">
                      🎬 <span className="leading-tight">Cinema at Home</span>
                    </CardTitle>
                    <p className="text-xs sm:text-sm text-slate-700 mb-2 sm:mb-3 leading-relaxed">
                      Enjoy our extensive DVD collection featuring films from around the world. You can:
                    </p>
                    <ul className="text-xs sm:text-sm text-slate-600 space-y-1 ml-3 sm:ml-4">
                      <li>• Explore movies by category (Drama, Comedy, Documentary, etc.)</li>
                      <li>• Find films by acclaimed directors and executors</li>
                      <li>• Check availability for home borrowing</li>
                      <li>• Discover classic cinema and contemporary releases</li>
                      <li>• Build your personal watchlist</li>
                    </ul>
                  </CardHeader>
                </Card>
              </div>
              
              {/* Search and Controls - DVDs specific */}
              <div className="mb-4 sm:mb-6 flex flex-wrap items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-white rounded-lg border border-slate-200 shadow-sm">
                {/* Search Bar */}
                <div className="relative flex-1 min-w-[150px] sm:min-w-[200px]">
                  <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
                  <Input
                    placeholder="Search DVDs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                {/* View Mode Toggle */}
                <div className="flex border border-slate-200 rounded-lg">
                  <Button
                    variant={viewMode === "cards" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("cards")}
                    className="rounded-r-none"
                    title="Card view"
                  >
                    <Grid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === "table" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("table")}
                    className="rounded-l-none border-l"
                    title="Table view"
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* Admin Controls */}
                {isAdmin(effectiveUser) && (
                  <div className="flex flex-wrap gap-2">
                    {/* Download Template Button */}
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        window.open(`${process.env.REACT_APP_BACKEND_URL}/dvds/template`, '_blank');
                      }}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Template
                    </Button>
                    
                    {/* Bulk Upload Button */}
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        document.getElementById('bulk-upload-input').click();
                      }}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Bulk Upload
                    </Button>
                    
                    {/* Add DVD Button */}
                    <Button 
                      size="sm"
                      className="bg-slate-700 hover:bg-slate-800"
                      onClick={() => {
                        setEditingItem(null);
                        setIsDialogOpen(true);
                        setDvdForm({
                          dvd_id: "", title: "", languages: "", actors: "", director: "", 
                          production_year: "", purchasing_year: "", category: "", area: ""
                        });
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add DVD
                    </Button>
                    
                    {/* Bulk Delete button */}
                    <Button 
                      variant="destructive"
                      size="sm"
                      onClick={handleBulkDeleteDVDs}
                      disabled={selectedDVDs.length === 0}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Selected ({selectedDVDs.length})
                    </Button>
                  </div>
                )}
              </div>
              
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>DVDs Collection</CardTitle>
                    {isAdmin(effectiveUser) && (
                      <Button
                        onClick={handleExportDVDs}
                        variant="outline"
                        className="flex items-center gap-2 text-green-700 border-green-300 hover:bg-green-50"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                        </svg>
                        Export DVDs to Excel
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Category Filter Buttons */}
                  <div className="mb-4 sm:mb-6">
                    <div className="flex flex-wrap gap-1 sm:gap-2">
                      <Button
                        size="sm"
                        variant={selectedDvdCategory === "" ? "default" : "outline"}
                        className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
                        onClick={() => setSelectedDvdCategory("")}
                      >
                        All Categories
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedDvdCategory === "Animation" ? "default" : "outline"}
                        className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
                        onClick={() => setSelectedDvdCategory("Animation")}
                      >
                        Animation
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedDvdCategory === "Action" ? "default" : "outline"}
                        className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
                        onClick={() => setSelectedDvdCategory("Action")}
                      >
                        Action
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedDvdCategory === "Comedy" ? "default" : "outline"}
                        className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
                        onClick={() => setSelectedDvdCategory("Comedy")}
                      >
                        Comedy
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedDvdCategory === "Documentary" ? "default" : "outline"}
                        className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
                        onClick={() => setSelectedDvdCategory("Documentary")}
                      >
                        Documentary
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedDvdCategory === "Erotic" ? "default" : "outline"}
                        className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
                        onClick={() => setSelectedDvdCategory("Erotic")}
                      >
                        Erotic
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedDvdCategory === "Science Fiction" ? "default" : "outline"}
                        className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
                        onClick={() => setSelectedDvdCategory("Science Fiction")}
                      >
                        Sci-Fi
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedDvdCategory === "Fantasy" ? "default" : "outline"}
                        className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
                        onClick={() => setSelectedDvdCategory("Fantasy")}
                      >
                        Fantasy
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedDvdCategory === "War" ? "default" : "outline"}
                        className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
                        onClick={() => setSelectedDvdCategory("War")}
                      >
                        War
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedDvdCategory === "Horror" ? "default" : "outline"}
                        className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
                        onClick={() => setSelectedDvdCategory("Horror")}
                      >
                        Horror
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedDvdCategory === "Masters of Cinema" ? "default" : "outline"}
                        className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
                        onClick={() => setSelectedDvdCategory("Masters of Cinema")}
                      >
                        Masters
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedDvdCategory === "Musical" ? "default" : "outline"}
                        className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
                        onClick={() => setSelectedDvdCategory("Musical")}
                      >
                        Musical
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedDvdCategory === "Political" ? "default" : "outline"}
                        className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
                        onClick={() => setSelectedDvdCategory("Political")}
                      >
                        Political
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedDvdCategory === "Historical" ? "default" : "outline"}
                        className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
                        onClick={() => setSelectedDvdCategory("Historical")}
                      >
                        Historical
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedDvdCategory === "TV series" ? "default" : "outline"}
                        className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
                        onClick={() => setSelectedDvdCategory("TV series")}
                      >
                        TV Series
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedDvdCategory === "Thriller" ? "default" : "outline"}
                        className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
                        onClick={() => setSelectedDvdCategory("Thriller")}
                      >
                        Thriller
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedDvdCategory === "Miscellaneous" ? "default" : "outline"}
                        className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
                        onClick={() => setSelectedDvdCategory("Miscellaneous")}
                      >
                        Misc
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedDvdCategory === "Western" ? "default" : "outline"}
                        className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
                        onClick={() => setSelectedDvdCategory("Western")}
                      >
                        Western
                      </Button>
                    </div>
                  </div>
                  
                  {/* DVDs Pagination Controls and Count */}
                  {Array.isArray(dvds) && dvds.length > 0 && (
                    <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        {/* DVD Count and Page Info */}
                        <div className="text-sm text-slate-600">
                          {showAllDvds ? (
                            <span>Showing all <strong>{dvds.length}</strong> DVDs</span>
                          ) : (
                            <span>
                              Showing <strong>{Math.min((dvdsCurrentPage - 1) * dvdsPerPage + 1, dvds.length)}</strong> to{' '}
                              <strong>{Math.min(dvdsCurrentPage * dvdsPerPage, dvds.length)}</strong> of{' '}
                              <strong>{dvds.length}</strong> DVDs
                              {getDvdsTotalPages() > 1 && (
                                <span className="ml-2 text-slate-500">
                                  (Page {dvdsCurrentPage} of {getDvdsTotalPages()})
                                </span>
                              )}
                            </span>
                          )}
                        </div>

                        {/* Items per page selector */}
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-slate-600">DVDs per page:</span>
                          <div className="flex gap-2">
                            {[20, 50, 100].map((size) => (
                              <Button
                                key={size}
                                size="sm"
                                variant={!showAllDvds && dvdsPerPage === size ? "default" : "outline"}
                                onClick={() => handleDvdsPageSizeChange(size)}
                                className="h-8 px-3 text-xs"
                              >
                                {size}
                              </Button>
                            ))}
                            <Button
                              size="sm"
                              variant={showAllDvds ? "default" : "outline"}
                              onClick={() => handleDvdsPageSizeChange('all')}
                              className="h-8 px-3 text-xs"
                            >
                              All
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Page Navigation */}
                      {!showAllDvds && getDvdsTotalPages() > 1 && (
                        <div className="mt-4 flex justify-center items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDvdsPageChange(dvdsCurrentPage - 1)}
                            disabled={dvdsCurrentPage === 1}
                            className="h-8 px-3"
                          >
                            ← Previous
                          </Button>
                          
                          {/* Page Numbers */}
                          <div className="flex gap-1">
                            {Array.from({ length: Math.min(5, getDvdsTotalPages()) }, (_, i) => {
                              let pageNum;
                              if (getDvdsTotalPages() <= 5) {
                                pageNum = i + 1;
                              } else if (dvdsCurrentPage <= 3) {
                                pageNum = i + 1;
                              } else if (dvdsCurrentPage >= getDvdsTotalPages() - 2) {
                                pageNum = getDvdsTotalPages() - 4 + i;
                              } else {
                                pageNum = dvdsCurrentPage - 2 + i;
                              }
                              
                              return (
                                <Button
                                  key={pageNum}
                                  size="sm"
                                  variant={dvdsCurrentPage === pageNum ? "default" : "outline"}
                                  onClick={() => handleDvdsPageChange(pageNum)}
                                  className="h-8 w-8 p-0 text-xs"
                                >
                                  {pageNum}
                                </Button>
                              );
                            })}
                          </div>
                          
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDvdsPageChange(dvdsCurrentPage + 1)}
                            disabled={dvdsCurrentPage === getDvdsTotalPages()}
                            className="h-8 px-3"
                          >
                            Next →
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {dvds.length === 0 ? (
                    <div className="text-center py-12">
                      <Film className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No DVDs found</p>
                    </div>
                  ) : (
                    <>
                      {viewMode === "table" ? (
                        /* Table View */
                        <div className="rounded-lg border border-slate-200 bg-white">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                {isAdmin(effectiveUser) && (
                                  <TableHead className="w-12">
                                    <input
                                      type="checkbox"
                                      checked={selectedDVDs.length === getPaginatedDvds().length && getPaginatedDvds().length > 0}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedDVDs(getPaginatedDvds().map(dvd => dvd.id));
                                        } else {
                                          setSelectedDVDs([]);
                                        }
                                      }}
                                      className="w-4 h-4 rounded border-slate-300"
                                    />
                                  </TableHead>
                                )}
                                <TableHead 
                                  className="cursor-pointer hover:bg-slate-50 select-none"
                                  onClick={() => handleSort('dvd_id', dvdsSortConfig, setDvdsSortConfig)}
                                >
                                  <div className="flex items-center gap-1">
                                    ID
                                    {dvdsSortConfig.key === 'dvd_id' && (
                                      dvdsSortConfig.direction === 'asc' ? (
                                        <ChevronUp className="h-4 w-4" />
                                      ) : (
                                        <ChevronDown className="h-4 w-4" />
                                      )
                                    )}
                                  </div>
                                </TableHead>
                                <TableHead>Cover</TableHead>
                                <TableHead 
                                  className="cursor-pointer hover:bg-slate-50 select-none"
                                  onClick={() => handleSort('title', dvdsSortConfig, setDvdsSortConfig)}
                                >
                                  <div className="flex items-center gap-1">
                                    Title
                                    {dvdsSortConfig.key === 'title' && (
                                      dvdsSortConfig.direction === 'asc' ? (
                                        <ChevronUp className="h-4 w-4" />
                                      ) : (
                                        <ChevronDown className="h-4 w-4" />
                                      )
                                    )}
                                  </div>
                                </TableHead>
                                <TableHead>Director</TableHead>
                                <TableHead>Languages</TableHead>
                                <TableHead>Category</TableHead>
                                <TableHead>Year</TableHead>
                                <TableHead>Status</TableHead>
                                {isAdmin(effectiveUser) && (
                                  <TableHead>Actions</TableHead>
                                )}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {getPaginatedDvds().map((dvd) => (
                                <TableRow key={dvd.id}>
                                  {isAdmin(effectiveUser) && (
                                    <TableCell className="w-12">
                                      <input
                                        type="checkbox"
                                        checked={selectedDVDs.includes(dvd.id)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedDVDs([...selectedDVDs, dvd.id]);
                                          } else {
                                            setSelectedDVDs(selectedDVDs.filter(id => id !== dvd.id));
                                          }
                                        }}
                                        className="w-4 h-4 rounded border-slate-300"
                                      />
                                    </TableCell>
                                  )}
                                  <TableCell className="font-medium">{dvd.dvd_id || dvd.id}</TableCell>
                                  <TableCell>
                                    <div className="relative w-12 h-16 bg-slate-100 rounded border overflow-hidden">
                                      {getDvdCoverUrl(dvd) ? (
                                        <>
                                          <img 
                                            src={getDvdCoverUrl(dvd)} 
                                            alt={`${dvd.title} cover`}
                                            className="w-full h-full object-cover"
                                            onError={(e) => handleDvdCoverError(e, dvd)}
                                          />
                                          <div className="absolute inset-0 bg-slate-200 flex items-center justify-center" style={{display: 'none'}}>
                                            <Film className="h-4 w-4 text-slate-400" />
                                          </div>
                                        </>
                                      ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                          <Film className="h-4 w-4 text-slate-400" />
                                        </div>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="font-semibold">{dvd.title}</TableCell>
                                  <TableCell>{dvd.director}</TableCell>
                                  <TableCell>{dvd.languages}</TableCell>
                                  <TableCell>
                                    <Badge variant="outline">{dvd.category}</Badge>
                                  </TableCell>
                                  <TableCell>{dvd.production_year}</TableCell>
                                  <TableCell>
                                    {loanStatuses[dvd.id]?.is_loaned ? (
                                      <div className="space-y-1">
                                        <Badge variant="destructive" className="text-xs">
                                          <Clock className="h-3 w-3 mr-1" />
                                          On Loan
                                        </Badge>
                                        <p className="text-xs text-slate-600">
                                          {loanStatuses[dvd.id].borrower}
                                        </p>
                                      </div>
                                    ) : (
                                      <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                                        Available
                                      </Badge>
                                    )}
                                  </TableCell>
                                  {isAdmin(effectiveUser) && (
                                    <TableCell>
                                      <div className="flex gap-1">
                                        <Button size="sm" variant="ghost" title="Edit DVD" onClick={() => handleEditDvd(dvd)}>
                                          <Edit className="h-4 w-4" />
                                        </Button>
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          title="Delete DVD"
                                          disabled={loanStatuses[dvd.id]?.is_loaned}
                                          onClick={() => handleDeleteDvd(dvd.id)}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        /* Card View */
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
                          {getPaginatedDvds().map((dvd) => (
                            <Card key={dvd.id} className="hover:shadow-lg transition-shadow">
                              <CardHeader className="pb-3">
                                <div className="flex justify-between items-start">
                                  <div className="flex items-center gap-2">
                                    <Film className="h-5 w-5 text-slate-600" />
                                    <div className="font-medium text-sm text-slate-500">
                                      #{dvd.dvd_id || dvd.id}
                                    </div>
                                  </div>
                                  {isAdmin(effectiveUser) && (
                                    <div className="flex gap-1">
                                      <Button size="sm" variant="ghost" title="Edit DVD" onClick={() => handleEditDvd(dvd)}>
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        title="Delete DVD"
                                        disabled={loanStatuses[dvd.id]?.is_loaned}
                                        onClick={() => handleDeleteDvd(dvd.id)}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                                <CardTitle className="text-lg">{dvd.title}</CardTitle>
                              </CardHeader>
                              <CardContent>
                                {/* DVD Cover Image */}
                                <div className="mb-4 flex justify-center">
                                  <div className="relative w-24 h-32 bg-slate-100 rounded-lg border overflow-hidden">
                                    {getDvdCoverUrl(dvd) ? (
                                      <>
                                        <img 
                                          src={getDvdCoverUrl(dvd)} 
                                          alt={`${dvd.title} cover`}
                                          className="w-full h-full object-cover"
                                          onError={(e) => handleDvdCoverError(e, dvd)}
                                        />
                                        <div className="absolute inset-0 bg-slate-200 flex items-center justify-center" style={{display: 'none'}}>
                                          <Film className="h-6 w-6 text-slate-400" />
                                        </div>
                                      </>
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center">
                                        <Film className="h-6 w-6 text-slate-400" />
                                      </div>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="space-y-3 text-sm">
                                  <div>
                                    <span className="font-medium text-slate-700">Director:</span>
                                    <p className="text-slate-600">{dvd.director}</p>
                                  </div>
                                  
                                  {dvd.actors && (
                                    <div>
                                      <span className="font-medium text-slate-700">Actors:</span>
                                      <p className="text-slate-600">{dvd.actors}</p>
                                    </div>
                                  )}
                                  
                                  <div className="flex gap-2 flex-wrap">
                                    {dvd.languages && <Badge variant="outline">{dvd.languages}</Badge>}
                                    {dvd.category && <Badge variant="secondary">{dvd.category}</Badge>}
                                    {dvd.production_year && <Badge variant="outline">{dvd.production_year}</Badge>}
                                  </div>

                                  {dvd.area && (
                                    <div>
                                      <span className="font-medium text-slate-700">Area:</span>
                                      <p className="text-slate-600">{dvd.area}</p>
                                    </div>
                                  )}

                                  {dvd.purchasing_year && (
                                    <div>
                                      <span className="font-medium text-slate-700">Purchased:</span>
                                      <p className="text-slate-600">{dvd.purchasing_year}</p>
                                    </div>
                                  )}

                                  {/* Loan Status */}
                                  {loanStatuses[dvd.id]?.is_loaned ? (
                                    <div className="bg-red-100 border border-red-300 rounded-lg p-2 mt-2">
                                      <div className="flex items-center gap-2">
                                        <Badge variant="destructive" className="text-xs">
                                          <Clock className="h-3 w-3 mr-1" />
                                          On Loan
                                        </Badge>
                                        <span className="text-sm text-red-700 font-medium">
                                          {loanStatuses[dvd.id].borrower}
                                        </span>
                                      </div>
                                      <div className="text-xs text-red-600 mt-1">
                                        Due: {new Date(loanStatuses[dvd.id].due_date).toLocaleDateString('en-GB')}
                                      </div>
                                    </div>
                                  ) : (
                                    <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                                      Available
                                    </Badge>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                      
                      {/* Load More DVDs Button */}
                      {dvdsHasMore && (
                        <div className="mt-6 pt-4 border-t border-slate-200 text-center">
                          <Button
                            onClick={() => fetchDvds(searchTerm, selectedDvdCategory, true)}
                            disabled={dvdsLoading}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-8"
                          >
                            {dvdsLoading ? 'Loading...' : `Load More DVDs (${dvds.length} of ${dvdsTotalCount})`}
                          </Button>
                          <p className="text-sm text-slate-500 mt-2">
                            Showing {dvds.length} of {dvdsTotalCount} DVDs
                          </p>
                        </div>
                      )}
                      
                      {/* Bottom DVD Pagination Controls */}
                      {Array.isArray(dvds) && dvds.length > 0 && !showAllDvds && getDvdsTotalPages() > 1 && (
                        <div className="mt-6 pt-4 border-t border-slate-200">
                          <div className="flex justify-center items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDvdsPageChange(dvdsCurrentPage - 1)}
                              disabled={dvdsCurrentPage === 1}
                              className="h-8 px-3"
                            >
                              ← Previous
                            </Button>
                            
                            <span className="text-sm text-slate-600 mx-4">
                              Page {dvdsCurrentPage} of {getDvdsTotalPages()}
                            </span>
                            
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDvdsPageChange(dvdsCurrentPage + 1)}
                              disabled={dvdsCurrentPage === getDvdsTotalPages()}
                              className="h-8 px-3"
                            >
                              Next →
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Events Dashboard - Enhanced Cultural Events Landing Page */}
            <TabsContent value="events">
              {/* Dashboard Header */}
              <div className="mb-6" id="cultural-events-dashboard-top">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-3">
                      <Calendar className="h-8 w-8 text-blue-600" />
                      Cultural Events Dashboard
                    </h1>
                    <p className="text-gray-600 mt-2">Discover amazing cultural experiences curated for ACE members</p>
                    
                    {/* Renewal Button (available from September onwards) - membership status now shown in top-right user menu */}
                    {currentUser?.can_renew && (
                      <div className="mt-3">
                        <Button 
                          size="sm" 
                          onClick={renewMembership}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          <CreditCard className="h-4 w-4 mr-2" />
                          Renew for {currentUser.membership_year + 1} (€35)
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* Quick Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    {/* My Bookings button - only for regular users, not administrators */}
                    {effectiveUser?.role !== 'Administrator' && (
                      <Button 
                        onClick={async () => {
                          await fetchUserBookings();
                          setShowBookingHistory(true);
                        }}
                        variant="outline" 
                        className="flex items-center gap-2"
                      >
                        <Clock className="h-4 w-4" />
                        My Bookings
                      </Button>
                    )}

                    {/* My Past Events button — opens the dedicated tab listing events the user attended */}
                    {effectiveUser?.role !== 'Administrator' && (
                      <Button
                        onClick={() => {
                          fetchUserBookings();
                          setCurrentTab('my-past-events');
                          setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 80);
                        }}
                        variant="outline"
                        className="flex items-center gap-2 border-purple-200 text-purple-700 hover:bg-purple-50"
                        data-testid="my-past-events-btn"
                      >
                        <Clock className="h-4 w-4" />
                        My Past Events
                      </Button>
                    )}

                    {/* My Loans button - only for regular users, not administrators */}
                    {effectiveUser?.role !== 'Administrator' && (
                      <Button 
                        onClick={async () => {
                          await fetchUserLoans();
                          setShowLoanHistory(true);
                        }}
                        variant="outline" 
                        className="flex items-center gap-2"
                      >
                        <Library className="h-4 w-4" />
                        My Loans
                      </Button>
                    )}
                    {isAdmin(effectiveUser) && (
                      <Button 
                        onClick={() => {
                          setCurrentTab('events');
                          setIsDialogOpen(true);
                          setEditingItem(null);
                        }}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="h-4 w-4" />
                        New Event
                      </Button>
                    )}
                  </div>
                </div>

                {/* Upcoming Events Preview */}
                {isAdmin(effectiveUser) && getUpcomingEvents().length > 0 && (
                  <div className="mb-6">
                    <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                      <CardHeader>
                        <CardTitle className="text-lg font-semibold text-blue-900 flex items-center gap-2">
                          <Calendar className="h-5 w-5" />
                          Upcoming Bookable Events
                          <Badge variant="secondary" className="ml-2">{getUpcomingEvents().length}</Badge>
                          {isAdmin(effectiveUser) && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="ml-auto h-8 gap-1.5 border-indigo-200 bg-white/70 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800"
                              onClick={() => {
                                fetchEventAvailability(getUpcomingEvents());
                                setIsCapacityChartOpen(true);
                              }}
                              data-testid="open-capacity-chart-btn"
                              title="View capacity vs. tickets-left chart for all upcoming events"
                            >
                              <BarChart3 className="h-4 w-4" />
                              <span className="hidden sm:inline">Statistics</span>
                            </Button>
                          )}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {getUpcomingEvents().map((event) => (
                            <Card key={event.id} className="hover:shadow-md transition-shadow cursor-pointer border border-gray-200">
                              <CardContent className="p-4">
                                <div className="flex items-start justify-between mb-2">
                                  <Badge variant="secondary" className="text-xs">
                                    {formatEventDate(event.event_date)}
                                  </Badge>
                                  <div className="flex gap-1">
                                    <Badge 
                                      variant={event.status === 'published' ? 'default' : 'outline'}
                                      className="text-xs"
                                    >
                                      {event.status}
                                    </Badge>
                                    {/* Show capacity info for admins */}
                                    {isAdmin(effectiveUser) && event.max_capacity && (() => {
                                      const remaining = eventAvailability[event.id]?.remaining_tickets;
                                      const remainingDisplay = remaining ?? event.max_capacity;
                                      return (
                                        <>
                                          <Badge
                                            variant="outline"
                                            className="text-xs bg-yellow-50 text-yellow-800 border-yellow-200"
                                          >
                                            Cap: {event.max_capacity}
                                          </Badge>
                                          <Badge
                                            variant="outline"
                                            className={`text-xs ${remainingDisplay === 0 ?
                                              'bg-red-50 text-red-800 border-red-200' :
                                              remainingDisplay <= 5 ?
                                              'bg-orange-50 text-orange-800 border-orange-200' :
                                              'bg-green-50 text-green-800 border-green-200'}`}
                                          >
                                            Left: {remainingDisplay}
                                          </Badge>
                                        </>
                                      );
                                    })()}
                                  </div>
                                </div>
                                <h3 className="font-semibold text-sm mb-2 leading-tight">
                                  {event.title}
                                </h3>
                                
                                {/* Low ticket warning — admin only on dashboard (members see count inside booking dialog) */}
                                {isAdmin(effectiveUser) &&
                                 eventAvailability[event.id]?.remaining_tickets != null && 
                                 eventAvailability[event.id].remaining_tickets > 0 && 
                                 eventAvailability[event.id].remaining_tickets <= 5 && (
                                  <div className="flex items-center gap-1 mb-2" data-testid={`low-ticket-warning-upcoming-${event.id}`}>
                                    <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-xs flex items-center gap-1">
                                      <AlertCircle className="h-3 w-3" />
                                      Only {eventAvailability[event.id].remaining_tickets} ticket{eventAvailability[event.id].remaining_tickets !== 1 ? 's' : ''} left!
                                    </Badge>
                                  </div>
                                )}
                                {/* Sold out badge for all users */}
                                {eventAvailability[event.id]?.is_sold_out && (
                                  <div className="flex items-center gap-1 mb-2" data-testid={`sold-out-badge-upcoming-${event.id}`}>
                                    <Badge className="bg-red-100 text-red-800 border-red-300 text-xs">
                                      Sold Out
                                    </Badge>
                                  </div>
                                )}

                                {/* Event Pricing Information */}
                                <div className="mb-3">
                                  <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                    💰 {getEventPricing(event)}
                                  </Badge>
                                </div>
                                
                                <div className="flex gap-2 flex-wrap">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleViewEvent(event)}
                                    className="flex items-center gap-1 text-xs"
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                    View
                                  </Button>
                                  {/* Admin: Edit event */}
                                  {isAdmin(effectiveUser) && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setEditingItem(event);
                                        setEventForm({
                                          title: event.title,
                                          description_text: event.description_text,
                                          description_html: event.description_html || "",
                                          event_date: toLocalDateTimeInput(event.event_date),
                                          booking_deadline: toLocalDateTimeInput(event.booking_deadline),
                                          organiser_ids: event.organiser_ids || [],
                                          venue_ids: event.venue_ids || [],
                                          category_tags: event.category_tags || [],
                                          event_label: event.event_label || "",
                                          status: event.status || "draft",
                                          max_capacity: event.max_capacity || "",
                                          guided_visit: event.guided_visit || false,
                                          guest_tickets_enabled: event.guest_tickets_enabled || false,
                                          guest_surcharge: event.guest_surcharge || "",
                                          comment: event.comment || "",
                                          pricing_type: event.pricing_type === "custom_rows"
                                            ? "custom_rows"
                                            : ((event.matrix_pricing && (event.matrix_pricing.member_pricing_enabled || event.matrix_pricing.galleria_enabled || event.matrix_pricing.platea_enabled)) ? "matrix" : "standard"),
                                          custom_price_rows: Array.isArray(event.custom_price_rows) && event.custom_price_rows.length > 0
                                            ? event.custom_price_rows.map(r => ({ id: r.id, label: r.label, amount: String(r.amount ?? ""), capacity: r.capacity == null ? "" : String(r.capacity), minors_only: !!r.minors_only }))
                                            : [{ id: null, label: "Adult", amount: "" }],
                                          price_categories: event.price_categories || { member_price: "", minor_price: "", senior_price: "", young_adult_price: "", member_price_enabled: false, minor_price_enabled: false, senior_price_enabled: false, young_adult_price_enabled: false },
                                          matrix_pricing: event.matrix_pricing || { member_pricing: {}, minor_pricing: {}, senior_pricing: {}, young_adult_pricing: {}, member_pricing_enabled: false, minor_pricing_enabled: false, senior_pricing_enabled: false, young_adult_pricing_enabled: false, galleria_enabled: false, platea_enabled: false },
                                          seat_types: event.seat_types || []
                                        });
                                        setEventImagePreview(null);
                                        setIsEditFormReady(true);
                                        setIsDialogOpen(true);
                                      }}
                                      className="flex items-center gap-1 text-xs text-blue-600 hover:bg-blue-50 border-blue-200"
                                      data-testid={`edit-upcoming-${event.id}`}
                                    >
                                      <Edit className="h-3 w-3" />
                                      Edit
                                    </Button>
                                  )}
                                  {/* Admin: View Bookings */}
                                  {isAdmin(effectiveUser) && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleViewEventBookings(event)}
                                      className="flex items-center gap-1 text-xs text-purple-600 hover:bg-purple-50 border-purple-200"
                                      data-testid={`view-bookings-upcoming-${event.id}`}
                                    >
                                      <Users className="h-3 w-3" />
                                      Bookings
                                    </Button>
                                  )}
                                  {/* Admin: Manual booking (creates a payment link for a member) */}
                                  {isAdmin(effectiveUser) && event.status !== 'closed' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setManualBookingEvent(event)}
                                      className="flex items-center gap-1 text-xs text-indigo-700 hover:bg-indigo-50 border-indigo-200"
                                      data-testid={`manual-booking-upcoming-${event.id}`}
                                      title="Create a manual booking and email a payment link to a member"
                                    >
                                      <Mail className="h-3 w-3" />
                                      Manual
                                    </Button>
                                  )}
                                  {isAdmin(effectiveUser) && <CopyEventLinkButton event={event} />}
                                  {/* Admin: Waitlist (only shown when at least 1 entry) */}
                                  {isAdmin(effectiveUser) && (waitlistCounts[event.id] || 0) > 0 && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setWaitlistDialogEvent(event)}
                                      className="flex items-center gap-1 text-xs text-blue-700 hover:bg-blue-50 border-blue-300"
                                      data-testid={`view-waitlist-upcoming-${event.id}`}
                                      title="View the waiting list for this event"
                                    >
                                      <Clock className="h-3 w-3" />
                                      Waitlist ({waitlistCounts[event.id]})
                                    </Button>
                                  )}
                                  {/* Admin controls */}
                                  {isAdmin(effectiveUser) && event.status !== 'closed' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => closeEventBookings(event.id, event.title)}
                                      className="text-red-600 hover:bg-red-50 border-red-200 p-1 h-7 w-7"
                                      title="Close Bookings for this Event"
                                    >
                                      <Ban className="h-3 w-3" />
                                    </Button>
                                  )}
                                  {/* Show status if closed with reopen option for admins */}
                                  {isAdmin(effectiveUser) && event.status === 'closed' && (
                                    <div className="flex items-center gap-1">
                                      <Badge variant="destructive" className="text-xs">
                                        Closed
                                      </Badge>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => reopenEventBookings(event.id, event.title)}
                                        className="text-green-600 hover:bg-green-50 border-green-200 p-1 h-7 w-7"
                                        title="Reopen Bookings for this Event"
                                      >
                                        <RotateCcw className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  )}
                                  {/* Book button - only visible for regular users, not admins */}
                                  {effectiveUser?.role !== 'Administrator' && event.status !== 'closed' && (
                                    pendingBookingsByEvent[event.id] ? (
                                      <Button
                                        size="sm"
                                        onClick={() => { window.location.href = pendingBookingsByEvent[event.id].checkout_url; }}
                                        className="flex items-center gap-1 text-xs bg-amber-500 hover:bg-amber-600 text-white"
                                        title="You have a pending checkout. Click to complete payment."
                                        data-testid={`resume-payment-upcoming-${event.id}`}
                                      >
                                        <Clock className="h-3 w-3" />
                                        Resume payment
                                      </Button>
                                    ) : bookedEventIds.includes(event.id) ? (
                                      <Badge className="bg-blue-100 text-blue-800 border-blue-200 px-2 py-1 text-xs flex items-center gap-1" data-testid={`booked-badge-upcoming-${event.id}`}>
                                        <CheckCircle className="h-3 w-3" /> Booked
                                      </Badge>
                                    ) : eventAvailability[event.id]?.is_sold_out ? (
                                      <Button
                                        size="sm"
                                        onClick={() => {
                                          handleSophisticatedBookEvent(event);
                                        }}
                                        className="flex items-center gap-1 text-xs bg-orange-600 hover:bg-orange-700"
                                        data-testid={`join-waitlist-upcoming-${event.id}`}
                                      >
                                        <Clock className="h-3 w-3" />
                                        Waiting List
                                      </Button>
                                    ) : (
                                      <Button
                                        size="sm"
                                        onClick={() => {
                                          handleSophisticatedBookEvent(event);
                                        }}
                                        className="flex items-center gap-1 text-xs bg-blue-600 hover:bg-blue-700"
                                        data-testid={`book-upcoming-${event.id}`}
                                      >
                                        <Calendar className="h-3 w-3" />
                                        Book
                                      </Button>
                                    )
                                  )}
                                  {/* Show booking closed message for regular users */}
                                  {effectiveUser?.role !== 'Administrator' && event.status === 'closed' && (
                                    <Badge variant="outline" className="text-xs text-gray-500">
                                      Closed
                                    </Badge>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Quick Stats — admin only */}
                {isAdmin(effectiveUser) && (
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                  <Card className="text-center p-4">
                    <div className="text-2xl font-bold text-blue-600">{events.length}</div>
                    <div className="text-sm text-gray-600">Total Events</div>
                  </Card>
                  <Card className="text-center p-4">
                    <div className="text-2xl font-bold text-green-600">
                      {events.filter(event => new Date(event.event_date) >= new Date()).length}
                    </div>
                    <div className="text-sm text-gray-600">Upcoming</div>
                  </Card>
                  <Card className="text-center p-4">
                    <div className="text-2xl font-bold text-red-600">
                      {events.filter(event => new Date(event.event_date) < new Date()).length}
                    </div>
                    <div className="text-sm text-gray-600">Past Events</div>
                  </Card>
                  <Card className="text-center p-4">
                    <div className="text-2xl font-bold text-purple-600">
                      {isAdmin(effectiveUser) ?
                        // Sum booked tickets across events that have backend availability data
                        events.reduce((total, ev) => {
                          const avail = eventAvailability[ev.id];
                          if (avail && ev.max_capacity != null && typeof avail.remaining_tickets === 'number') {
                            return total + Math.max(0, ev.max_capacity - avail.remaining_tickets);
                          }
                          return total;
                        }, 0) :
                        (userBookings.length || 0)
                      }
                    </div>
                    <div className="text-sm text-gray-600">
                      {isAdmin(effectiveUser) ? 'Total Bookings' : 'My Bookings'}
                    </div>
                  </Card>
                  <Card className="text-center p-4">
                    <div className="text-2xl font-bold text-orange-600">{organisers.length}</div>
                    <div className="text-sm text-gray-600">Organizers</div>
                  </Card>
                </div>
                )}
              </div>

              {/* Archived Events Section — admin only */}
              {isAdmin(effectiveUser) && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 id="all-cultural-events" className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                    <List className="h-5 w-5" />
                    Archived Events of the Year
                  </h2>
                  <div className="text-sm text-gray-500">
                    {getArchivedEvents().length} past events
                  </div>
                </div>
              </div>
              )}
              
              {/* Member-only Upcoming Events — same image-rich card grid as the archived section */}
              {!isAdmin(effectiveUser) && getUpcomingEvents().length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 id="member-upcoming-events" className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-blue-600" />
                      Upcoming Events
                    </h2>
                    <div className="text-sm text-gray-500">
                      {getUpcomingEvents().length} upcoming
                    </div>
                  </div>
                </div>
              )}

              <div className="relative">
                {/* Loading Overlay */}
                {isDataLoading && currentTab === 'events' && (
                  <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-700"></div>
                      <span className="text-slate-600">Loading cultural events...</span>
                    </div>
                  </div>
                )}
                <EventsTabContent 
                  events={isAdmin(effectiveUser) ? getArchivedEvents() : getUpcomingEvents()}
                  organisers={organisers}
                  venues={venues}
                  currentUser={effectiveUser}
                  bookedEventIds={bookedEventIds}
                  pendingBookingsByEvent={pendingBookingsByEvent}
                  eventAvailability={eventAvailability}
                  onViewEventBookings={handleViewEventBookings}
                  viewEvent={handleViewEvent}
                  editEvent={(event) => {
                    setEditingItem(event);
                    setEventForm({
                      title: event.title,
                      description_text: event.description_text,
                      description_html: event.description_html || "",
                      event_date: toLocalDateTimeInput(event.event_date),
                      booking_deadline: toLocalDateTimeInput(event.booking_deadline),
                      organiser_ids: event.organiser_ids || [],
                      venue_ids: event.venue_ids || [],
                      category_tags: event.category_tags || [],
                      event_label: event.event_label || "",
                      status: event.status || "draft",
                      max_capacity: event.max_capacity || "",
                      guided_visit: event.guided_visit || false,
                      guest_tickets_enabled: event.guest_tickets_enabled || false,
                      guest_surcharge: event.guest_surcharge || "",
                      comment: event.comment || "",
                      // Determine pricing type based on existing event data - simplified logic
                      pricing_type: (() => {
                        // New custom_rows model wins if the backend flags it
                        if (event.pricing_type === "custom_rows") return "custom_rows";
                        // Check if event has matrix pricing with any enabled fields
                        const hasEnabledMatrix = event.matrix_pricing && (
                          event.matrix_pricing.member_pricing_enabled || 
                          event.matrix_pricing.minor_pricing_enabled || 
                          event.matrix_pricing.senior_pricing_enabled || 
                          event.matrix_pricing.young_adult_pricing_enabled ||
                          event.matrix_pricing.galleria_enabled ||
                          event.matrix_pricing.platea_enabled
                        );
                        
                        // Check if event has standard pricing (price_categories with enabled fields)
                        const hasEnabledStandard = event.price_categories && (
                          event.price_categories.member_price_enabled ||
                          event.price_categories.minor_price_enabled ||
                          event.price_categories.senior_price_enabled ||
                          event.price_categories.young_adult_price_enabled
                        );
                        
                        // Determine pricing type - prioritize what's actually enabled
                        const pricingType = hasEnabledMatrix ? "matrix" : "standard";
                        
                        console.log('🔍 PRICING TYPE DETERMINATION (Enhanced):', {
                          event_title: event.title?.substring(0, 30) + "...",
                          has_matrix_pricing: !!event.matrix_pricing,
                          has_enabled_matrix: hasEnabledMatrix,
                          has_price_categories: !!event.price_categories,
                          has_enabled_standard: hasEnabledStandard,
                          matrix_data_sample: event.matrix_pricing ? Object.keys(event.matrix_pricing) : null,
                          price_categories_sample: event.price_categories ? Object.keys(event.price_categories) : null,
                          final_pricing_type: pricingType
                        });
                        return pricingType;
                      })(),
                      // Populate custom rows when editing a custom_rows event
                      custom_price_rows: Array.isArray(event.custom_price_rows) && event.custom_price_rows.length > 0
                        ? event.custom_price_rows.map(r => ({ id: r.id, label: r.label, amount: String(r.amount ?? ""), capacity: r.capacity == null ? "" : String(r.capacity), minors_only: !!r.minors_only }))
                        : [{ id: null, label: "Adult", amount: "" }],
                      // Initialize with pricing structure from backend data
                      price_categories: event.price_categories ? {
                        // Use existing price_categories data - ensure proper type conversion
                        member_price: (() => {
                          const price = event.price_categories.member_price;
                          const converted = price != null ? String(price) : "";
                          console.log('🔍 EDIT FORM INITIALIZATION:', {
                            original_price: price,
                            price_type: typeof price,
                            is_null: price === null,
                            is_undefined: price === undefined,
                            converted_value: converted
                          });
                          return converted;
                        })(),
                        minor_price: String(event.price_categories.minor_price || ""),
                        senior_price: String(event.price_categories.senior_price || ""),
                        young_adult_price: String(event.price_categories.young_adult_price || ""),
                        member_price_enabled: Boolean(event.price_categories.member_price_enabled),
                        minor_price_enabled: Boolean(event.price_categories.minor_price_enabled),
                        senior_price_enabled: Boolean(event.price_categories.senior_price_enabled),
                        young_adult_price_enabled: Boolean(event.price_categories.young_adult_price_enabled)
                      } : {
                        // Fallback to legacy fields if no price_categories
                        member_price: String(event.member_price || event.cost_euro_full || ""),
                        minor_price: String(event.minor_price || event.cost_euro_reduced || ""),
                        senior_price: String(event.senior_price || ""),
                        young_adult_price: String(event.young_adult_price || ""),
                        member_price_enabled: Boolean(event.member_price || event.cost_euro_full),
                        minor_price_enabled: Boolean(event.minor_price || event.cost_euro_reduced),
                        senior_price_enabled: Boolean(event.senior_price),
                        young_adult_price_enabled: Boolean(event.young_adult_price)
                      },
                      // Initialize matrix pricing if it exists
                      matrix_pricing: event.matrix_pricing || {
                        member_pricing: { galleria_price: "", platea_price: "" },
                        minor_pricing: { galleria_price: "", platea_price: "" },
                        senior_pricing: { galleria_price: "", platea_price: "" },
                        young_adult_pricing: { galleria_price: "", platea_price: "" },
                        member_pricing_enabled: false,
                        minor_pricing_enabled: false,
                        senior_pricing_enabled: false,
                        young_adult_pricing_enabled: false,
                        galleria_enabled: false,
                        platea_enabled: false
                      },
                      seat_types: event.seat_types || []
                    });
                    
                    // Set existing event image preview if it exists
                    if ((event.images && event.images.length > 0) || (event.images_s3 && event.images_s3.length > 0)) {
                      const imageUrl = getEventImageUrlForForm(event);
                      setEventImagePreview(imageUrl);
                    } else {
                      setEventImagePreview(null);
                    }
                    
                    // Mark form as ready and open dialog
                    setIsEditFormReady(true);
                    setIsDialogOpen(true);
                  }}
                  deleteEvent={async (eventId) => {
                    try {
                      await axios.delete(`/events/${eventId}`);
                      toast.success("Event deleted successfully!");
                      await fetchEvents(searchTerm, selectedCategory);
                    } catch (error) {
                      const errorMessage = error.response?.data?.detail || 'Failed to delete event';
                      toast.error(errorMessage);
                    }
                  }}
                  viewMode={viewMode}
                  setViewMode={setViewMode}
                  onBookEvent={handleSophisticatedBookEvent}
                />
              </div>

              {/* Member-facing Season Pass browse, below Upcoming Events (auto-hides for admins & when no passes are on sale) */}
              <div className="mt-8" data-testid="season-pass-section-below-events">
                <SeasonPassBrowse currentUser={effectiveUser} />
              </div>
            </TabsContent>

            {/* My Past Events — member-only */}
            {!isAdmin(effectiveUser) && (
              <TabsContent value="my-past-events">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Clock className="h-5 w-5 text-purple-700" />
                          My Past Events
                        </CardTitle>
                        <CardDescription>
                          Events you have attended in the past 12 months (paid bookings only).
                        </CardDescription>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentTab('events')}
                        data-testid="back-to-events-btn"
                      >
                        <ChevronRight className="h-4 w-4 rotate-180" />
                        Back to Events
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {(() => {
                      // Build a list of past events for which the current user has a paid booking
                      const now = new Date();
                      const paidEventIds = new Set(
                        (userBookings || [])
                          .filter((b) => ['paid', 'confirmed'].includes((b.payment_status || '').toLowerCase()))
                          .map((b) => b.event_id)
                      );
                      const myPastEvents = (events || [])
                        .filter((e) => paidEventIds.has(e.id) && new Date(e.event_date) < now)
                        .sort((a, b) => new Date(b.event_date) - new Date(a.event_date));

                      if (myPastEvents.length === 0) {
                        return (
                          <div className="text-center py-12">
                            <Clock className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                            <p className="text-slate-500">You haven&apos;t attended any past events yet.</p>
                            <p className="text-xs text-slate-400 mt-1">
                              Once you&apos;ve booked and the event date has passed, it will appear here.
                            </p>
                          </div>
                        );
                      }

                      return (
                        <EventsTabContent
                          events={myPastEvents}
                          organisers={organisers}
                          venues={venues}
                          currentUser={effectiveUser}
                          bookedEventIds={bookedEventIds}
                          pendingBookingsByEvent={pendingBookingsByEvent}
                          eventAvailability={eventAvailability}
                          onViewEventBookings={handleViewEventBookings}
                          viewEvent={handleViewEvent}
                          editEvent={() => {}}
                          deleteEvent={() => {}}
                          viewMode={viewMode}
                          setViewMode={setViewMode}
                          onBookEvent={handleSophisticatedBookEvent}
                        />
                      );
                    })()}
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            {/* Admin-only content */}
            {isAdmin(effectiveUser) && (
              <>
                <TabsContent value="users">
                  <Card>
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <CardTitle>Users Management</CardTitle>
                        <div className="flex items-center gap-2">
                          {/* Membership stats badges — grouped by year */}
                          {(() => {
                            // Bucket every user by their membership_year and
                            // count internal/external per bucket. Users with
                            // no year land in "—".
                            const groups = {};
                            for (const u of users) {
                              const ys = u.membership_years?.length ? u.membership_years : [u.membership_year || "—"];
                              const cat = (u.member_category || "").toLowerCase();
                              for (const y of ys) {
                                if (!groups[y]) groups[y] = { internal: 0, external: 0 };
                                if (cat.includes("internal")) groups[y].internal += 1;
                                else if (cat.includes("external")) groups[y].external += 1;
                              }
                            }
                            const years = Object.keys(groups).sort((a, b) => {
                              if (a === "—") return 1;
                              if (b === "—") return -1;
                              return Number(b) - Number(a); // newest first
                            });
                            return (
                              <div
                                className="hidden md:flex items-center gap-1.5 mr-2 flex-wrap"
                                data-testid="users-stats-badges"
                              >
                                {years.map((y) => {
                                  const g = groups[y];
                                  const denom = g.internal + g.external;
                                  const pctExt = denom > 0 ? Math.round((g.external / denom) * 100) : 0;
                                  return (
                                    <div
                                      key={y}
                                      className="flex items-center gap-1 px-2 py-1 rounded-md bg-slate-50 border border-slate-200"
                                      data-testid={`users-stats-year-${y}`}
                                    >
                                      <span className="text-[11px] font-semibold text-slate-700">{y}</span>
                                      <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-800 text-[10px] py-0 px-1.5">
                                        Int {g.internal}
                                      </Badge>
                                      <Badge variant="outline" className="bg-blue-50 border-blue-200 text-blue-800 text-[10px] py-0 px-1.5">
                                        Ext {g.external}
                                      </Badge>
                                      <Badge variant="outline" className="bg-purple-50 border-purple-200 text-purple-800 text-[10px] py-0 px-1.5">
                                        {pctExt}%
                                      </Badge>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                          <ResendRenewedCardsButton />
                          {/* View Toggle */}
                          <div className="flex items-center bg-slate-100 rounded-md p-1">
                            <Button
                              size="sm"
                              variant={usersViewMode === 'cards' ? 'default' : 'ghost'}
                              onClick={() => setUsersViewMode('cards')}
                              className="px-3 py-1 text-xs"
                            >
                              <Grid className="h-3 w-3 mr-1" />
                              Cards
                            </Button>
                            <Button
                              size="sm"
                              variant={usersViewMode === 'table' ? 'default' : 'ghost'}
                              onClick={() => setUsersViewMode('table')}
                              className="px-3 py-1 text-xs"
                            >
                              <List className="h-3 w-3 mr-1" />
                              Table
                            </Button>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="ml-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border-emerald-200 text-xs px-3 py-1"
                            onClick={() => setShowLoginReport(true)}
                            data-testid="open-login-report"
                          >
                            <LogIn className="h-3 w-3 mr-1" />
                            Login Report
                          </Button>
                        </div>
                      </div>
                      {/* Mobile-only stats row — grouped by year */}
                      {(() => {
                        const groups = {};
                        for (const u of users) {
                          const ys = u.membership_years?.length ? u.membership_years : [u.membership_year || "—"];
                          const cat = (u.member_category || "").toLowerCase();
                          for (const y of ys) {
                            if (!groups[y]) groups[y] = { internal: 0, external: 0 };
                            if (cat.includes("internal")) groups[y].internal += 1;
                            else if (cat.includes("external")) groups[y].external += 1;
                          }
                        }
                        const years = Object.keys(groups).sort((a, b) => {
                          if (a === "—") return 1;
                          if (b === "—") return -1;
                          return Number(b) - Number(a);
                        });
                        return (
                          <div
                            className="md:hidden flex flex-col gap-1.5 mt-2"
                            data-testid="users-stats-badges-mobile"
                          >
                            {years.map((y) => {
                              const g = groups[y];
                              const denom = g.internal + g.external;
                              const pctExt = denom > 0 ? Math.round((g.external / denom) * 100) : 0;
                              return (
                                <div key={y} className="flex flex-wrap items-center gap-1.5">
                                  <span className="text-[11px] font-semibold text-slate-700">{y}:</span>
                                  <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-800 text-xs">
                                    Int {g.internal}
                                  </Badge>
                                  <Badge variant="outline" className="bg-blue-50 border-blue-200 text-blue-800 text-xs">
                                    Ext {g.external}
                                  </Badge>
                                  <Badge variant="outline" className="bg-purple-50 border-purple-200 text-purple-800 text-xs">
                                    {pctExt}%
                                  </Badge>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </CardHeader>
                    <CardContent>
                      {/* Search field above the registered-members table */}
                      <div className="mb-4 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                        <Input
                          value={usersSearchTerm}
                          onChange={(e) => setUsersSearchTerm(e.target.value)}
                          placeholder="Search by Member No., last name or email…"
                          className="pl-9 max-w-md"
                          data-testid="users-search-input"
                        />
                        {usersSearchTerm && (
                          <button
                            type="button"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-500 hover:text-slate-700"
                            onClick={() => setUsersSearchTerm("")}
                            data-testid="users-search-clear"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      {users.length === 0 ? (
                        <div className="text-center py-12">
                          <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                          <p className="text-slate-500">No users found</p>
                        </div>
                      ) : (() => {
                        const q = usersSearchTerm.trim().toLowerCase();
                        const filteredUsers = q
                          ? users.filter((u) => {
                              const mn = (u.member_number || '').toString().toLowerCase();
                              const ln = (u.last_name || '').toLowerCase();
                              const em = (u.email || '').toLowerCase();
                              return mn.includes(q) || ln.includes(q) || em.includes(q);
                            })
                          : users;
                        if (filteredUsers.length === 0) {
                          return (
                            <div className="text-center py-12">
                              <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                              <p className="text-slate-500">No users match "{usersSearchTerm}"</p>
                            </div>
                          );
                        }
                        return (
                          <UsersTabContent
                            users={filteredUsers}
                            currentUser={currentUser}
                            editUser={handleEditUserForComponent}
                            deleteUser={handleDeleteUserForComponent}
                            promoteUser={handlePromoteUserForComponent}
                            onViewPaymentHistory={(user) =>
                              setPaymentHistoryTarget({
                                userId: user.id,
                                name: `${user.first_name_1 || ""} ${user.last_name || ""}`.trim(),
                              })
                            }
                            onUpdateRoles={async (userId, roles) => {
                              try {
                                await axios.put(`users/${userId}/admin-roles`, { admin_roles: roles });
                                toast.success('Admin roles updated');
                                fetchUsers();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Failed to update roles');
                              }
                            }}
                            viewMode={usersViewMode}
                            onViewModeChange={setUsersViewMode}
                          />
                        );
                      })()}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="loans">
                  <LoansTabContent 
                    loans={loans}
                    users={users}
                    handleReturnLoan={handleReturnLoan}
                    handleClearLoanHistory={handleClearLoanHistory}
                    handleExportLoanHistory={handleExportLoanHistory}
                    currentUser={currentUser}
                    loanExportFilters={loanExportFilters}
                    setLoanExportFilters={setLoanExportFilters}
                    showLoanExportDialog={showLoanExportDialog}
                    setShowLoanExportDialog={setShowLoanExportDialog}
                    onOpenAdminLoanManagement={() => setIsAdminLoanManagementOpen(true)}
                  />
                </TabsContent>

                <TabsContent value="financials">
                  <EventFinancialManagement />
                </TabsContent>

              </>
            )}
          </Tabs>
        </div>

        <Toaster 
          position="top-center"
          toastOptions={{
            style: { fontSize: '16px', padding: '16px 24px', maxWidth: '500px' },
            error: {
              style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '2px solid #dc2626', fontSize: '16px', fontWeight: '600', padding: '16px 24px', maxWidth: '500px' },
            },
          }}
        />
        
        {/* Dynamic Add/Edit Dialog for Admin */}
        <Dialog open={isDialogOpen} onOpenChange={closeDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {currentTab === "books" && <Book className="h-5 w-5" />}
                {currentTab === "cds" && <Music className="h-5 w-5" />}
                {currentTab === "dvds" && <Film className="h-5 w-5" />}
                {currentTab === "users" && <User className="h-5 w-5" />}
                {currentTab === "loans" && <Calendar className="h-5 w-5" />}
                {currentTab === "events" && <Calendar className="h-5 w-5" />}
                {editingItem ? 
                  `Edit ${currentTab === "books" ? "Book" : currentTab === "cds" ? "CD" : currentTab === "dvds" ? "DVD" : currentTab === "users" ? "User" : currentTab === "loans" ? "Loan" : currentTab === "events" ? "Event" : currentTab === "organiser" ? "Organiser" : currentTab === "venue" ? "Venue" : "Item"}` : 
                  `Add ${currentTab === "books" ? "Book" : currentTab === "cds" ? "CD" : currentTab === "dvds" ? "DVD" : currentTab === "users" ? "User" : currentTab === "loans" ? "Loan" : currentTab === "events" ? "Event" : currentTab === "organiser" ? "Organiser" : currentTab === "venue" ? "Venue" : "Item"}`
                }
              </DialogTitle>
            </DialogHeader>
            
            {/* Book Form */}
            {currentTab === "books" && (
              <form onSubmit={handleBookSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="book_id">Book ID *</Label>
                    <Input
                      id="book_id"
                      value={bookForm.book_id}
                      onChange={(e) => setBookForm({...bookForm, book_id: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="purchasing_year">Purchasing Year</Label>
                    <Input
                      id="purchasing_year"
                      value={bookForm.purchasing_year}
                      onChange={(e) => setBookForm({...bookForm, purchasing_year: e.target.value})}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="book_title">Book Title *</Label>
                  <Input
                    id="book_title"
                    value={bookForm.book_title}
                    onChange={(e) => setBookForm({...bookForm, book_title: e.target.value})}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="author">Author *</Label>
                  <Input
                    id="author"
                    value={bookForm.author}
                    onChange={(e) => setBookForm({...bookForm, author: e.target.value})}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="language">Language</Label>
                    <Input
                      id="language"
                      value={bookForm.language}
                      onChange={(e) => setBookForm({...bookForm, language: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select 
                      value={bookForm.category} 
                      onValueChange={(value) => setBookForm({...bookForm, category: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No category</SelectItem>
                        {bookCategories
                          .filter(category => category.is_active)
                          .sort((a, b) => a.display_order - b.display_order)
                          .map((category) => (
                            <SelectItem key={category.id} value={category.name}>
                              {category.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="place">Place</Label>
                  <Input
                    id="place"
                    value={bookForm.place}
                    onChange={(e) => setBookForm({...bookForm, place: e.target.value})}
                  />
                </div>

                <div>
                  <Label htmlFor="comment">Comment</Label>
                  <Input
                    id="comment"
                    value={bookForm.comment}
                    onChange={(e) => setBookForm({...bookForm, comment: e.target.value})}
                  />
                </div>

                <div>
                  <Label htmlFor="more_info">More Info URL</Label>
                  <Input
                    id="more_info"
                    type="url"
                    value={bookForm.more_info}
                    onChange={(e) => setBookForm({...bookForm, more_info: e.target.value})}
                    placeholder="https://..."
                  />
                </div>

                {/* Cover Image Upload */}
                <div>
                  <Label htmlFor="cover_image">Book Cover Image</Label>
                  <Input
                    id="cover_image"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      setSelectedCoverImage(file);
                      
                      // Generate image preview
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (e) => setImagePreview(e.target.result);
                        reader.readAsDataURL(file);
                      } else {
                        setImagePreview(null);
                      }
                    }}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Upload PNG, JPEG, or GIF. Image will be resized to 200x300 pixels.
                  </p>
                  
                  {/* Image Preview */}
                  {imagePreview && (
                    <div className="mt-3">
                      <Label className="text-sm font-medium">Preview:</Label>
                      <div className="mt-1 relative inline-block">
                        <img
                          src={imagePreview}
                          alt="Cover preview"
                          className="w-24 h-32 object-cover rounded border border-slate-200 shadow-sm"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-500 text-white hover:bg-red-600"
                          onClick={() => {
                            setSelectedCoverImage(null);
                            setImagePreview(null);
                            document.getElementById('cover_image').value = '';
                          }}
                        >
                          ×
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="bg-slate-700 hover:bg-slate-800">
                    {editingItem ? "Update Book" : "Add Book"}
                  </Button>
                  <Button type="button" variant="outline" onClick={closeDialog}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}

            {/* CD Form */}
            {currentTab === "cds" && (
              <form onSubmit={handleCdSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="cd_id">CD ID *</Label>
                  <Input
                    id="cd_id"
                    type="number"
                    value={cdForm.cd_id}
                    onChange={(e) => setCdForm({...cdForm, cd_id: e.target.value})}
                    required
                    placeholder="e.g., 1001"
                  />
                </div>

                <div>
                  <Label htmlFor="cd_title">Title *</Label>
                  <Input
                    id="cd_title"
                    value={cdForm.title}
                    onChange={(e) => setCdForm({...cdForm, title: e.target.value})}
                    required
                    placeholder="e.g., Abbey Road"
                  />
                </div>

                <div>
                  <Label htmlFor="executor">Executor *</Label>
                  <Input
                    id="executor"
                    value={cdForm.executor}
                    onChange={(e) => setCdForm({...cdForm, executor: e.target.value})}
                    required
                    placeholder="e.g., The Beatles"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Input
                      id="category"
                      value={cdForm.category}
                      onChange={(e) => setCdForm({...cdForm, category: e.target.value})}
                      placeholder="e.g., Rock, Jazz, Classical"
                    />
                  </div>
                  <div>
                    <Label htmlFor="support">Support</Label>
                    <select
                      id="support"
                      value={cdForm.support}
                      onChange={(e) => setCdForm({...cdForm, support: e.target.value})}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Select Support</option>
                      <option value="CD">CD</option>
                      <option value="Vinyl">Vinyl</option>
                      <option value="Digital">Digital</option>
                      <option value="Cassette">Cassette</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="acquisition_date">Acquisition Date *</Label>
                  <Input
                    id="acquisition_date"
                    type="date"
                    value={cdForm.acquisition_date}
                    onChange={(e) => setCdForm({...cdForm, acquisition_date: e.target.value})}
                    required
                  />
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="bg-slate-700 hover:bg-slate-800">
                    {editingItem ? "Update CD" : "Add CD"}
                  </Button>
                  <Button type="button" variant="outline" onClick={closeDialog}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}

            {/* DVD Form */}
            {currentTab === "dvds" && (
              <form onSubmit={handleDvdSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="dvd_id">DVD ID *</Label>
                    <Input
                      id="dvd_id"
                      value={dvdForm.dvd_id}
                      onChange={(e) => setDvdForm({...dvdForm, dvd_id: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="production_year">Production Year</Label>
                    <Input
                      id="production_year"
                      value={dvdForm.production_year}
                      onChange={(e) => setDvdForm({...dvdForm, production_year: e.target.value})}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="dvd_title">Title *</Label>
                  <Input
                    id="dvd_title"
                    value={dvdForm.title}
                    onChange={(e) => setDvdForm({...dvdForm, title: e.target.value})}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="director">Director</Label>
                  <Input
                    id="director"
                    value={dvdForm.director}
                    onChange={(e) => setDvdForm({...dvdForm, director: e.target.value})}
                  />
                </div>

                <div>
                  <Label htmlFor="actors">Actors</Label>
                  <Input
                    id="actors"
                    value={dvdForm.actors}
                    onChange={(e) => setDvdForm({...dvdForm, actors: e.target.value})}
                    placeholder="Comma-separated list of actors"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="languages">Languages</Label>
                    <Input
                      id="languages"
                      value={dvdForm.languages}
                      onChange={(e) => setDvdForm({...dvdForm, languages: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="dvd_category">Category</Label>
                    <Input
                      id="dvd_category"
                      value={dvdForm.category}
                      onChange={(e) => setDvdForm({...dvdForm, category: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="purchasing_year_dvd">Purchasing Year</Label>
                    <Input
                      id="purchasing_year_dvd"
                      value={dvdForm.purchasing_year}
                      onChange={(e) => setDvdForm({...dvdForm, purchasing_year: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="area">Area</Label>
                    <Input
                      id="area"
                      value={dvdForm.area}
                      onChange={(e) => setDvdForm({...dvdForm, area: e.target.value})}
                    />
                  </div>
                </div>

                {/* DVD Cover Image Upload */}
                <div>
                  <Label htmlFor="dvd_cover_image">DVD Cover Image</Label>
                  <div className="space-y-2">
                    <Input
                      id="dvd_cover_image"
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        setSelectedDvdCoverImage(file);
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (e) => setDvdImagePreview(e.target.result);
                          reader.readAsDataURL(file);
                        } else {
                          setDvdImagePreview(null);
                        }
                      }}
                      className="file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-slate-50 file:text-slate-700 hover:file:bg-slate-100"
                    />
                    {dvdImagePreview && (
                      <div className="mt-2">
                        <img src={dvdImagePreview} alt="DVD cover preview" className="w-20 h-28 object-cover rounded border" />
                      </div>
                    )}
                    {editingItem && getDvdCoverUrl(editingItem) && !dvdImagePreview && (
                      <div className="mt-2">
                        <p className="text-sm text-slate-600 mb-1">Current cover:</p>
                        <img src={getDvdCoverUrl(editingItem)} alt="Current DVD cover" className="w-20 h-28 object-cover rounded border" />
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="bg-slate-700 hover:bg-slate-800">
                    {editingItem ? "Update DVD" : "Add DVD"}
                  </Button>
                  <Button type="button" variant="outline" onClick={closeDialog}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}

            {/* Events Form */}
            {currentTab === "events" && (!editingItem || isEditFormReady) && (
              <form onSubmit={handleEventSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="event_title">Event Title *</Label>
                  <Input
                    id="event_title"
                    value={eventForm.title}
                    onChange={(e) => setEventForm({...eventForm, title: e.target.value})}
                    required
                  />
                </div>

                {/* Event ID - Read-only field for editing */}
                {editingItem && (
                  <div>
                    <Label htmlFor="event_id">Event ID</Label>
                    <Input
                      id="event_id"
                      value={editingItem.id || ""}
                      readOnly
                      className="bg-slate-50 text-slate-600 cursor-not-allowed"
                      title="This is the unique identifier for this event"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      This is the unique Event ID that can be used for direct booking links and references
                    </p>
                  </div>
                )}

                {/* Event Image Upload */}
                <div>
                  <Label htmlFor="event_image">Event Image</Label>
                  <div className="mt-2">
                    <input
                      id="event_image"
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          // Store the file temporarily - will upload after event creation
                          setEventForm({...eventForm, imageFile: file});
                        }
                      }}
                      className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-slate-50 file:text-slate-700 hover:file:bg-slate-100"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Upload an image that will appear between the event title and description
                    </p>
                    
                    {/* Show preview for newly selected file */}
                    {eventForm.imageFile && (
                      <div className="mt-2 flex justify-center">
                        <img 
                          src={URL.createObjectURL(eventForm.imageFile)}
                          alt="New event image preview"
                          className="max-w-48 max-h-32 object-contain rounded-md border shadow-sm"
                        />
                        <p className="text-xs text-green-600 mt-1 text-center">New image selected</p>
                      </div>
                    )}
                    
                    {/* Show preview for existing S3/local image (only if no new file selected) */}
                    {!eventForm.imageFile && eventImagePreview && (
                      <div className="mt-2 flex flex-col items-center">
                        <img 
                          src={eventImagePreview}
                          alt="Current event image"
                          className="max-w-48 max-h-32 object-contain rounded-md border shadow-sm"
                          onError={(e) => {
                            console.warn('Event image preview failed to load');
                            setEventImagePreview(null);
                          }}
                        />
                        <p className="text-xs text-slate-500 mt-1 text-center">Current image</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Rich Text Event Description */}
<div>
                  <Label htmlFor="event_description">Event Description *</Label>
                  <div className="mt-2">
                    <textarea
                      id="event_description"
                      value={eventForm.description_text || ''}
                      onChange={(e) => {
                        const textContent = e.target.value;
                        setEventForm({
                          ...eventForm, 
                          description_html: textContent, // Store HTML content
                          description_text: textContent
                        });
                      }}
                      className="min-h-40 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y font-mono"
                      placeholder="Enter event description with HTML formatting... Use tags like <h3>, <p>, <strong>, <em>, <span style='color:blue'>, etc."
                      required
                      rows={8}
                    />
                    <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200 text-xs text-slate-700 space-y-2">
                      <p className="font-semibold text-blue-900">✨ Full HTML Support Available:</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <strong>Text Formatting:</strong>
                          <ul className="list-disc list-inside ml-2 mt-1">
                            <li>&lt;strong&gt;bold&lt;/strong&gt;</li>
                            <li>&lt;em&gt;italic&lt;/em&gt;</li>
                            <li>&lt;u&gt;underline&lt;/u&gt;</li>
                            <li>&lt;h1&gt; to &lt;h6&gt; headings</li>
                          </ul>
                        </div>
                        <div>
                          <strong>Colors & Styles:</strong>
                          <ul className="list-disc list-inside ml-2 mt-1">
                            <li>&lt;span style="color:red"&gt;text&lt;/span&gt;</li>
                            <li>&lt;span style="background:yellow"&gt;highlight&lt;/span&gt;</li>
                            <li>&lt;font color="blue"&gt;text&lt;/font&gt;</li>
                            <li>&lt;span style="font-size:20px"&gt;large&lt;/span&gt;</li>
                          </ul>
                        </div>
                        <div>
                          <strong>Structure:</strong>
                          <ul className="list-disc list-inside ml-2 mt-1">
                            <li>&lt;p&gt;paragraphs&lt;/p&gt;</li>
                            <li>&lt;br&gt; line breaks</li>
                            <li>&lt;ul&gt;&lt;li&gt;bullet lists&lt;/li&gt;&lt;/ul&gt;</li>
                            <li>&lt;ol&gt;&lt;li&gt;numbered lists&lt;/li&gt;&lt;/ol&gt;</li>
                          </ul>
                        </div>
                        <div>
                          <strong>Links & More:</strong>
                          <ul className="list-disc list-inside ml-2 mt-1">
                            <li>&lt;a href="url"&gt;link&lt;/a&gt;</li>
                            <li>&lt;div&gt; and &lt;span&gt; containers</li>
                            <li>&lt;hr&gt; horizontal line</li>
                            <li>Inline CSS with style="..."</li>
                          </ul>
                        </div>
                      </div>
                      <p className="text-xs italic text-blue-700 mt-2">
                        💡 Tip: You can combine styles like: &lt;span style="color:green; font-weight:bold; font-size:18px"&gt;Important Text&lt;/span&gt;
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="event_date">Event Date & Time *</Label>
                    <Input
                      id="event_date"
                      type="datetime-local"
                      value={eventForm.event_date}
                      onChange={(e) => setEventForm({...eventForm, event_date: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="booking_deadline">Booking closes on (optional)</Label>
                    <Input
                      id="booking_deadline"
                      type="datetime-local"
                      value={eventForm.booking_deadline}
                      onChange={(e) => setEventForm({...eventForm, booking_deadline: e.target.value})}
                      max={eventForm.event_date || undefined}
                      data-testid="booking-deadline-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Leave empty to accept bookings until the event starts.
                    </p>
                  </div>
                </div>

                {/* Pricing Type Selection */}
                <div className="space-y-4 p-4 border rounded-lg bg-slate-50">
                  <h3 className="text-lg font-semibold text-blue-700">Pricing Structure</h3>

                  <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="pricing_type"
                        value="custom_rows"
                        checked={eventForm.pricing_type === "custom_rows"}
                        onChange={(e) => setEventForm({...eventForm, pricing_type: e.target.value})}
                        data-testid="pricing-type-custom-rows"
                      />
                      <span className="font-medium text-emerald-700">Custom price rows (1–5, member picks)</span>
                    </label>
                    <label className="flex items-center space-x-2 opacity-70" title="Legacy — new bookings are disabled on events using this model">
                      <input
                        type="radio"
                        name="pricing_type"
                        value="standard"
                        checked={eventForm.pricing_type === "standard" || eventForm.pricing_type === "legacy"}
                        onChange={(e) => setEventForm({...eventForm, pricing_type: e.target.value})}
                      />
                      <span>Standard Pricing <span className="text-xs text-slate-500">(legacy)</span></span>
                    </label>
                    <label className="flex items-center space-x-2 opacity-70" title="Legacy — new bookings are disabled on events using this model">
                      <input
                        type="radio"
                        name="pricing_type"
                        value="matrix"
                        checked={eventForm.pricing_type === "matrix"}
                        onChange={(e) => setEventForm({...eventForm, pricing_type: e.target.value})}
                      />
                      <span>Opera-Style Matrix Pricing (Galleria/Platea) <span className="text-xs text-slate-500">(legacy)</span></span>
                    </label>
                  </div>
                  {(eventForm.pricing_type === "standard" || eventForm.pricing_type === "legacy" || eventForm.pricing_type === "matrix") && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                      Legacy pricing — this event will not accept new member bookings. Use <em>Custom price rows</em> for new events.
                    </p>
                  )}
                </div>

                {/* Custom Rows Pricing Section (new model) */}
                {eventForm.pricing_type === "custom_rows" && (
                  <div className="space-y-3 p-4 border rounded-lg bg-emerald-50/30" data-testid="custom-rows-editor">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-emerald-700">Custom Ticket Types (1–5)</h3>
                      <span className="text-xs text-slate-500">Members pick themselves at booking</span>
                    </div>
                    <p className="text-xs text-slate-600">
                      Define up to 5 ticket types with a free-text label, a price and optionally the number of tickets available (leave empty for no limit). Ages are not used — members choose the right ticket for each attendee (a disclaimer is shown on the booking form).
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-[11px] font-medium text-slate-500" aria-hidden="true">
                        <span className="w-6" />
                        <span className="flex-1">Label</span>
                        <span className="w-28 pl-4">Price</span>
                        <span className="w-28">Tickets available</span>
                        <span className="w-20 text-center">Minors only</span>
                        <span className="w-8" />
                      </div>
                      {(eventForm.custom_price_rows || []).map((row, idx) => (
                        <div key={idx} className="flex items-center gap-2" data-testid={`custom-row-${idx}`}>
                          <span className="text-xs text-slate-500 w-6">#{idx + 1}</span>
                          <Input
                            placeholder="Label (e.g. Adult, Under 12, Senior)"
                            value={row.label || ""}
                            onChange={(e) => {
                              const rows = [...(eventForm.custom_price_rows || [])];
                              rows[idx] = { ...rows[idx], label: e.target.value };
                              setEventForm({ ...eventForm, custom_price_rows: rows });
                            }}
                            className="flex-1"
                            data-testid={`custom-row-label-${idx}`}
                          />
                          <div className="flex items-center gap-1">
                            <span className="text-sm text-slate-600">€</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="0.00"
                              value={row.amount ?? ""}
                              onChange={(e) => {
                                const rows = [...(eventForm.custom_price_rows || [])];
                                rows[idx] = { ...rows[idx], amount: e.target.value };
                                setEventForm({ ...eventForm, custom_price_rows: rows });
                              }}
                              className="w-24"
                              data-testid={`custom-row-amount-${idx}`}
                            />
                          </div>
                          <Input
                            type="number"
                            step="1"
                            min="0"
                            placeholder="No limit"
                            title="Tickets available in this category (leave empty for no limit)"
                            value={row.capacity ?? ""}
                            onChange={(e) => {
                              const rows = [...(eventForm.custom_price_rows || [])];
                              rows[idx] = { ...rows[idx], capacity: e.target.value };
                              setEventForm({ ...eventForm, custom_price_rows: rows });
                            }}
                            className="w-28"
                            data-testid={`custom-row-capacity-${idx}`}
                          />
                          <label className="w-20 flex justify-center items-center" title="Show this price only in the Minors section of the booking form">
                            <input
                              type="checkbox"
                              checked={!!row.minors_only}
                              onChange={(e) => {
                                const rows = [...(eventForm.custom_price_rows || [])];
                                rows[idx] = { ...rows[idx], minors_only: e.target.checked };
                                setEventForm({ ...eventForm, custom_price_rows: rows });
                              }}
                              className="h-4 w-4 accent-amber-600"
                              data-testid={`custom-row-minors-only-${idx}`}
                            />
                          </label>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-red-600 hover:bg-red-50"
                            disabled={(eventForm.custom_price_rows || []).length <= 1}
                            onClick={() => {
                              const rows = [...(eventForm.custom_price_rows || [])];
                              rows.splice(idx, 1);
                              setEventForm({ ...eventForm, custom_price_rows: rows });
                            }}
                            data-testid={`custom-row-remove-${idx}`}
                            title="Remove row"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    {(eventForm.custom_price_rows || []).length < 5 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const rows = [...(eventForm.custom_price_rows || []), { id: null, label: "", amount: "", capacity: "" }];
                          setEventForm({ ...eventForm, custom_price_rows: rows });
                        }}
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        data-testid="custom-row-add"
                      >
                        <Plus className="h-4 w-4 mr-1" /> Add row
                      </Button>
                    )}
                  </div>
                )}

                {/* Standard Pricing Section */}
                {(eventForm.pricing_type === "standard" || eventForm.pricing_type === "legacy") && (
                  <div className="space-y-4 p-4 border rounded-lg">
                    <h3 className="text-lg font-semibold text-blue-700">Standard Price Categories</h3>
                    
                    {/* Member Price */}
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="member_price_enabled"
                          checked={(() => {
                            console.log('🔍 CHECKBOX DEBUG:', {
                              has_editingItem: !!editingItem,
                              editingItem_title: editingItem?.title?.substring(0, 20),
                              editingItem_has_price_categories: !!editingItem?.price_categories,
                              editingItem_member_price_enabled: editingItem?.price_categories?.member_price_enabled,
                              eventForm_member_price_enabled: eventForm.price_categories?.member_price_enabled,
                              should_use_fallback: editingItem?.price_categories?.member_price_enabled && !eventForm.price_categories?.member_price_enabled
                            });
                            
                            // For editing: use the original event data if form isn't ready yet
                            if (editingItem?.price_categories?.member_price_enabled && !eventForm.price_categories?.member_price_enabled) {
                              console.log('🔍 USING FALLBACK - checkbox');
                              return editingItem.price_categories.member_price_enabled;
                            }
                            return eventForm.price_categories?.member_price_enabled || false;
                          })()}
                          onChange={(e) => setEventForm({
                            ...eventForm, 
                            price_categories: {
                              ...eventForm.price_categories,
                              member_price_enabled: e.target.checked
                            }
                          })}
                        />
                        <Label htmlFor="member_price_enabled">Member Price</Label>
                      </div>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        key={`member_price_${eventForm.id || 'new'}_${eventForm.price_categories?.member_price || 'empty'}`}
                        value={(() => {
                          const value = eventForm.price_categories?.member_price || "";
                          console.log('🔍 PRICE FIELD RENDER:', {
                            timestamp: new Date().toLocaleTimeString(),
                            form_member_price: eventForm.price_categories?.member_price,
                            full_price_categories: eventForm.price_categories,
                            final_value: value,
                            input_key: `member_price_${eventForm.id || 'new'}_${eventForm.price_categories?.member_price || 'empty'}`,
                            eventForm_has_title: !!eventForm.title
                          });
                          return value;
                        })()}
                        onChange={(e) => setEventForm({
                          ...eventForm, 
                          price_categories: {
                            ...eventForm.price_categories,
                            member_price: e.target.value
                          }
                        })}
                        disabled={!eventForm.price_categories?.member_price_enabled}
                        className="w-24"
                      />
                      <span className="text-sm text-gray-600">€</span>
                    </div>

                    {/* Senior(over 65) Price */}
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="senior_price_enabled"
                          checked={eventForm.price_categories?.senior_price_enabled || false}
                          onChange={(e) => setEventForm({
                            ...eventForm, 
                            price_categories: {
                              ...eventForm.price_categories,
                              senior_price_enabled: e.target.checked
                            }
                          })}
                        />
                        <Label htmlFor="senior_price_enabled">Senior Price (Over 65)</Label>
                      </div>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={eventForm.price_categories?.senior_price || ""}
                        onChange={(e) => setEventForm({
                          ...eventForm, 
                          price_categories: {
                            ...eventForm.price_categories,
                            senior_price: e.target.value
                          }
                        })}
                        disabled={!eventForm.price_categories?.senior_price_enabled}
                        className="w-24"
                      />
                      <span className="text-sm text-gray-600">€</span>
                    </div>

                    {/* Minor Price */}
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="minor_price_enabled"
                          checked={eventForm.price_categories?.minor_price_enabled || false}
                          onChange={(e) => setEventForm({
                            ...eventForm, 
                            price_categories: {
                              ...eventForm.price_categories,
                              minor_price_enabled: e.target.checked
                            }
                          })}
                        />
                        <Label htmlFor="minor_price_enabled">Minor Price (Under 18)</Label>
                      </div>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={eventForm.price_categories?.minor_price || ""}
                        onChange={(e) => setEventForm({
                          ...eventForm, 
                          price_categories: {
                            ...eventForm.price_categories,
                            minor_price: e.target.value
                          }
                        })}
                        disabled={!eventForm.price_categories?.minor_price_enabled}
                        className="w-24"
                      />
                      <span className="text-sm text-gray-600">€</span>
                    </div>

                    {/* Young Adult Price */}
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="young_adult_price_enabled"
                          checked={eventForm.price_categories?.young_adult_price_enabled || false}
                          onChange={(e) => setEventForm({
                            ...eventForm, 
                            price_categories: {
                              ...eventForm.price_categories,
                              young_adult_price_enabled: e.target.checked
                            }
                          })}
                        />
                        <Label htmlFor="young_adult_price_enabled">Young Adult Price (Under 26)</Label>
                      </div>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={eventForm.price_categories?.young_adult_price || ""}
                        onChange={(e) => setEventForm({
                          ...eventForm, 
                          price_categories: {
                            ...eventForm.price_categories,
                            young_adult_price: e.target.value
                          }
                        })}
                        disabled={!eventForm.price_categories?.young_adult_price_enabled}
                        className="w-24"
                      />
                      <span className="text-sm text-gray-600">€</span>
                    </div>
                  </div>
                )}

                {/* Opera-Style Matrix Pricing Section */}
                {eventForm.pricing_type === "matrix" && (
                  <div className="space-y-4 p-4 border rounded-lg">
                    <h3 className="text-lg font-semibold text-purple-700">🎭 Opera-Style Matrix Pricing</h3>
                    <p className="text-sm text-gray-600">Set different prices for two sections by age category</p>
                    
                    {/* Custom Section Labels */}
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 space-y-3">
                      <h4 className="text-sm font-semibold text-blue-900">Section Names (Customizable)</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Section 1 Label
                          </label>
                          <input
                            type="text"
                            value={eventForm.section_1_label || "Galleria"}
                            onChange={(e) => setEventForm({
                              ...eventForm,
                              section_1_label: e.target.value
                            })}
                            placeholder="e.g., Galleria, Balcony, Upper Level"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Section 2 Label
                          </label>
                          <input
                            type="text"
                            value={eventForm.section_2_label || "Platea"}
                            onChange={(e) => setEventForm({
                              ...eventForm,
                              section_2_label: e.target.value
                            })}
                            placeholder="e.g., Platea, Orchestra, Main Floor"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 italic">
                        💡 Customize the names for your venue sections (e.g., "Balcony" and "Orchestra", "Upper Level" and "Lower Level")
                      </p>
                    </div>
                    
                    {/* Seat Section Selection */}
                    <div className="flex gap-4 mb-4">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={eventForm.matrix_pricing?.galleria_enabled || false}
                          onChange={(e) => setEventForm({
                            ...eventForm,
                            matrix_pricing: {
                              ...eventForm.matrix_pricing,
                              galleria_enabled: e.target.checked
                            }
                          })}
                        />
                        <span className="font-medium">{eventForm.section_1_label || "Galleria"} Section</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={eventForm.matrix_pricing?.platea_enabled || false}
                          onChange={(e) => setEventForm({
                            ...eventForm,
                            matrix_pricing: {
                              ...eventForm.matrix_pricing,
                              platea_enabled: e.target.checked
                            }
                          })}
                        />
                        <span className="font-medium">{eventForm.section_2_label || "Platea"} Section</span>
                      </label>
                    </div>

                    {/* Matrix Pricing Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border border-gray-300">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="border border-gray-300 p-2 text-left">Age Category</th>
                            <th className="border border-gray-300 p-2 text-center">Enable</th>
                            {eventForm.matrix_pricing?.galleria_enabled && (
                              <th className="border border-gray-300 p-2 text-center">{eventForm.section_1_label || "Galleria"} Price (€)</th>
                            )}
                            {eventForm.matrix_pricing?.platea_enabled && (
                              <th className="border border-gray-300 p-2 text-center">{eventForm.section_2_label || "Platea"} Price (€)</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {/* Member Pricing */}
                          <tr>
                            <td className="border border-gray-300 p-2 font-medium">Member</td>
                            <td className="border border-gray-300 p-2 text-center">
                              <input
                                type="checkbox"
                                checked={eventForm.matrix_pricing?.member_pricing_enabled || false}
                                onChange={(e) => setEventForm({
                                  ...eventForm,
                                  matrix_pricing: {
                                    ...eventForm.matrix_pricing,
                                    member_pricing_enabled: e.target.checked
                                  }
                                })}
                              />
                            </td>
                            {eventForm.matrix_pricing?.galleria_enabled && (
                              <td className="border border-gray-300 p-2">
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="0.00"
                                  value={eventForm.matrix_pricing?.member_pricing?.galleria_price || ""}
                                  onChange={(e) => setEventForm({
                                    ...eventForm,
                                    matrix_pricing: {
                                      ...eventForm.matrix_pricing,
                                      member_pricing: {
                                        ...eventForm.matrix_pricing.member_pricing,
                                        galleria_price: e.target.value
                                      }
                                    }
                                  })}
                                  disabled={!eventForm.matrix_pricing?.member_pricing_enabled}
                                  className="w-20"
                                />
                              </td>
                            )}
                            {eventForm.matrix_pricing?.platea_enabled && (
                              <td className="border border-gray-300 p-2">
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="0.00"
                                  value={eventForm.matrix_pricing?.member_pricing?.platea_price || ""}
                                  onChange={(e) => setEventForm({
                                    ...eventForm,
                                    matrix_pricing: {
                                      ...eventForm.matrix_pricing,
                                      member_pricing: {
                                        ...eventForm.matrix_pricing.member_pricing,
                                        platea_price: e.target.value
                                      }
                                    }
                                  })}
                                  disabled={!eventForm.matrix_pricing?.member_pricing_enabled}
                                  className="w-20"
                                />
                              </td>
                            )}
                          </tr>

                          {/* Senior Pricing */}
                          <tr className="bg-blue-50">
                            <td className="border border-gray-300 p-2 font-medium">Senior (65+)</td>
                            <td className="border border-gray-300 p-2 text-center">
                              <input
                                type="checkbox"
                                checked={eventForm.matrix_pricing?.senior_pricing_enabled || false}
                                onChange={(e) => setEventForm({
                                  ...eventForm,
                                  matrix_pricing: {
                                    ...eventForm.matrix_pricing,
                                    senior_pricing_enabled: e.target.checked
                                  }
                                })}
                              />
                            </td>
                            {eventForm.matrix_pricing?.galleria_enabled && (
                              <td className="border border-gray-300 p-2">
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="0.00"
                                  value={eventForm.matrix_pricing?.senior_pricing?.galleria_price || ""}
                                  onChange={(e) => setEventForm({
                                    ...eventForm,
                                    matrix_pricing: {
                                      ...eventForm.matrix_pricing,
                                      senior_pricing: {
                                        ...eventForm.matrix_pricing.senior_pricing,
                                        galleria_price: e.target.value
                                      }
                                    }
                                  })}
                                  disabled={!eventForm.matrix_pricing?.senior_pricing_enabled}
                                  className="w-20"
                                />
                              </td>
                            )}
                            {eventForm.matrix_pricing?.platea_enabled && (
                              <td className="border border-gray-300 p-2">
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="0.00"
                                  value={eventForm.matrix_pricing?.senior_pricing?.platea_price || ""}
                                  onChange={(e) => setEventForm({
                                    ...eventForm,
                                    matrix_pricing: {
                                      ...eventForm.matrix_pricing,
                                      senior_pricing: {
                                        ...eventForm.matrix_pricing.senior_pricing,
                                        platea_price: e.target.value
                                      }
                                    }
                                  })}
                                  disabled={!eventForm.matrix_pricing?.senior_pricing_enabled}
                                  className="w-20"
                                />
                              </td>
                            )}
                          </tr>

                          {/* Minor Pricing */}
                          <tr>
                            <td className="border border-gray-300 p-2 font-medium">Minor (Under 18)</td>
                            <td className="border border-gray-300 p-2 text-center">
                              <input
                                type="checkbox"
                                checked={eventForm.matrix_pricing?.minor_pricing_enabled || false}
                                onChange={(e) => setEventForm({
                                  ...eventForm,
                                  matrix_pricing: {
                                    ...eventForm.matrix_pricing,
                                    minor_pricing_enabled: e.target.checked
                                  }
                                })}
                              />
                            </td>
                            {eventForm.matrix_pricing?.galleria_enabled && (
                              <td className="border border-gray-300 p-2">
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="0.00"
                                  value={eventForm.matrix_pricing?.minor_pricing?.galleria_price || ""}
                                  onChange={(e) => setEventForm({
                                    ...eventForm,
                                    matrix_pricing: {
                                      ...eventForm.matrix_pricing,
                                      minor_pricing: {
                                        ...eventForm.matrix_pricing.minor_pricing,
                                        galleria_price: e.target.value
                                      }
                                    }
                                  })}
                                  disabled={!eventForm.matrix_pricing?.minor_pricing_enabled}
                                  className="w-20"
                                />
                              </td>
                            )}
                            {eventForm.matrix_pricing?.platea_enabled && (
                              <td className="border border-gray-300 p-2">
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="0.00"
                                  value={eventForm.matrix_pricing?.minor_pricing?.platea_price || ""}
                                  onChange={(e) => setEventForm({
                                    ...eventForm,
                                    matrix_pricing: {
                                      ...eventForm.matrix_pricing,
                                      minor_pricing: {
                                        ...eventForm.matrix_pricing.minor_pricing,
                                        platea_price: e.target.value
                                      }
                                    }
                                  })}
                                  disabled={!eventForm.matrix_pricing?.minor_pricing_enabled}
                                  className="w-20"
                                />
                              </td>
                            )}
                          </tr>

                          {/* Young Adult Pricing */}
                          <tr className="bg-green-50">
                            <td className="border border-gray-300 p-2 font-medium">Young Adult (Under 26)</td>
                            <td className="border border-gray-300 p-2 text-center">
                              <input
                                type="checkbox"
                                checked={eventForm.matrix_pricing?.young_adult_pricing_enabled || false}
                                onChange={(e) => setEventForm({
                                  ...eventForm,
                                  matrix_pricing: {
                                    ...eventForm.matrix_pricing,
                                    young_adult_pricing_enabled: e.target.checked
                                  }
                                })}
                              />
                            </td>
                            {eventForm.matrix_pricing?.galleria_enabled && (
                              <td className="border border-gray-300 p-2">
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="0.00"
                                  value={eventForm.matrix_pricing?.young_adult_pricing?.galleria_price || ""}
                                  onChange={(e) => setEventForm({
                                    ...eventForm,
                                    matrix_pricing: {
                                      ...eventForm.matrix_pricing,
                                      young_adult_pricing: {
                                        ...eventForm.matrix_pricing.young_adult_pricing,
                                        galleria_price: e.target.value
                                      }
                                    }
                                  })}
                                  disabled={!eventForm.matrix_pricing?.young_adult_pricing_enabled}
                                  className="w-20"
                                />
                              </td>
                            )}
                            {eventForm.matrix_pricing?.platea_enabled && (
                              <td className="border border-gray-300 p-2">
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="0.00"
                                  value={eventForm.matrix_pricing?.young_adult_pricing?.platea_price || ""}
                                  onChange={(e) => setEventForm({
                                    ...eventForm,
                                    matrix_pricing: {
                                      ...eventForm.matrix_pricing,
                                      young_adult_pricing: {
                                        ...eventForm.matrix_pricing.young_adult_pricing,
                                        platea_price: e.target.value
                                      }
                                    }
                                  })}
                                  disabled={!eventForm.matrix_pricing?.young_adult_pricing_enabled}
                                  className="w-20"
                                />
                              </td>
                            )}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Venue Information */}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="event_label">Event Label</Label>
                    <Input
                      id="event_label"
                      value={eventForm.event_label}
                      onChange={(e) => setEventForm({...eventForm, event_label: e.target.value})}
                      placeholder="e.g., Special Event, Featured"
                    />
                  </div>
                  <div>
                    <Label htmlFor="event_status">Status</Label>
                    <Select value={eventForm.status} onValueChange={(value) => setEventForm({...eventForm, status: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="published">Published</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="max_capacity">Maximum Capacity</Label>
                  <Input
                    id="max_capacity"
                    type="number"
                    min="1"
                    value={eventForm.max_capacity}
                    onChange={(e) => setEventForm({...eventForm, max_capacity: e.target.value})}
                    placeholder="Leave empty for unlimited capacity"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Set maximum number of attendees. Leave empty for unlimited capacity.
                  </p>
                </div>

                <div>
                  <Label htmlFor="guided_visit">Guided Visit</Label>
                  <Select 
                    value={eventForm.guided_visit ? "yes" : "no"} 
                    onValueChange={(value) => setEventForm({...eventForm, guided_visit: value === "yes"})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="organisers">Organisers</Label>
                  <Select 
                    value={eventForm.organiser_ids.length > 0 ? eventForm.organiser_ids[0] : ""} 
                    onValueChange={(value) => {
                      const currentIds = eventForm.organiser_ids || [];
                      const newIds = currentIds.includes(value) ? 
                        currentIds.filter(id => id !== value) : 
                        [...currentIds, value];
                      setEventForm({...eventForm, organiser_ids: newIds});
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select organisers" />
                    </SelectTrigger>
                    <SelectContent>
                      {(Array.isArray(organisers) ? organisers : []).map((organiser) => (
                        <SelectItem key={organiser.id} value={organiser.id}>
                          {organiser.name} {organiser.email && `(${organiser.email})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                      {eventForm.organiser_ids && eventForm.organiser_ids.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {eventForm.organiser_ids.map(id => {
                        const organiser = (Array.isArray(organisers) ? organisers : []).find(o => o.id === id);
                        return organiser ? (
                          <Badge key={id} variant="secondary" className="text-xs">
                            {organiser.name}
                            <button 
                              type="button"
                              onClick={() => setEventForm({
                                ...eventForm, 
                                organiser_ids: eventForm.organiser_ids.filter(oid => oid !== id)
                              })}
                              className="ml-1 text-red-500"
                            >×</button>
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="venues">Venues</Label>
                  <Select 
                    value={eventForm.venue_ids.length > 0 ? eventForm.venue_ids[0] : ""} 
                    onValueChange={(value) => {
                      const currentIds = eventForm.venue_ids || [];
                      const newIds = currentIds.includes(value) ? 
                        currentIds.filter(id => id !== value) : 
                        [...currentIds, value];
                      setEventForm({...eventForm, venue_ids: newIds});
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select venues" />
                    </SelectTrigger>
                    <SelectContent>
                      {(Array.isArray(venues) ? venues : []).map((venue) => (
                        <SelectItem key={venue.id} value={venue.id}>
                          {venue.name} {venue.city && `- ${venue.city}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                      {eventForm.venue_ids && eventForm.venue_ids.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {eventForm.venue_ids.map(id => {
                        const venue = (Array.isArray(venues) ? venues : []).find(v => v.id === id);
                        return venue ? (
                          <Badge key={id} variant="secondary" className="text-xs">
                            {venue.name}
                            <button 
                              type="button"
                              onClick={() => setEventForm({
                                ...eventForm, 
                                venue_ids: eventForm.venue_ids.filter(vid => vid !== id)
                              })}
                              className="ml-1 text-red-500"
                            >×</button>
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="category_tags">Category Tags</Label>
                  <Input
                    id="category_tags"
                    value={Array.isArray(eventForm.category_tags) ? eventForm.category_tags.join(', ') : eventForm.category_tags}
                    onChange={(e) => setEventForm({...eventForm, category_tags: e.target.value})}
                    placeholder="e.g., Music, Culture, Workshop (comma-separated)"
                  />
                </div>

                <div>
                  <Label htmlFor="event_comment">Comment</Label>
                  <textarea
                    id="event_comment"
                    value={eventForm.comment}
                    onChange={(e) => setEventForm({...eventForm, comment: e.target.value})}
                    placeholder="Add any additional comments or notes about the event..."
                    className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                    rows="3"
                  />
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="bg-slate-700 hover:bg-slate-800">
                    {editingItem ? "Update Event" : "Create Event"}
                  </Button>
                  <Button type="button" variant="outline" onClick={closeDialog}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}

            {/* Organiser Form */}
            {currentTab === "organiser" && (
              <form onSubmit={handleOrganiserSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="organiser_name">Organiser Name *</Label>
                  <Input
                    id="organiser_name"
                    value={organiserForm.name}
                    onChange={(e) => setOrganiserForm({...organiserForm, name: e.target.value})}
                    placeholder="Enter organiser name"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="organiser_email">Email</Label>
                  <Input
                    id="organiser_email"
                    type="email"
                    value={organiserForm.email}
                    onChange={(e) => setOrganiserForm({...organiserForm, email: e.target.value})}
                    placeholder="organiser@example.com"
                  />
                </div>

                <div>
                  <Label htmlFor="organiser_phone">Phone</Label>
                  <Input
                    id="organiser_phone"
                    value={organiserForm.phone}
                    onChange={(e) => setOrganiserForm({...organiserForm, phone: e.target.value})}
                    placeholder="+1 234 567 8900"
                  />
                </div>

                <div>
                  <Label htmlFor="organiser_website">Website</Label>
                  <Input
                    id="organiser_website"
                    type="url"
                    value={organiserForm.website}
                    onChange={(e) => setOrganiserForm({...organiserForm, website: e.target.value})}
                    placeholder="https://example.com"
                  />
                </div>

                <div>
                  <Label htmlFor="organiser_bio">Bio/Description</Label>
                  <textarea
                    id="organiser_bio"
                    value={organiserForm.bio}
                    onChange={(e) => setOrganiserForm({...organiserForm, bio: e.target.value})}
                    className="min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y"
                    placeholder="Brief description about the organiser..."
                    rows={3}
                  />
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    {editingItem ? "Update Organiser" : "Create Organiser"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => {
                    setCurrentTab("events"); // Return to events tab
                    closeDialog();
                  }}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}

            {/* Venue Form */}
            {currentTab === "venue" && (
              <form onSubmit={handleVenueSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="venue_name">Venue Name *</Label>
                  <Input
                    id="venue_name"
                    value={venueForm.name}
                    onChange={(e) => setVenueForm({...venueForm, name: e.target.value})}
                    placeholder="Enter venue name"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="venue_address">Address</Label>
                  <Input
                    id="venue_address"
                    value={venueForm.address}
                    onChange={(e) => setVenueForm({...venueForm, address: e.target.value})}
                    placeholder="Street address"
                  />
                </div>

                <div>
                  <Label htmlFor="venue_city">City</Label>
                  <Input
                    id="venue_city"
                    value={venueForm.city}
                    onChange={(e) => setVenueForm({...venueForm, city: e.target.value})}
                    placeholder="City name"
                  />
                </div>

                <div>
                  <Label htmlFor="venue_capacity">Capacity</Label>
                  <Input
                    id="venue_capacity"
                    type="number"
                    value={venueForm.capacity}
                    onChange={(e) => setVenueForm({...venueForm, capacity: e.target.value})}
                    placeholder="Maximum number of people"
                  />
                </div>

                <div>
                  <Label htmlFor="venue_contact">Contact Information</Label>
                  <Input
                    id="venue_contact"
                    value={venueForm.contact_info}
                    onChange={(e) => setVenueForm({...venueForm, contact_info: e.target.value})}
                    placeholder="Phone, email, or contact person"
                  />
                </div>

                <div>
                  <Label htmlFor="venue_facilities">Facilities (comma-separated)</Label>
                  <Input
                    id="venue_facilities"
                    value={Array.isArray(venueForm.facilities) ? venueForm.facilities.join(', ') : venueForm.facilities}
                    onChange={(e) => {
                      const facilitiesArray = e.target.value.split(',').map(f => f.trim()).filter(f => f.length > 0);
                      setVenueForm({...venueForm, facilities: facilitiesArray});
                    }}
                    placeholder="WiFi, Parking, Accessibility, Audio/Visual"
                  />
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="bg-green-600 hover:bg-green-700">
                    {editingItem ? "Update Venue" : "Create Venue"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => {
                    setCurrentTab("events"); // Return to events tab
                    closeDialog();
                  }}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}

            {/* User Form - Keep existing implementation */}
            {currentTab === "users" && (
              <form onSubmit={handleUserSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit_first_name_1">First Name 1 *</Label>
                    <Input
                      id="edit_first_name_1"
                      value={userForm.first_name_1}
                      onChange={(e) => setUserForm({...userForm, first_name_1: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_first_name_2">First Name 2</Label>
                    <Input
                      id="edit_first_name_2"
                      value={userForm.first_name_2}
                      onChange={(e) => setUserForm({...userForm, first_name_2: e.target.value})}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="edit_last_name">Last Name *</Label>
                  <Input
                    id="edit_last_name"
                    value={userForm.last_name}
                    onChange={(e) => setUserForm({...userForm, last_name: e.target.value})}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="edit_email">Email Address *</Label>
                  <Input
                    id="edit_email"
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit_mobile">Mobile</Label>
                    <Input
                      id="edit_mobile"
                      type="tel"
                      value={userForm.mobile}
                      onChange={(e) => setUserForm({...userForm, mobile: e.target.value})}
                      placeholder="+39 xxx xxx xxxx"
                    />
                    <div className="text-xs text-slate-600 mt-1">
                      Phone number will be used to contact before or after cultural events
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="edit_landline">Landline</Label>
                    <Input
                      id="edit_landline"
                      type="tel"
                      value={userForm.landline}
                      onChange={(e) => setUserForm({...userForm, landline: e.target.value})}
                      placeholder="E.g. 0332 XXXX"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="edit_last_year_member"
                      checked={userForm.last_year_member}
                      onChange={(e) => setUserForm({...userForm, last_year_member: e.target.checked})}
                      className="rounded border-gray-300"
                    />
                    <Label htmlFor="edit_last_year_member">Last Year Member</Label>
                  </div>
                  <div>
                    <Label htmlFor="edit_member_category">Member Category *</Label>
                    <Select value={userForm.member_category} onValueChange={(value) => setUserForm({...userForm, member_category: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="JRC Internal">JRC Internal</SelectItem>
                        <SelectItem value="JRC External">JRC External</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit_birth_date">Birth Date *</Label>
                    <Input
                      id="edit_birth_date"
                      type="date"
                      value={userForm.birth_date}
                      onChange={(e) => setUserForm({...userForm, birth_date: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_birth_place">Birth Place *</Label>
                    <Input
                      id="edit_birth_place"
                      value={userForm.birth_place}
                      onChange={(e) => setUserForm({...userForm, birth_place: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit_nationality">Nationality *</Label>
                    <Select value={userForm.nationality} onValueChange={(value) => setUserForm({...userForm, nationality: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select nationality" />
                      </SelectTrigger>
                      <SelectContent>
                        {nationalities.map((nationality) => (
                          <SelectItem key={nationality} value={nationality}>
                            {nationality}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="edit_other_nationality">Other Nationality</Label>
                    <Input
                      id="edit_other_nationality"
                      value={userForm.other_nationality}
                      onChange={(e) => setUserForm({...userForm, other_nationality: e.target.value})}
                      placeholder="If 'Other' selected above"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="edit_address">Address *</Label>
                  <Input
                    id="edit_address"
                    value={userForm.address}
                    onChange={(e) => setUserForm({...userForm, address: e.target.value})}
                    required
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="edit_postal_code">Postal Code *</Label>
                    <Input
                      id="edit_postal_code"
                      value={userForm.postal_code}
                      onChange={(e) => setUserForm({...userForm, postal_code: e.target.value})}
                      pattern="[0-9]{5}"
                      title="5-digit postal code"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_city">City *</Label>
                    <Input
                      id="edit_city"
                      value={userForm.city}
                      onChange={(e) => setUserForm({...userForm, city: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_province">Province</Label>
                    <Input
                      id="edit_province"
                      value={userForm.province}
                      onChange={(e) => setUserForm({...userForm, province: e.target.value})}
                      maxLength="2"
                      pattern="[A-Z]{2}"
                      title="2-letter province code"
                      placeholder="e.g. MI"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="edit_country">Country *</Label>
                  <Select 
                    value={userForm.country} 
                    onValueChange={(value) => setUserForm({...userForm, country: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Italy">Italy</SelectItem>
                      <SelectItem value="Austria">Austria</SelectItem>
                      <SelectItem value="Belgium">Belgium</SelectItem>
                      <SelectItem value="Bulgaria">Bulgaria</SelectItem>
                      <SelectItem value="Croatia">Croatia</SelectItem>
                      <SelectItem value="Cyprus">Cyprus</SelectItem>
                      <SelectItem value="Czech Republic">Czech Republic</SelectItem>
                      <SelectItem value="Denmark">Denmark</SelectItem>
                      <SelectItem value="Estonia">Estonia</SelectItem>
                      <SelectItem value="Finland">Finland</SelectItem>
                      <SelectItem value="France">France</SelectItem>
                      <SelectItem value="Germany">Germany</SelectItem>
                      <SelectItem value="Greece">Greece</SelectItem>
                      <SelectItem value="Hungary">Hungary</SelectItem>
                      <SelectItem value="Ireland">Ireland</SelectItem>
                      <SelectItem value="Latvia">Latvia</SelectItem>
                      <SelectItem value="Lithuania">Lithuania</SelectItem>
                      <SelectItem value="Luxembourg">Luxembourg</SelectItem>
                      <SelectItem value="Malta">Malta</SelectItem>
                      <SelectItem value="Netherlands">Netherlands</SelectItem>
                      <SelectItem value="Poland">Poland</SelectItem>
                      <SelectItem value="Portugal">Portugal</SelectItem>
                      <SelectItem value="Romania">Romania</SelectItem>
                      <SelectItem value="Slovakia">Slovakia</SelectItem>
                      <SelectItem value="Slovenia">Slovenia</SelectItem>
                      <SelectItem value="Spain">Spain</SelectItem>
                      <SelectItem value="Sweden">Sweden</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="edit_fiscal_code">Fiscal Code *</Label>
                  <Input
                    id="edit_fiscal_code"
                    value={userForm.fiscal_code}
                    onChange={(e) => setUserForm({...userForm, fiscal_code: e.target.value.toUpperCase()})}
                    maxLength="16"
                    placeholder="Enter your fiscal code"
                    required
                  />
                </div>

                {!editingItem && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-blue-900">Default Password</h4>
                        <p className="text-sm text-blue-700 mt-1">
                          New users will be created with default password: <code>TempPassword123!</code>
                          <br />They should change this password after first login.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="bg-slate-700 hover:bg-slate-800">
                    {editingItem ? "Update User" : "Add User"}
                  </Button>
                  <Button type="button" variant="outline" onClick={closeDialog}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}

            {/* Loan Form */}
            {currentTab === "loans" && (
              <form onSubmit={handleLoanSubmit} className="space-y-4">
                {/* Borrower Search */}
                <div>
                  <Label htmlFor="loan_user_search">Borrower *</Label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search by last name, first name, or email..."
                      value={selectedUser ? selectedUser.label : userSearch}
                      onChange={(e) => {
                        if (selectedUser) {
                          // Clear selection when user types
                          setSelectedUser(null);
                          setLoanForm({...loanForm, user_id: ""});
                        }
                        setUserSearch(e.target.value);
                      }}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    />
                    {userSearchLoading && (
                      <div className="absolute right-3 top-3">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-500"></div>
                      </div>
                    )}
                  </div>
                  
                  {/* User Search Results */}
                  {userSearchResults.length > 0 && !selectedUser && (
                    <div className="mt-1 max-h-60 overflow-y-auto border border-slate-200 rounded-md bg-white shadow-lg z-50 relative">
                      {userSearchResults.map((user) => (
                        <div
                          key={user.id}
                          className="px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0"
                          onClick={() => {
                            setSelectedUser(user);
                            setLoanForm({...loanForm, user_id: user.id});
                            setUserSearch("");
                            setUserSearchResults([]);
                          }}
                        >
                          <div className="font-medium text-slate-900">{user.last_name}, {user.first_name}</div>
                          <div className="text-sm text-slate-600">{user.email}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Selected User Display */}
                  {selectedUser && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md flex justify-between items-center">
                      <div>
                        <div className="font-medium text-green-900">{selectedUser.last_name}, {selectedUser.first_name}</div>
                        <div className="text-sm text-green-700">{selectedUser.email}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedUser(null);
                          setLoanForm({...loanForm, user_id: ""});
                          setUserSearch("");
                        }}
                        className="text-green-600 hover:text-green-800"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>

                {/* Media Type Filter */}
                <div>
                  <Label htmlFor="loan_media_type">Media Type *</Label>
                  <Select 
                    value={loanForm.media_type} 
                    onValueChange={(value) => {
                      setLoanForm({...loanForm, media_type: value, media_id: ""});
                      setSelectedMedia(null);
                      setMediaSearch("");
                      setMediaSearchResults([]);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select media type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="book">Book</SelectItem>
                      <SelectItem value="cd">CD</SelectItem>
                      <SelectItem value="dvd">DVD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Media Item Search */}
                <div>
                  <Label htmlFor="loan_media_search">
                    {loanForm.media_type === "book" ? "Book" : loanForm.media_type === "cd" ? "CD" : "DVD"} *
                  </Label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder={`Search ${loanForm.media_type === "book" ? "by title or author" : loanForm.media_type === "cd" ? "by title or artist" : "by title or director"}...`}
                      value={selectedMedia ? selectedMedia.label : mediaSearch}
                      onChange={(e) => {
                        if (selectedMedia) {
                          // Clear selection when user types
                          setSelectedMedia(null);
                          setLoanForm({...loanForm, media_id: ""});
                        }
                        setMediaSearch(e.target.value);
                      }}
                      disabled={!loanForm.media_type}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent disabled:bg-slate-100 disabled:cursor-not-allowed"
                    />
                    {mediaSearchLoading && (
                      <div className="absolute right-3 top-3">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-500"></div>
                      </div>
                    )}
                  </div>
                  
                  {/* Media Search Results */}
                  {mediaSearchResults.length > 0 && !selectedMedia && (
                    <div className="mt-1 max-h-60 overflow-y-auto border border-slate-200 rounded-md bg-white shadow-lg z-50 relative">
                      {mediaSearchResults.map((media) => (
                        <div
                          key={media.id}
                          className="px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0"
                          onClick={() => {
                            setSelectedMedia(media);
                            setLoanForm({...loanForm, media_id: media.id});
                            setMediaSearch("");
                            setMediaSearchResults([]);
                          }}
                        >
                          <div className="font-medium text-slate-900">{media.title}</div>
                          <div className="text-sm text-slate-600">
                            {media.media_type === "book" ? `by ${media.author}` : 
                             media.media_type === "cd" ? `by ${media.artist}` : 
                             `by ${media.director}`} (ID: {media.item_id})
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Selected Media Display */}
                  {selectedMedia && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md flex justify-between items-center">
                      <div>
                        <div className="font-medium text-blue-900">{selectedMedia.title}</div>
                        <div className="text-sm text-blue-700">
                          {selectedMedia.media_type === "book" ? `by ${selectedMedia.author}` : 
                           selectedMedia.media_type === "cd" ? `by ${selectedMedia.artist}` : 
                           `by ${selectedMedia.director}`} (ID: {selectedMedia.item_id})
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedMedia(null);
                          setLoanForm({...loanForm, media_id: ""});
                          setMediaSearch("");
                        }}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                  
                  {loanForm.media_type && !selectedMedia && mediaSearch.length < 2 && (
                    <p className="text-xs text-slate-500 mt-1">
                      Type at least 2 characters to search {loanForm.media_type === "book" ? "books" : loanForm.media_type + "s"}
                    </p>
                  )}
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-yellow-900">Loan Information</h4>
                      <p className="text-sm text-yellow-700 mt-1">
                        Loans are automatically set for 4 weeks (28 days) from today.
                        <br />Maximum 10 items per user allowed.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button 
                    type="submit" 
                    className="bg-slate-700 hover:bg-slate-800"
                    disabled={!selectedUser || !selectedMedia}
                  >
                    Create Loan
                  </Button>
                  <Button type="button" variant="outline" onClick={closeDialog}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
        
        {/* Hidden file input for bulk upload */}
        <input
          id="bulk-upload-input"
          type="file"
          accept=".csv,.xlsx,.xls"
          style={{ display: 'none' }}
          onChange={handleBulkUpload}
        />

        {/* Event Detail View Modal */}
        <EventDetailView
          event={viewingEvent}
          organisers={organisers}
          venues={venues}
          isOpen={isEventDetailOpen}
          onClose={handleCloseEventDetail}
          currentUser={effectiveUser}
          onBookEvent={handleSophisticatedBookEvent}
          bookedEventIds={bookedEventIds}
          pendingBookingsByEvent={pendingBookingsByEvent}
          eventAvailability={eventAvailability}
          editEvent={(event) => {
            handleCloseEventDetail();
            setEditingItem(event);
            setEventForm({
              id: event.id,
              title: event.title,
              description_text: event.description_text,
              description_html: event.description_html || "",
              event_date: toLocalDateTimeInput(event.event_date),
              booking_deadline: toLocalDateTimeInput(event.booking_deadline),
              organiser_ids: event.organiser_ids || [],
              venue_ids: event.venue_ids || [],
              category_tags: event.category_tags || [],
              event_label: event.event_label || "",
              status: event.status || "draft",
              max_capacity: event.max_capacity || "",
              guided_visit: event.guided_visit || false,
              guest_tickets_enabled: event.guest_tickets_enabled || false,
              guest_surcharge: event.guest_surcharge || "",
              comment: event.comment || "",
              // Determine pricing type based on existing event data - simplified logic  
              pricing_type: (() => {
                // New custom_rows model wins if the backend flags it
                if (event.pricing_type === "custom_rows") return "custom_rows";
                // Check if event has matrix pricing with any enabled fields
                const hasEnabledMatrix = event.matrix_pricing && (
                  event.matrix_pricing.member_pricing_enabled || 
                  event.matrix_pricing.minor_pricing_enabled || 
                  event.matrix_pricing.senior_pricing_enabled || 
                  event.matrix_pricing.young_adult_pricing_enabled ||
                  event.matrix_pricing.galleria_enabled ||
                  event.matrix_pricing.platea_enabled
                );
                
                // Check if event has standard pricing (price_categories with enabled fields)
                const hasEnabledStandard = event.price_categories && (
                  event.price_categories.member_price_enabled ||
                  event.price_categories.minor_price_enabled ||
                  event.price_categories.senior_price_enabled ||
                  event.price_categories.young_adult_price_enabled
                );
                
                // Determine pricing type - prioritize what's actually enabled
                const pricingType = hasEnabledMatrix ? "matrix" : "standard";
                
                console.log('🔍 PRICING TYPE DETERMINATION (EventDetail Enhanced):', {
                  event_title: event.title?.substring(0, 30) + "...",
                  has_matrix_pricing: !!event.matrix_pricing,
                  has_enabled_matrix: hasEnabledMatrix,
                  has_price_categories: !!event.price_categories,
                  has_enabled_standard: hasEnabledStandard,
                  matrix_data_sample: event.matrix_pricing ? Object.keys(event.matrix_pricing) : null,
                  price_categories_sample: event.price_categories ? Object.keys(event.price_categories) : null,
                  final_pricing_type: pricingType
                });
                return pricingType;
              })(),
              // Populate custom rows when editing a custom_rows event
              custom_price_rows: Array.isArray(event.custom_price_rows) && event.custom_price_rows.length > 0
                ? event.custom_price_rows.map(r => ({ id: r.id, label: r.label, amount: String(r.amount ?? ""), capacity: r.capacity == null ? "" : String(r.capacity), minors_only: !!r.minors_only }))
                : [{ id: null, label: "Adult", amount: "" }],
              // Initialize with pricing structure from backend data - use sophisticated logic
              price_categories: event.price_categories ? {
                // Use existing price_categories data - ensure proper type conversion
                member_price: (() => {
                  const price = event.price_categories.member_price;
                  const converted = price != null ? String(price) : "";
                  console.log('🔍 EDIT FORM INITIALIZATION (EventDetail):', {
                    original_price: price,
                    price_type: typeof price,
                    is_null: price === null,
                    is_undefined: price === undefined,
                    converted_value: converted
                  });
                  return converted;
                })(),
                minor_price: String(event.price_categories.minor_price || ""),
                senior_price: String(event.price_categories.senior_price || ""),
                young_adult_price: String(event.price_categories.young_adult_price || ""),
                member_price_enabled: Boolean(event.price_categories.member_price_enabled),
                minor_price_enabled: Boolean(event.price_categories.minor_price_enabled),
                senior_price_enabled: Boolean(event.price_categories.senior_price_enabled),
                young_adult_price_enabled: Boolean(event.price_categories.young_adult_price_enabled)
              } : {
                // Fallback to legacy fields if no price_categories
                member_price: String(event.member_price || event.cost_euro_full || ""),
                minor_price: String(event.minor_price || event.cost_euro_reduced || ""),
                senior_price: String(event.senior_price || ""),
                young_adult_price: String(event.young_adult_price || ""),
                member_price_enabled: Boolean(event.member_price || event.cost_euro_full),
                minor_price_enabled: Boolean(event.minor_price || event.cost_euro_reduced),
                senior_price_enabled: Boolean(event.senior_price),
                young_adult_price_enabled: Boolean(event.young_adult_price)
              },
              // Initialize matrix pricing if it exists
              matrix_pricing: event.matrix_pricing || {
                member_pricing: { galleria_price: "", platea_price: "" },
                minor_pricing: { galleria_price: "", platea_price: "" },
                senior_pricing: { galleria_price: "", platea_price: "" },
                young_adult_pricing: { galleria_price: "", platea_price: "" },
                member_pricing_enabled: false,
                minor_pricing_enabled: false,
                senior_pricing_enabled: false,
                young_adult_pricing_enabled: false,
                galleria_enabled: false,
                platea_enabled: false
              },
              seat_types: event.seat_types || []
            });
            
            // Set existing event image preview if it exists
            if ((event.images && event.images.length > 0) || (event.images_s3 && event.images_s3.length > 0)) {
              const imageUrl = getEventImageUrlForForm(event);
              setEventImagePreview(imageUrl);
            } else {
              setEventImagePreview(null);
            }
            
            // Mark form as ready and open dialog
            setIsEditFormReady(true);
            setIsDialogOpen(true);
          }}
          deleteEvent={async (eventId) => {
            try {
              await axios.delete(`/events/${eventId}`);
              toast.success("Event deleted successfully!");
              await fetchEvents(searchTerm, selectedCategory);
              handleCloseEventDetail();
            } catch (error) {
              const errorMessage = error.response?.data?.detail || 'Failed to delete event';
              toast.error(errorMessage);
            }
          }}
        />

        {/* Booking System Dialogs */}
        <BookingDialog
          isOpen={isBookingDialogOpen}
          onClose={closeBookingDialog}
          event={selectedEventForBooking}
          bookingForm={bookingForm}
          setBookingForm={setBookingForm}
          onSubmit={handleBookingSubmit}
          onTicketQuantityChange={handleTicketQuantityChange}
          calculateTotal={calculateBookingTotal}
          editingBooking={editingBooking}
          currentUser={currentUser}
          renewMembership={renewMembership}
        />

        {/* Sophisticated Booking Dialog */}
        <SophisticatedBookingDialog
          isOpen={isSophisticatedBookingOpen}
          onClose={() => setIsSophisticatedBookingOpen(false)}
          event={selectedEventForBooking}
          bookingForm={bookingForm}
          setBookingForm={setBookingForm}
          onSubmit={handleSophisticatedBookingSubmit}
          currentUser={currentUser}
          calculatePricing={calculateEventPricing}
          editingBooking={editingBooking}
          renewMembership={renewMembership}
          onBookingForChange={(member) => {
            setBookingForm(prev => ({...prev, booking_for_user_id: member?.id}));
          }}
        />

        {/* Custom-rows Booking Dialog (new pricing model) */}
        <CustomRowsBookingDialog
          isOpen={isCustomRowsBookingOpen}
          onClose={() => setIsCustomRowsBookingOpen(false)}
          event={selectedEventForBooking}
          currentUser={currentUser}
          onSuccess={() => { fetchUserBookings(); fetchEventAvailability(events); }}
        />

        {/* Global admin settings (booking disclaimer, etc.) */}
        <AdminSettingsDialog
          isOpen={isAdminSettingsOpen}
          onClose={() => setIsAdminSettingsOpen(false)}
        />

        {/* Global admin-only Login Report — mounted once, toggled from Users tab */}
        <LoginReportDialog
          open={showLoginReport}
          onOpenChange={setShowLoginReport}
        />

        {/* Global Change Password dialog — opened from the profile menu */}
        <ChangePasswordForm
          isOpen={isChangePasswordOpen}
          onClose={() => setIsChangePasswordOpen(false)}
        />

        {/* Global Delete Account dialog — GDPR self-service; on success, log the user out */}
        <DeleteAccountForm
          isOpen={isDeleteAccountOpen}
          onClose={() => setIsDeleteAccountOpen(false)}
        />

        <PaymentHistoryDialog
          isOpen={!!paymentHistoryTarget}
          onClose={() => setPaymentHistoryTarget(null)}
          userId={paymentHistoryTarget?.userId || "me"}
          memberName={paymentHistoryTarget?.name || ""}
        />

        {showBookingHistory && (
          <BookingHistory
            bookings={userBookings}
            onClose={() => setShowBookingHistory(false)}
            onDeleteBooking={handleUserDeleteBooking}
            onVerifyPayment={handleVerifyPayment}
          />
        )}

        {/* User Loan History Dialog */}
        {showLoanHistory && (
          <Dialog open={true} onOpenChange={() => setShowLoanHistory(false)}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl">My Loan History</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                {userLoans.length === 0 ? (
                  <div className="text-center py-8">
                    <Library className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-600 mb-2">No loans yet</h3>
                    <p className="text-slate-500">Borrow your first item from the library to see it here!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {userLoans.map((loan) => (
                      <Card key={loan.id}>
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold">{loan.media_title || 'Unknown Title'}</h3>
                              <p className="text-slate-600">
                                {loan.media_type === 'book' && 'Book'}
                                {loan.media_type === 'cd' && 'CD'}
                                {loan.media_type === 'dvd' && 'DVD'}
                                {loan.author && ` by ${loan.author}`}
                              </p>
                              <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                                <span>Borrowed: {loan.loan_date ? new Date(loan.loan_date).toLocaleDateString() : 'N/A'}</span>
                                {loan.return_date && (
                                  <span>Returned: {new Date(loan.return_date).toLocaleDateString()}</span>
                                )}
                                {loan.due_date && !loan.return_date && (
                                  <span>Due: {new Date(loan.due_date).toLocaleDateString()}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge 
                                className={
                                  loan.status === 'active' 
                                    ? 'bg-green-100 text-green-800' 
                                    : loan.status === 'returned'
                                    ? 'bg-blue-100 text-blue-800'
                                    : loan.status === 'overdue'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-800'
                                }
                              >
                                {loan.status === 'active' && 'Active'}
                                {loan.status === 'returned' && 'Returned'}
                                {loan.status === 'overdue' && 'Overdue'}
                                {loan.status && !['active', 'returned', 'overdue'].includes(loan.status) && loan.status}
                              </Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex justify-end pt-4">
                <Button onClick={() => setShowLoanHistory(false)}>
                  Close
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Custom Confirmation Dialog */}
        <Dialog open={confirmDialog.isOpen} onOpenChange={() => setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null })}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{confirmDialog.title}</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="whitespace-pre-line text-sm text-gray-600">
                {confirmDialog.message}
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={confirmDialog.onCancel}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDialog.onConfirm}
              >
                Delete
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Payment Success Dialog */}
        <Dialog open={paymentSuccess} onOpenChange={(open) => !open && setPaymentSuccess(false)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-green-600">🎉 Payment Successful!</DialogTitle>
            </DialogHeader>
            <div className="py-4 text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-gray-600 mb-4">
                Your booking has been confirmed and payment processed successfully.
              </p>
              {paymentBookingId && (
                <div className="bg-gray-50 p-3 rounded text-sm">
                  <strong>Booking ID:</strong> {paymentBookingId}
                </div>
              )}
            </div>
            <div className="flex justify-center pt-4">
              <Button
                onClick={() => {
                  setPaymentSuccess(false);
                  setPaymentBookingId(null);
                }}
                className="bg-green-600 hover:bg-green-700"
              >
                Continue
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Event Bookings Management Dialog */}
        <EventBookingsTable
          eventId={selectedEventForManagement?.id}
          eventTitle={selectedEventForManagement?.title}
          isOpen={isEventBookingsDialogOpen}
          onClose={() => {
            setIsEventBookingsDialogOpen(false);
            setSelectedEventForManagement(null);
          }}
          onEditBooking={handleEditBooking}
          onDeleteBooking={handleDeleteBooking}
          onUpdateBookingStatus={handleUpdateBookingStatus}
          bookings={eventBookings}
          stats={bookingStats}
          isLoading={isEventBookingsLoading}
          onCreateManualBooking={selectedEventForManagement ? () => setManualBookingEvent(selectedEventForManagement) : null}
          currentUser={currentUser}
        />

        {/* Capacity vs. Tickets-Left Chart Dialog */}
        <EventCapacityChart
          isOpen={isCapacityChartOpen}
          onClose={() => setIsCapacityChartOpen(false)}
          events={getUpcomingEvents().filter((e) => e.status === 'published')}
          eventAvailability={eventAvailability}
        />

        {/* Document Repository Dialog (members + admins) */}
        <DocumentRepositoryDialog
          isOpen={isDocumentRepositoryOpen}
          onClose={() => setIsDocumentRepositoryOpen(false)}
          currentUser={currentUser}
        />

        {/* Season Pass management modal (admin only) */}
        {isSeasonPassAdminOpen && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto py-8" data-testid="season-pass-admin-modal">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl mx-4 p-6">
              <SeasonPassManagement
                currentUser={currentUser}
                onClose={() => setIsSeasonPassAdminOpen(false)}
              />
            </div>
          </div>
        )}


        {/* Event Waitlist Dialog (admin) */}
        <EventWaitlistDialog
          isOpen={!!waitlistDialogEvent}
          event={waitlistDialogEvent}
          onClose={() => setWaitlistDialogEvent(null)}
          onAfterChange={() => {
            fetchWaitlistCounts();
            // Refresh availability so converted bookings reduce "Left"
            fetchEventAvailability(events);
          }}
        />

        {/* Admin Manual Booking Dialog */}
        <AdminManualBookingDialog
          isOpen={!!manualBookingEvent}
          event={manualBookingEvent}
          onClose={() => setManualBookingEvent(null)}
          onCreated={() => {
            // Refresh availability + any open bookings table
            fetchEventAvailability(events);
            if (manualBookingEvent?.id && (selectedEventForManagement?.id === manualBookingEvent.id || isEventBookingsDialogOpen)) {
              fetchEventBookings(manualBookingEvent.id);
            }
          }}
        />
        
        {/* Organisers Management Dialog */}
        <Dialog open={isOrganisersManagementOpen} onOpenChange={setIsOrganisersManagementOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Manage Organisers</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {organisers.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No organisers found</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {organisers.map((organiser) => (
                    <Card key={organiser.id} className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-semibold text-lg">{organiser.name}</h3>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleEditOrganiser(organiser)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteOrganiser(organiser)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        {organiser.email && (
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-slate-500" />
                            <span>{organiser.email}</span>
                          </div>
                        )}
                        {organiser.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-slate-500" />
                            <span>{organiser.phone}</span>
                          </div>
                        )}
                        {organiser.website && (
                          <div className="flex items-center gap-2">
                            <Globe className="h-4 w-4 text-slate-500" />
                            <a href={organiser.website} target="_blank" rel="noopener noreferrer" 
                               className="text-blue-600 hover:underline">
                              {organiser.website}
                            </a>
                          </div>
                        )}
                        {organiser.bio && (
                          <p className="text-slate-600 text-xs mt-2">{organiser.bio}</p>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Venues Management Dialog */}
        <Dialog open={isVenuesManagementOpen} onOpenChange={setIsVenuesManagementOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Manage Venues</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {venues.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No venues found</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {venues.map((venue) => (
                    <Card key={venue.id} className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-semibold text-lg">{venue.name}</h3>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleEditVenue(venue)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteVenue(venue)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        {venue.address && (
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-slate-500" />
                            <span>{venue.address}</span>
                          </div>
                        )}
                        {venue.city && (
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-slate-500" />
                            <span>{venue.city}</span>
                          </div>
                        )}
                        {venue.capacity && (
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-slate-500" />
                            <span>Capacity: {venue.capacity}</span>
                          </div>
                        )}
                        {venue.contact_info && (
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-slate-500" />
                            <span>{venue.contact_info}</span>
                          </div>
                        )}
                        {venue.facilities && venue.facilities.length > 0 && (
                          <div className="mt-2">
                            <p className="font-medium text-xs mb-1">Facilities:</p>
                            <div className="flex flex-wrap gap-1">
                              {venue.facilities.map((facility, index) => (
                                <Badge key={index} variant="secondary" className="text-xs">
                                  {facility}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Admin Loan Management Dialog */}
        <AdminLoanManagement 
          isOpen={isAdminLoanManagementOpen}
          onClose={() => { setIsAdminLoanManagementOpen(false); fetchLoans(); fetchLoanStatuses(); }}
        />

        {/* Book Category Management Dialog */}
        <BookCategoryManagement 
          isOpen={isBookCategoryManagementOpen}
          onClose={() => {
            setIsBookCategoryManagementOpen(false);
            // Refresh categories after management
            fetchBookCategories();
          }}
        />

        {/* Database Backup Management Dialog */}
        {showBackupDialog && (
          <Dialog open={true} onOpenChange={() => setShowBackupDialog(false)}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl flex items-center gap-2">
                  <Download className="h-5 w-5" />
                  Database Backup & Restore Management
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6">
                {/* Database Information */}
                {collectionsInfo && (
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-3">Current Database Information</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div className="bg-white p-3 rounded border">
                        <div className="font-medium text-blue-600">Users</div>
                        <div className="text-xl">{collectionsInfo.collections.users || 0}</div>
                      </div>
                      <div className="bg-white p-3 rounded border">
                        <div className="font-medium text-green-600">Events</div>
                        <div className="text-xl">{collectionsInfo.collections.events || 0}</div>
                      </div>
                      <div className="bg-white p-3 rounded border">
                        <div className="font-medium text-purple-600">Books</div>
                        <div className="text-xl">{collectionsInfo.collections.books || 0}</div>
                      </div>
                      <div className="bg-white p-3 rounded border">
                        <div className="font-medium text-orange-600">Total Documents</div>
                        <div className="text-xl">{collectionsInfo.total_documents || 0}</div>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-slate-500">
                      Last checked: {new Date(collectionsInfo.timestamp).toLocaleString()}
                    </div>
                  </div>
                )}
                
                {/* Backup Operations */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Create Backup */}
                  <div className="border border-slate-200 rounded-lg p-4">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Download className="h-4 w-4" />
                      Create Backup
                    </h3>
                    <p className="text-sm text-slate-600 mb-4">
                      Create a complete backup of all database collections including users, events, library items, and bookings.
                    </p>
                    <Button
                      onClick={createDatabaseBackup}
                      disabled={backupLoading}
                      className="w-full mb-3"
                    >
                      {backupLoading ? (
                        <>
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                          Creating Backup...
                        </>
                      ) : (
                        'Create Full Backup'
                      )}
                    </Button>
                    
                    {backupData && (
                      <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded">
                        <div className="text-sm text-green-800 mb-2">
                          ✅ Backup created successfully!
                        </div>
                        <div className="text-xs text-green-600 mb-3">
                          {backupData.backup_info.total_documents} documents from {backupData.backup_info.total_collections} collections
                        </div>
                        <Button
                          onClick={downloadBackup}
                          size="sm"
                          variant="outline"
                          className="text-green-700 border-green-300 hover:bg-green-100"
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download Backup File
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* Restore Backup */}
                  <div className="border border-slate-200 rounded-lg p-4">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Upload className="h-4 w-4" />
                      Restore Backup
                    </h3>
                    <p className="text-sm text-slate-600 mb-4">
                      Restore database from a previously created backup file. 
                      <span className="text-red-600 font-medium"> Warning: This will replace all current data!</span>
                    </p>
                    
                    <div className="mb-3">
                      <input
                        type="file"
                        accept=".json"
                        onChange={handleRestoreUpload}
                        disabled={restoreLoading}
                        className="block w-full text-sm text-slate-500
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-md file:border-0
                          file:text-sm file:font-semibold
                          file:bg-red-50 file:text-red-700
                          hover:file:bg-red-100"
                      />
                    </div>
                    
                    {restoreLoading && (
                      <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                        <div className="flex items-center text-yellow-800">
                          <div className="animate-spin h-4 w-4 border-2 border-yellow-600 border-t-transparent rounded-full mr-2"></div>
                          Restoring database... Please wait.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Automated Backup Info */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h3 className="font-semibold mb-2 text-blue-800">Automated Backup System</h3>
                  <div className="text-sm text-blue-700 space-y-1">
                    <div>• Automated backups run every 24 hours</div>
                    <div>• System keeps the last 7 automated backups</div>
                    <div>• Backups include all critical collections (users, events, library, bookings)</div>
                    <div>• First automated backup starts 1 hour after system startup</div>
                  </div>
                </div>
                
                {/* Warning */}
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <h3 className="font-semibold mb-2 text-red-800">⚠️ Important Backup Notes</h3>
                  <div className="text-sm text-red-700 space-y-1">
                    <div>• Always test restore procedures with non-production data first</div>
                    <div>• Keep backup files in secure, offsite locations</div>
                    <div>• Verify backup integrity before relying on them for recovery</div>
                    <div>• Consider additional backup strategies for critical production systems</div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-between items-center pt-4">
                <Button
                  onClick={fetchCollectionsInfo}
                  variant="outline"
                  size="sm"
                >
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Refresh Info
                </Button>
                <Button onClick={() => setShowBackupDialog(false)}>
                  Close
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Help Dialog */}
        {showHelp && (
          <Dialog open={true} onOpenChange={() => setShowHelp(false)}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl flex items-center gap-2">
                  <HelpCircle className="h-5 w-5" />
                  {isAdmin(effectiveUser) ? 'Administrator Help Guide' : 'User Help Guide'}
                </DialogTitle>
              </DialogHeader>
              
              <div className="max-w-none">
                <HelpContent text={helpContent} />
              </div>
              
              <div className="flex justify-between items-center pt-4">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Info className="h-4 w-4" />
                  <span>
                    For complete documentation, see the help files in your system
                  </span>
                </div>
                <Button onClick={() => setShowHelp(false)}>
                  Close
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

      </div>
    </div>
  );
}

// Router-aware App wrapper
function AppWithRouter() {
  return (
    <Router>
      <Routes>
        <Route path="/registration-success" element={<RegistrationResult />} />
        <Route path="/registration-result" element={<RegistrationResult />} />
        <Route path="/membership-renewal-result" element={<MembershipRenewalResult />} />
        <Route path="/season-pass/success" element={<SeasonPassSuccess />} />
        <Route path="/season-pass/cancel" element={<SeasonPassCancel />} />
        <Route path="/*" element={<App />} />
      </Routes>
    </Router>
  );
}

export default AppWithRouter;
